export type Language = 'en' | 'nl' | 'fr';

export const translations = {
  en: {
    // Common
    common: {
      back: 'Back',
      backToHome: 'Back to home',
      submit: 'Submit',
      submitting: 'Submitting...',
      loading: 'Loading...',
      signIn: 'Sign In',
      signUp: 'Sign Up',
      signInSignUp: 'Sign In / Sign Up',
      returnHome: 'Return Home',
      next: 'Next',
      cancel: 'Cancel',
      save: 'Save',
      edit: 'Edit',
      delete: 'Delete',
      view: 'View',
      close: 'Close',
      confirm: 'Confirm',
      search: 'Search',
      filter: 'Filter',
      all: 'All',
      learnMore: 'Learn More',
      getStarted: 'Get Started',
      bookNow: 'Book Now',
      contactUs: 'Contact Us',
      optional: 'optional',
    },
    // Reviews
    reviews: {
      rateService: 'Rate this service',
      howWasExperience: 'How was your experience with',
      selectRating: 'Select a rating',
      ratingPoor: 'Poor',
      ratingFair: 'Fair',
      ratingGood: 'Good',
      ratingVeryGood: 'Very Good',
      ratingExcellent: 'Excellent!',
      writeReview: 'Write a review',
      shareExperience: 'Share your experience with this technician...',
      submitReview: 'Submit Review',
      thankYou: 'Thank you for your feedback!',
      alreadyReviewed: 'You have already reviewed this job',
    },
    // Navbar
    navbar: {
      home: 'Home',
      services: 'Services',
      howItWorks: 'How It Works',
      plans: 'Plans',
      propertyManagers: 'Property Managers',
      becomeTechnician: 'Become a Technician',
      forTechnicians: 'For technicians',
      dashboard: 'Dashboard',
      signIn: 'Sign In',
      signOut: 'Sign Out',
    },
    // Hero
    hero: {
      badge: 'Vetted technicians · Secure payments',
      title: 'Any home job, one trusted platform',
      titleHighlight: 'for customers & technicians',
      subtitle: "Find a vetted technician for electrical, locks, heating, cleaning and more — book instantly when it's urgent, or compare quotes for planned work. Technicians find local jobs and get paid securely.",
      cta: 'Find a technician',
      secondaryCta: 'Work as a technician',
      trust: 'Trusted across the Netherlands',
      trustVerified: 'Verified technicians',
      trustEscrow: 'Escrow-protected payments',
      trustPricing: 'Transparent pricing',
    },
    // For Technicians
    forTechnicians: {
      badge: 'For technicians',
      title: 'Grow your business with Bricos',
      subtitle: 'Get matched with local customers, set your prices, and get paid securely — no lead fees to chase.',
      benefit1: {
        title: 'Steady local jobs',
        description: 'Receive job offers and quote requests from customers near you.',
      },
      benefit2: {
        title: 'Get paid on time',
        description: 'Payments are held in escrow and released to you when the job is done.',
      },
      benefit3: {
        title: 'Build your reputation',
        description: 'Earn ratings and reviews that win you more work.',
      },
      cta: 'Become a technician',
    },
    // Services
    services: {
      title: 'Services for every home',
      subtitle: 'From urgent fixes to planned projects — electrical, locks, heating, cleaning and more.',
      electrician: {
        title: 'Electrician',
        description: 'Electrical repairs, wiring, panels, and emergency power issues',
        eta: 'Fast response',
      },
      locksmith: {
        title: 'Locksmith',
        description: 'Lock repairs, lock-outs, key duplication, and security upgrades',
        eta: 'Fast response',
      },
      boiler: {
        title: 'Boiler/CV',
        description: 'Heating systems, boiler repairs, maintenance, and CV issues',
        eta: 'Fast response',
      },
      from: 'From',
      warranty: 'Quality guaranteed',
      comingSoonTitle: 'More services coming soon',
      comingSoonDesc: "We're expanding across the Netherlands — these disciplines are on the way.",
      comingSoonBadge: 'Coming soon',
    },
    // How It Works
    howItWorks: {
      title: 'How It Works',
      subtitle: 'Get the right pro in a few simple steps',
      step1: {
        title: 'Tell us the job',
        description: 'Describe what you need — for any of our service categories.',
      },
      step2: {
        title: 'Get matched or compare quotes',
        description: 'Book an available technician instantly, or receive quotes and pick the one you like.',
      },
      step3: {
        title: 'Work gets done',
        description: 'Track progress and chat with your technician until the job is complete.',
      },
      step4: {
        title: 'Pay securely',
        description: 'Your payment is held in escrow and only released to the technician 3 days after the job is done — and only if no dispute has been opened.',
      },
    },
    // Plans
    plans: {
      title: 'Service Plans',
      subtitle: 'Benefits for regular customers',
      annual: {
        title: 'Annual Plan',
        price: '€149/year',
        features: [
          'Discounted safety check (€99 instead of regular price)',
          '10% discount on call-out fees',
          'Priority booking queue',
          'Escrow-protected payments',
          '2x loyalty points on bookings',
        ],
      },
      payAsYouGo: {
        title: 'Pay As You Go',
        price: 'No commitment',
        features: [
          'Pay per service',
          'Standard response times',
          'Escrow-protected payments',
          'Earn loyalty points',
        ],
      },
      cta: 'Get Started',
    },
    // Property Managers
    propertyManagers: {
      title: 'For Property Managers',
      subtitle: 'Manage all your properties in one place',
      features: {
        multiProperty: 'Multi-property dashboard',
        bulkPricing: 'Bulk pricing discounts',
        sla: 'Priority SLA response',
        billing: 'Consolidated billing',
        reports: 'Detailed job reports',
        maintenance: 'Maintenance passport per unit',
      },
      cta: 'Start Free Trial',
    },
    // Technician Onboarding
    technicianOnboarding: {
      title: 'Become a Technician',
      subtitle: 'Join our network of verified professionals',
      signInRequired: 'Sign in required',
      signInMessage: 'Please create an account or sign in to apply as a technician.',
      applicationSubmitted: 'Application Submitted!',
      applicationMessage: 'Thank you for applying to join Bricos. Our team will review your application and contact you within 2-3 business days.',
      step1: {
        title: 'Select your specialty',
        subtitle: 'Choose the service type you specialize in',
      },
      step2: {
        title: 'Your Information',
        subtitle: 'Tell us about yourself and your experience',
      },
      form: {
        fullName: 'Full Name',
        fullNamePlaceholder: 'John Doe',
        phone: 'Phone Number',
        phonePlaceholder: '+31 6 12345678',
        companyName: 'Company Name',
        companyNamePlaceholder: 'Your Company BV',
        kvkNumber: 'KVK Number',
        kvkNumberPlaceholder: '12345678',
        vatNumber: 'VAT Number (BTW)',
        vatNumberPlaceholder: 'NL123456789B01',
        experience: 'Experience & Qualifications',
        experiencePlaceholder: 'Describe your experience, certifications, and any relevant qualifications...',
        submitApplication: 'Submit Application',
      },
      benefits: {
        title: 'Why join Bricos?',
        fairPay: 'Fair pay',
        fairPayDesc: 'No lead fees – see your payout before accepting',
        fastPayouts: 'Fast payouts',
        fastPayoutsDesc: 'Weekly payouts, next-day for top performers',
        protection: 'Protection',
        protectionDesc: 'Dispute resolution and customer communication handled',
        flexibility: 'Flexibility',
        flexibilityDesc: 'Set your own availability and work area',
      },
      errors: {
        selectService: 'Please select a service',
        applicationExists: 'Application exists',
        applicationExistsMessage: "You've already submitted an application. Please wait for review.",
        submitError: 'Error',
        submitErrorMessage: 'Failed to submit application. Please try again.',
        nameMin: 'Name must be at least 2 characters',
        phoneMin: 'Please enter a valid phone number',
        experienceMin: 'Please describe your experience (at least 20 characters)',
        companyRequired: 'Company name is required',
        kvkRequired: 'KVK number is required',
        kvkFormat: 'KVK number must be 8 digits',
      },
    },
    // Onboarding
    onboarding: {
      welcome: 'Welcome to Bricos!',
      welcomeDesc: 'The marketplace for any home job. Book vetted technicians for electrical, plumbing, locksmith, and more — book instantly or compare quotes, all with fixed pricing and escrow-protected payments.',
      howItWorks: 'Fast & transparent',
      howItWorksDesc: 'Select a service, describe your problem, and get matched with a verified technician in minutes. Track their arrival in real-time on the map.',
      safePayments: 'Secure escrow payments',
      safePaymentsDesc: 'Your payment is held safely in escrow until the job is completed to your satisfaction. No hidden fees, no surprises.',
      skip: 'Skip',
      next: 'Next',
      bookFirst: 'Book your first repair',
    },
    // Footer
    footer: {
      description: 'The trusted marketplace connecting customers with vetted technicians across the Netherlands — for any home job, urgent or planned.',
      services: 'Services',
      moreServices: 'More Services',
      company: 'Company',
      legal: 'Legal',
      termsOfService: 'Terms of Service',
      privacyPolicy: 'Privacy Policy',
      helpCenter: 'Help Center',
      contact: 'Contact',
      phone: '',
      email: 'support@bricos.nl',
      contactComingSoon: 'Phone & email support coming soon',
      location: 'Netherlands',
      rights: 'All rights reserved.',
    },
    // Auth
    auth: {
      signIn: 'Sign In',
      signUp: 'Sign Up',
      signInShort: 'Sign in',
      signUpShort: 'Sign up',
      createAccount: 'Create Account',
      creatingAccount: 'Creating account...',
      signingIn: 'Signing in...',
      welcomeBack: 'Welcome back',
      createYourAccount: 'Create your account',
      signInDesc: 'Sign in to access your dashboard and bookings',
      signUpDesc: 'Sign up to book vetted technicians and track your jobs',
      fullName: 'Full Name',
      fullNamePlaceholder: 'John Doe',
      email: 'Email',
      emailPlaceholder: 'you@example.com',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      forgotPassword: 'Forgot your password?',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      resetPassword: 'Reset Password',
      newPassword: 'New Password',
      iAmA: 'I am a...',
      homeowner: 'Homeowner / User',
      homeownerDesc: 'Book vetted technicians for your home',
      technician: 'Technician',
      technicianDesc: 'Join as a service provider',
      propertyManager: 'Property Manager',
      propertyManagerDesc: 'Manage multiple properties',
      emergencyServices: 'Vetted technicians, when you need them',
      authDescription: 'Electricians, locksmiths, and boiler technicians at your door. Fixed pricing, escrow payments, and quality-guaranteed repairs.',
      avgArrival: 'Avg. arrival',
      rating: 'Rating',
      jobsDone: 'Jobs done',
    },
    // Dashboard
    dashboard: {
      welcome: 'Welcome back',
      manageBookings: 'Manage your bookings and track repairs',
      newBooking: 'New Booking',
      loyaltyPoints: 'Loyalty Points',
      tier: 'tier',
      ptsTo: 'pts to',
      activeJobs: 'Active Jobs',
      trackLatestJob: 'Track latest job',
      completedJobs: 'Completed Jobs',
      viewHistory: 'View history',
      activeBookings: 'Active Bookings',
      recentRepairs: 'Recent Repairs',
      viewAll: 'View all',
      report: 'Report',
      noBookingsYet: 'No bookings yet',
      bookFirstService: 'Book your first job and get matched with a vetted technician.',
      recentActivity: 'Recent Activity',
      noBookings: 'No active bookings',
      bookService: 'Book a Service',
      customerDashboard: 'Customer Dashboard',
      openDisputes: 'Open Disputes',
      viewDisputes: 'View Disputes',
    },
    // Dashboard Header
    dashboardHeader: {
      admin: 'Admin',
      adminPanel: 'Admin Panel',
      notifications: 'Notifications',
      noNewNotifications: 'No new notifications',
      profile: 'Profile',
      settings: 'Settings',
      bookingHistory: 'Booking History',
      servicePlans: 'Service Plans',
      helpSupport: 'Help & Support',
      signOut: 'Sign Out',
    },
    // Dashboard Footer
    dashboardFooter: {
      helpCenter: 'Help Center',
      contact: 'Contact',
      support247: 'Support',
      privacy: 'Privacy',
      terms: 'Terms',
    },
    // Tracking
    tracking: {
      trackTechnician: 'Track Your Technician',
      booking: 'Booking',
      paymentConfirmed: 'Payment Confirmed!',
      paymentFailed: 'Payment was not completed. Please try booking again.',
      paymentPending: "We couldn't confirm your payment yet — if you completed it, it'll update shortly.",
      paymentEscrowMsg: 'Your payment is held securely in escrow. A technician will be assigned shortly.',
      estimatedArrival: 'Estimated Arrival',
      minutes: 'minutes',
      jobProgress: 'Job Progress',
      bookingConfirmed: 'Booking Confirmed',
      bookingConfirmedDesc: 'Your booking has been confirmed',
      technicianAssigned: 'Technician Assigned',
      technicianAssignedDesc: 'A technician has been assigned to your job',
      onTheWay: 'On The Way',
      onTheWayDesc: 'Your technician is on the way',
      jobCompleted: 'Job Completed',
      jobCompletedDesc: 'The job has been completed successfully',
      yourTechnician: 'Your Technician',
      specialist: 'Specialist',
      verified: 'Verified',
      jobsCompleted: 'jobs completed',
      call: 'Call',
      message: 'Message',
      jobDetails: 'Job Details',
      location: 'Location',
      serviceType: 'Service Type',
      emergency: 'Emergency',
      standard: 'Standard',
      fixedPrice: 'Fixed Price',
      bookingNotFound: 'Booking not found',
      backToDashboard: 'Back to Dashboard',
      technicianArrived: 'Technician Has Arrived',
      technicianArrivedDesc: 'has started working on your job. Please confirm their arrival to start the timer.',
      confirmArrival: 'Confirm Technician Arrived',
      arrivalConfirmed: 'Arrival confirmed! Job timer is now running.',
      arrivalConfirmFailed: 'Failed to confirm arrival',
      confirmedAt: 'Confirmed at',
      approvedExtraWork: 'Approved Extra Work',
      total: 'Total',
      enRoute: 'En Route',
      navigateToJob: 'Navigate to the job location',
      iHaveArrived: "I've Arrived",
      travelTime: 'Travel Time',
      pricing: 'Pricing',
      callOutFee: 'Call-out fee',
      hourlyRate: 'Hourly',
      estimated: 'est.',
      jobDetailsExpand: 'Job Details',
      kmAway: 'km away',
      customer: 'Customer',
    },
    // History
    history: {
      title: 'Maintenance History',
      completedJobs: 'completed jobs',
      noCompletedJobs: 'No completed jobs yet',
      bookFirstService: 'Book Your First Service',
      report: 'Report',
      invoice: 'Invoice',
      rate: 'Rate',
    },
    // Settings
    settings: {
      title: 'Settings',
      backToDashboard: 'Back to dashboard',
      profile: 'Profile',
      email: 'Email',
      emailCannotChange: 'Email cannot be changed',
      fullName: 'Full Name',
      fullNamePlaceholder: 'John Doe',
      phoneNumber: 'Phone Number',
      phonePlaceholder: '+31 6 12345678',
      language: 'Language',
      saveChanges: 'Save Changes',
      saving: 'Saving...',
      profileUpdated: 'Profile updated',
      changesSaved: 'Your changes have been saved.',
      error: 'Error',
      updateError: 'Failed to update profile. Please try again.',
      security: 'Security',
      changePassword: 'Change Password',
      changePasswordDesc: 'Update your password to keep your account secure.',
      resetPassword: 'Reset Password',
      account: 'Account',
      signOut: 'Sign Out',
      deleteAccount: 'Delete Account',
      deleteAccountTitle: 'Delete your account?',
      deleteAccountDesc: 'This action cannot be undone. This will permanently delete your account and remove all your data from our servers.',
      cancel: 'Cancel',
      accountDeletion: 'Account deletion',
      contactSupport: 'Please contact support to delete your account.',
    },
    // Language
    language: {
      select: 'Language',
      en: 'English',
      nl: 'Nederlands',
      fr: 'Français',
    },
    // Pricing Transparency
    pricing: {
      badge: 'Transparent Pricing',
      title: 'Know Exactly What You Pay',
      subtitle: 'No hidden fees. Every cost shown upfront before you book.',
      fixedPrice: {
        title: 'Fixed Price',
        description: 'Price is set before the job starts. You know the exact total upfront. Perfect for standard repairs and installations.',
        services: 'Locksmiths, Electricians, Plumbers',
      },
      hourlyRate: {
        title: 'Hourly Rate',
        description: 'Pay a call-out fee + hourly rate for actual time worked. Ideal for jobs where duration varies.',
        services: 'Cleaning, Painting, Renovations',
      },
      examples: {
        fixedJob: {
          type: 'Fixed Price Job',
          service: 'Lock Replacement',
          note: 'Price agreed upfront. No surprises.',
        },
        fixedUrgent: {
          type: 'Fixed + Urgent',
          service: 'Emergency Lockout',
          note: 'Same fixed price + urgent fee for priority dispatch.',
        },
        hourlyJob: {
          type: 'Hourly Rate Job',
          service: 'Home Cleaning',
          note: 'Pay only for actual time worked.',
        },
        hourlyUrgent: {
          type: 'Hourly + Urgent',
          service: 'Emergency Cleaning',
          note: 'Urgent adds €25/hr for immediate availability.',
        },
      },
      labels: {
        servicePrice: 'Service price',
        serviceFee: 'Service fee',
        emergencySurcharge: 'Emergency surcharge',
        callOutFee: 'Call-out fee',
        hourlyRate: 'Hourly rate',
        total: 'Total',
        urgent: 'Urgent',
        hours: 'hrs',
        perHour: 'per hour',
        urgentPerHour: 'urgent',
      },
      trust: {
        title: 'Your Money is Protected',
        subtitle: 'Payment held in escrow until you confirm the job is done right.',
        warranty: 'Quality guaranteed',
        verified: 'Verified technicians',
        support: 'Customer support',
      },
    },
    // Satisfaction Guarantee
    satisfaction: {
      badge: 'Satisfaction Guaranteed',
      title: 'Your Trust, Our Priority',
      subtitle: 'We stand behind every job with comprehensive protection',
      moneyBack: 'Escrow Protection',
      moneyBackDesc: "Your payment is held securely and only released when you're satisfied. Disputes are handled through our structured resolution process.",
      warranty: 'Quality Focus',
      warrantyDesc: 'Issues after a job? Our dispute resolution process helps find a fair outcome for everyone.',
      escrow: 'Secure Escrow',
      escrowDesc: 'Your money is held in escrow and only released to the technician 3 days after completion, if no dispute is open.',
      verified: 'Professional Technicians',
      verifiedDesc: 'Technicians register with their KvK number, certifications, and insurance details.',
    },
    // Booking
    booking: {
      step1Title: 'Select Service',
      step2Title: 'Details',
      step3Title: 'Confirm',
      whatDoYouNeed: 'What do you need help with?',
      searchOrBrowse: 'Search or browse our services',
      searchPlaceholder: 'Search services... (e.g., leak, lock, boiler)',
      popularServices: 'Popular services',
      foundServices: 'Found {count} matching services:',
      noServicesFound: 'No services found for',
      clearSearch: 'Clear search',
      emergency: 'Emergency',
      emergencyArrival: 'Priority dispatch',
      scheduled: 'Scheduled',
      scheduledSave: 'Flexible scheduling',
      selectSpecificService: 'Select a specific service:',
      continue: 'Continue',
      tellUsMore: 'Tell us more',
      provideDetails: 'Provide details about your issue and location',
      preferredDate: 'Preferred Date',
      describeProblem: 'Describe the problem',
      problemPlaceholder: 'E.g., Power went out in the kitchen, no lights working...',
      streetAddress: 'Street Address',
      addressPlaceholder: 'Start typing your address...',
      city: 'City',
      cityPlaceholder: 'Amsterdam',
      postalCode: 'Postal Code',
      postalCodePlaceholder: '1000 AA',
      reviewBooking: 'Review Booking',
      confirmBooking: 'Confirm your booking',
      reviewDetails: 'Review the details and complete payment',
      location: 'Location',
      estimatedArrival: 'Estimated arrival',
      scheduledFor: 'Scheduled for',
      serviceType: 'Service type',
      pricingModel: 'Pricing model',
      hourlyRateLabel: 'Hourly rate',
      fixedPriceLabel: 'Fixed price',
      callOutFee: 'Call-out fee',
      hourlyRate: 'Hourly rate',
      estimatedHours: 'Estimated hours',
      hourlySubtotal: 'Hourly subtotal',
      serviceFee: 'Service fee',
      emergencySurcharge: 'Emergency surcharge',
      included: 'included',
      hourlyNote: 'Final price will be calculated based on actual hours worked. You\'ll be asked to approve any additional time before work continues.',
      servicePrice: 'Service price',
      estimatedTotal: 'Estimated Total',
      total: 'Total',
      finalPriceNote: 'Final price based on actual hours',
      escrowProtection: 'Escrow Payment Protection',
      escrowDescription: 'Your payment is held securely in escrow and only released to the technician 3 days after the job is completed, provided no dispute is open — so you\'re protected until you\'re satisfied.',
      redirectingPayment: 'Redirecting to payment...',
      payWithIdeal: 'Pay with iDEAL / Card',
      signInToBook: 'Sign in to book',
      signInRequired: 'You need to be signed in to book a service.',
      estimatedDuration: 'Estimated Duration',
      estimatedDurationDesc: 'This service is billed by the hour. How long do you estimate the job will take?',
      estimatedHoursLabel: 'Estimated hours',
      hours: 'hours',
      technicianConfirm: 'The technician will confirm the estimate on arrival. You\'ll need to approve any additional time.',
    },
    // Service Landing
    serviceLanding: {
      professional: 'Professional',
      services: 'Services',
      availableDesc: 'Book vetted professionals, typically arriving within',
      eta: 'ETA',
      warranty: 'Quality Guaranteed',
      rating: 'Rating',
      book: 'Book',
      callNow: 'Call Now',
      startingPrice: 'starting price',
      perHour: 'per hour',
      callOutFee: 'call-out fee',
      serviceFee: 'service fee',
      features: {
        fixedPricing: 'Fixed transparent pricing',
        noHiddenFees: 'No hidden fees',
        verifiedTechnicians: 'Verified & insured technicians',
        warrantyIncluded: 'Quality guaranteed',
        payAfterCompletion: 'Pay after job completion',
        emergencySurcharge: 'Emergency surcharge',
      },
      ourServices: 'Our',
      comprehensive: 'Comprehensive',
      servicesFor: 'services for all your needs',
      whyChoose: 'Why Choose Bricos for',
      fastResponse: 'Fast Response',
      fastResponseDesc: 'Our technicians are dispatched quickly',
      evenForEmergencies: 'even for emergencies',
      verifiedExperts: 'Verified Experts',
      verifiedExpertsDesc: 'All technicians are background-checked, certified, and fully insured.',
      trustedByThousands: 'Fair prices, vetted pros',
      trustedByThousandsDesc: 'Book with confidence — escrow-protected from start to finish.',
      otherServices: 'Other Services You Might Need',
      moreServices: 'More Services',
    },
    // Property Manager Portal
    pm: {
      portal: 'Property Manager Portal',
      dashboard: 'Dashboard',
      properties: 'Properties',
      contractors: 'Contractors',
      jobs: 'Jobs',
      bookService: 'Book Service',
      disputes: 'Disputes',
      reports: 'Reports',
      billing: 'Billing',
      team: 'Team',
      settings: 'Settings',
      signOut: 'Sign Out',
      organizations: 'Organizations',
      organizationSettings: 'Organization Settings',
      addProperty: 'Add Property',
      bulkBook: 'Bulk Book',
      propertyName: 'Property Name',
      address: 'Address',
      city: 'City',
      postalCode: 'Postal Code',
      numberOfUnits: 'Number of Units',
      adding: 'Adding...',
      totalUnits: 'Total Units',
      activeJobs: 'Active Jobs',
      completed: 'Completed',
      totalSpent: 'Total Spent',
      avgJobCost: 'Avg Job Cost',
      monthlySpending: 'Monthly Spending',
      last6Months: 'Last 6 months',
      jobsByProperty: 'Jobs by Property',
      recentJobs: 'Recent Jobs',
      noRecentJobs: 'No recent jobs',
      viewAllJobs: 'View All Jobs',
      yourProperties: 'Your Properties',
      noProperties: 'No properties yet',
      addFirstProperty: 'Add your first property',
      units: 'units',
      manageUnits: 'Manage Units',
      // Contractor management
      allContractors: 'All Contractors',
      external: 'External',
      platformLinked: 'Platform Linked',
      pending: 'Pending',
      withContract: 'With Contract',
      searchContractors: 'Search contractors...',
      addContractor: 'Add Contractor',
      inviteSent: 'Invite Sent',
      accepted: 'Accepted',
      active: 'Active',
      suspended: 'Suspended',
      sendInvite: 'Send Invite',
      resendInvite: 'Resend Invite',
      uploadContract: 'Upload Contract',
      managePricing: 'Manage Pricing',
      manageCoverage: 'Manage Coverage',
      viewContract: 'View Contract',
      suspend: 'Suspend',
      reactivate: 'Reactivate',
      noContractors: 'No contractors',
      addFirstContractor: 'Add your first contractor',
      // Roles
      owner: 'Owner',
      admin: 'Admin',
      dispatcher: 'Dispatcher',
      finance: 'Finance',
      readonly: 'Read Only',
      manageDescription: 'Manage your properties and maintenance',
      addNewProperty: 'Add New Property',
      noJobsYet: 'No jobs yet',
      jobReports: 'Job Reports',
      viewAllReports: 'View all reports',
      report: 'Report',
      // Billing page
      financeInvoicing: 'Finance & Invoicing',
      billingInvoices: 'Billing & Invoices',
      billingSubtitle: 'Manage invoices and accounting integration',
      totalInvoices: 'Total Invoices',
      paid: 'Paid',
      overdue: 'Overdue',
      monthlyInvoicesRevenue: 'Monthly Invoices & Revenue',
      invoiceStatus: 'Invoice Status',
      invoicesByProperty: 'Invoices by Property',
      noInvoiceData: 'No invoice data yet',
      noStatusData: 'No status data yet',
      allInvoices: 'All Invoices',
      connections: 'Connections',
      allCaughtUp: 'All caught up!',
      noPendingInvoices: 'No pending invoices',
      noPaidInvoices: 'No paid invoices yet',
      connectedProperties: 'Connected Properties',
      connectNew: 'Connect New',
      loadingConnections: 'Loading connections...',
      noAccountingConnections: 'No accounting connections',
      connectAccountingDesc: 'Connect your properties to accounting software to auto-sync invoices',
      connectAccountingSoftware: 'Connect Accounting Software',
      connected: 'Connected',
      lastSync: 'Last sync',
      connect: 'Connect',
      billingPreference: 'Billing Preference',
      monthlyConsolidated: 'Monthly Consolidated',
      monthlyConsolidatedDesc: 'One invoice for all jobs at month end',
      perJob: 'Per Job',
      perJobDesc: 'Invoice for each completed job',
      perBuilding: 'Per Building',
      perBuildingDesc: 'Separate invoice per property',
      accountingIntegration: 'Accounting Integration',
      connectAccountingAutoSync: 'Connect your accounting software to automatically sync invoices',
      supported: 'Supported',
      configureIntegration: 'Configure Integration',
      export: 'Export',
      exportDesc: 'Export your invoices to various formats for accounting and record-keeping.',
      exportCSV: 'Export CSV',
      exportExcel: 'Export Excel',
      mt940Format: 'MT940 Format',
      mt940Desc: 'MT940 format is compatible with most Dutch bank reconciliation systems.',
      selectProperty: 'Select Property',
      chooseProperty: 'Choose a property',
      accountingSystem: 'Accounting System',
      chooseAccountingSoftware: 'Choose accounting software',
      comingSoon: 'Coming soon',
      afasConfiguration: 'AFAS Configuration',
      afasTokenDesc: 'AFAS uses token-based authentication. Enter your API token and environment key below.',
      apiToken: 'API Token',
      enterAfasToken: 'Enter your AFAS API token',
      environmentKey: 'Environment Key',
      afasEnvironmentDesc: 'Your AFAS environment identifier (found in your AFAS portal)',
      oauthConnectDesc: 'Clicking "Connect" will open a popup to authorize',
      forProperty: 'for',
      connecting: 'Connecting...',
      cancel: 'Cancel',
      disconnectAccounting: 'Disconnect Accounting',
      disconnectConfirm: 'Are you sure you want to disconnect this accounting integration? Future invoices will no longer sync automatically.',
      disconnect: 'Disconnect',
      disconnecting: 'Disconnecting...',
      syncInvoices: 'Sync',
      generateInvoices: 'Generate',
      vsLastMonth: 'vs last month',
      noInvoicesToExport: 'No invoices to export',
      createInvoicesFirst: 'Create some invoices first before exporting.',
      exportComplete: 'Export complete',
      exportFailed: 'Export failed',
      noPaidForMT940: 'No paid invoices',
      mt940RequiresPaid: 'MT940 export requires at least one paid invoice.',
    },
    // Technician Portal
    tech: {
      portal: 'Technician Portal',
      overview: 'Overview',
      activeJobs: 'Active Jobs',
      calendar: 'Calendar',
      earnings: 'Earnings',
      reviews: 'Reviews',
      settings: 'Settings',
      payoutHistory: 'Payout History',
      signOut: 'Sign Out',
      online: 'Online',
      offline: 'Offline',
      available: 'Available',
      noTechnicianProfile: 'No Technician Profile',
      noProfileMessage: "You don't have an active technician profile yet.",
      goHome: 'Go Home',
      applyAsTechnician: 'Apply as Technician',
      hello: 'Hello',
      youreOnline: "You're online and ready for jobs",
      youreOffline: "You're currently offline",
      newJobs: 'new jobs',
      incomingJobs: 'Incoming Jobs',
      pending: 'Pending',
      totalEarned: 'Total Earned',
      completed: 'Completed',
      thisMonth: 'this month',
      rating: 'Rating',
      weeklyEarnings: 'Weekly Earnings',
      last7Days: 'Last 7 days',
      jobsByService: 'Jobs by Service',
      activeJobsTitle: 'Active Jobs',
      noActiveJobs: 'No active jobs',
      waitingForJobs: 'Waiting for new job offers',
      recentReviews: 'Recent Reviews',
      noReviews: 'No reviews yet',
      allReviews: 'All Reviews',
      showLess: 'Show Less',
      startJob: 'Start Job',
      completeJob: 'Complete Job',
      openChat: 'Open Chat',
      downloadReport: 'Download Report',
      requestPause: 'Request Pause',
      cancelPause: 'Cancel Pause',
      pauseRequested: 'Pause Requested',
      paused: 'Paused',
      waitingForJobsDesc: "You'll be notified when a new job is available nearby",
      jobHistory: 'Job History',
      arrivedStartJob: "I've Arrived - Start Job",
      pausePendingApproval: 'Pause request pending customer approval',
      clientConfirmed: 'Client confirmed',
      awaitingClientConfirmation: 'Awaiting client confirmation',
      completedJobs: 'Completed Jobs',
      viewAll: 'View all',
      goOnlineForJobs: 'Go online to receive job offers',
      disputeStatusPending: "Pending",
      disputeStatusUnderReview: "Under Review",
      disputeStatusAwaitingTechnician: "Awaiting Your Response",
      disputeStatusAwaitingCustomer: "Awaiting Customer",
      disputeStatusResolved: "Resolved",
      disputeStatusEscalated: "Escalated",
      disputeStatusDismissed: "Dismissed",
      notATechnician: "Not a Technician",
      needTechProfileDisputes: "You need a technician profile to view disputes.",
      backToDashboard: "Back to Dashboard",
      myDisputes: "My Disputes",
      activeLower: "active",
      resolvedLower: "resolved",
      noDisputes: "No Disputes",
      noDisputesMessage: "Great work! No customers have filed disputes against your jobs.",
      activeDisputes: "Active Disputes",
      responseRequired: "Response required",
      jobDetails: "Job Details",
      customersComplaint: "Customer's Complaint",
      customerEvidence: "Customer Evidence",
      resolution: "Resolution",
      filed: "Filed",
      outcome: "Outcome",
      payoutStatusPending: "Pending",
      payoutStatusProcessing: "Processing",
      payoutStatusCompleted: "Completed",
      payoutStatusFailed: "Failed",
      projectStatusNotified: "New",
      projectStatusAccepted: "Accepted",
      projectStatusDeclined: "Declined",
      projectStatusFilled: "Filled",
      projectStatusExpired: "Expired",
      projectStart: "Start",
      projectBudget: "Budget",
      photos: "photos",
      acceptAndSubmitQuote: "Accept & Submit Quote",
      pass: "Pass",
      submitQuote: "Submit Quote",
      dashboard: "Dashboard",
      projectOpportunities: "Project Opportunities",
      projectOpportunitiesSubtitle: "Renovation & construction projects matched to your skills",
      tabNew: "New",
      tabAccepted: "Accepted",
      tabPast: "Past",
      noNewProjects: "No new project opportunities at the moment. Check back soon!",
      noAcceptedProjects: "No accepted projects yet.",
      noPastProjects: "No past opportunities.",
      projectAcceptedToast: "Project accepted! You can now submit your quote.",
      projectAcceptFailedToast: "Failed to accept project.",
      projectDeclinedToast: "Project declined.",
      projectDeclineFailedToast: "Failed to decline project.",
      loading: "Loading...",
    },

    projects: {
      statusDraft: "Draft",
      statusMatching: "Matching",
      statusAwaitingQuotes: "Awaiting Quotes",
      statusQuoteAccepted: "Quote Accepted",
      statusActive: "Active",
      statusInProgress: "In Progress",
      statusCompleted: "Completed",
      statusCancelled: "Cancelled",
      statusDisputed: "Disputed",
      dashboard: "Dashboard",
      title: "My Projects",
      subtitle: "Manage your renovation & construction projects",
      newProject: "New Project",
      emptyTitle: "No projects yet",
      emptyDescription: "Post your first renovation or construction project and get matched with 3 vetted professionals.",
      postAProject: "Post a Project",
      notFound: "Project not found.",
      backToProjects: "Back to Projects",
      posted: "Posted",
      matchedTechnicians: "Your 3 Matched Technicians",
      waitingForTechnician: "Waiting for technician…",
      slot: "Slot",
      of: "of",
      technician: "Technician",
      jobs: "jobs",
      quoteSubmitted: "Quote submitted",
      quotePending: "Quote pending",
      daysLeftToSubmit: "days left to submit",
      waitingForQuotes: "Waiting for quotes…",
      compareQuotes: "Compare Quotes",
      received: "received",
      tabOverview: "Overview",
      tabMessages: "Messages",
      tabDocuments: "Documents",
      projectProgress: "Project Progress",
      milestoneDone: "Done",
      milestoneUpcoming: "Upcoming",
      projectDetails: "Project Details",
      location: "Location",
      startDate: "Start Date",
      duration: "Duration",
      budget: "Budget",
      noMessages: "No messages yet. Start a conversation!",
      typeMessage: "Type a message…",
      documentsEmpty: "Documents will appear here once the project progresses.",
      thisProjectIs: "This project is",
      acceptQuote: "Accept Quote",
      hiringPrefix: "You're hiring",
      hiringFor: "for",
      hiringSuffix: "— this will notify them immediately and create your project. Ready to proceed?",
      cancel: "Cancel",
      confirmAndHire: "Confirm & Hire",
      quoteAcceptedToast: "Quote accepted! Your project is now active.",
      quoteAcceptFailedToast: "Failed to accept quote.",
      messageSentToast: "Message sent",
      messageSendFailedToast: "Failed to send message",
      stepBasics: "Project Basics",
      stepLocation: "Location & Timing",
      stepBudget: "Budget & Review",
      back: "Back",
      next: "Next",
      newProjectSubtitle: "Find vetted professionals for your renovation or construction project",
      matchingTitle: "Matching your project…",
      matchingSubtitle: "Finding the best technicians on the platform for your project",
      findTechnicians: "Find my 3 technicians",
      fieldTitle: "Project Title",
      fieldCategory: "Category",
      fieldDescription: "Description",
      charactersMinimum: "characters minimum",
      fieldPhotos: "Photos (optional, max 8)",
      photosHint: "Help technicians understand the scope",
      uploading: "Uploading...",
      dropPhotos: "Drop photos here",
      dragDropPhotos: "Drag & drop or click to upload",
      fieldAddress: "Property Address",
      fieldCity: "City",
      fieldPostalCode: "Postal Code",
      fieldStartDate: "Preferred Start Date",
      fieldTimeline: "Timeline Flexibility",
      timelineFixed: "Fixed date",
      timelineFlex2Weeks: "Flexible within 2 weeks",
      timelineFlex1Month: "Flexible within a month",
      fieldDuration: "Estimated Project Duration",
      selectDuration: "Select duration",
      fieldBudget: "Budget Range (optional)",
      shareWithTechnicians: "Share with technicians",
      selectBudget: "Select budget range",
      reviewTitle: "Review Your Project",
      reviewFieldTitle: "Title",
      maxPhotosToast: "Maximum 8 photos allowed",
      createFailedToast: "Failed to create project. Please try again.",
      validationEnterTitle: "Enter a title",
      validationSelectCategory: "Select a category",
      validationDescriptionNeeds: "Description needs",
      validationMoreCharacters: "more characters",
      validationEnterAddress: "Enter address",
      validationEnterCity: "Enter city",
      validationEnterPostalCode: "Enter postal code",
      validationSelectStartDate: "Select start date",
      validationSelectDuration: "Select duration",
      placeholderTitle: "e.g. \"Bathroom renovation — 2 bathrooms\"",
      placeholderDescription: "Describe the work needed, current state, any specific requirements…",
      placeholderAddress: "Street + house number",
      placeholderCity: "Rotterdam",
    },

    referrals: {
      nav: "Invite Friends",
      title: "Invite friends, earn points",
      subtitle: "Share Bricos. When a friend signs up and completes their first job, you both get {n} loyalty points.",
      yourLink: "Your invite link",
      linkPending: "Available after launch",
      copied: "Copied!",
      share: "Share",
      email: "Email",
      yourCode: "Your code",
      statInvited: "Invited",
      statJoined: "Rewarded",
      statPoints: "Points earned",
      howItWorks: "How it works",
      step1: "Share your personal invite link with friends.",
      step2: "They sign up and book their first job.",
      step3: "You both get {n} loyalty points once it's completed.",
      shareMessage: "I'm using Bricos for home services — vetted pros, fixed prices. Sign up with my link:",
      dashboardTitle: "Invite friends, earn points",
      dashboardSubtitle: "You both get 250 loyalty points when a friend completes their first job",
    },

    pricingGuide: {
      from: "from",
      perHour: "/hour",
      serviceFee: "service fee",
      basePrice: "Fixed price",
      callOutFee: "Call-out fee",
      standard: "Standard / planned",
      emergency: "Emergency",
      emergencySurcharge: "Emergency surcharge",
      costOf: "What does",
      costInNl: "cost in the Netherlands?",
      heroSub: "Real, fixed prices — shown upfront before you book. No quotes to chase, no surprises.",
      priceNote: "Prices are indicative starting prices; the exact fixed price is confirmed before you book.",
      tp1Title: "Fixed & upfront",
      tp1Desc: "You see the price before booking — not after.",
      tp2Title: "Escrow-protected",
      tp2Desc: "We hold payment until the job is done to your satisfaction.",
      tp3Title: "Vetted & insured",
      tp3Desc: "Every pro is ID-, credential- and insurance-checked.",
      whatsIncluded: "What's included",
      typicalEta: "Typical arrival",
      ctaTitle: "Ready to book at a fixed price?",
      ctaSub: "See your exact price and book a vetted pro in a couple of taps.",
      bookCta: "Book",
      learnMore: "Learn more",
      otherPrices: "Prices for other services",
      hubTitle: "Home service prices in the Netherlands (2026) — fixed & transparent",
      hubHeading: "What do home services cost in the Netherlands?",
      hubSub: "Most platforms make you chase quotes. We publish real, fixed prices — you see the price before you book, and it's held in escrow until the job is done.",
      howItWorks: "How our pricing works",
      hp1Title: "Fixed price + flat fee",
      hp1Desc: "A fixed service price + a flat €{fee} fee. That's it.",
      hp2Title: "Emergency is a set surcharge",
      hp2Desc: "Need it urgently? A clear, fixed emergency surcharge — never a mystery markup.",
      hp3Title: "Paid safely via escrow",
      hp3Desc: "Your money is held until the job's done and you're happy.",
      trustLine: "Vetted, insured pros — fixed prices — escrow-protected",
      ctaBtn: "Get a price & book",
    },
    // Admin Portal
    admin: {
      portal: 'Admin Portal',
      dashboard: 'Dashboard',
      bookings: 'Bookings',
      users: 'Users',
      technicians: 'Technicians',
      disputes: 'Disputes',
      reconciliation: 'Reconciliation',
      settings: 'Settings',
      signOut: 'Sign Out',
      totalBookings: 'Total Bookings',
      totalRevenue: 'Total Revenue',
      activeTechnicians: 'Active Technicians',
      openDisputes: 'Open Disputes',
      recentBookings: 'Recent Bookings',
      manageRoles: 'Manage Roles',
      addRole: 'Add Role',
      removeRole: 'Remove Role',
      searchUsers: 'Search users...',
      noUsers: 'No users found',
      userDetails: 'User Details',
      bookingDetails: 'Booking Details',
      // Statuses
      pending: 'Pending',
      confirmed: 'Confirmed',
      technicianAssigned: 'Technician Assigned',
      inProgress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
      underReview: 'Under Review',
      awaitingTechnician: 'Awaiting Technician',
      awaitingCustomer: 'Awaiting Customer',
      resolved: 'Resolved',
      escalated: 'Escalated',
      acceptanceRate: "Acceptance rate",
      activeJobs: "Active Jobs",
      activeUsersPerDay: "Active users per day",
      actor: "Actor",
      addNewRole: "Add New Role",
      addOrRemoveRoles: "Add or remove roles for",
      adminDashboard: "Admin Dashboard",
      adminNotes: "Admin Notes",
      adminNotesOptional: "Admin Notes (optional)",
      adminNotesPlaceholder: "Internal notes about this decision...",
      admins: "Admins",
      admin: "Admin",
      ago: "ago",
      all: "All",
      allClear: "All Clear",
      allDisputes: "All Disputes",
      allRatings: "All Ratings",
      allRoles: "All Roles",
      allStatuses: "All Statuses",
      allTechnicians: "All Technicians",
      annualPlans: "Annual plans",
      appliedOn: "Applied on",
      appliedRecently: "Applied recently",
      approve: "Approve",
      approved: "Approved",
      approvedOnly: "Approved Only",
      approveTechDesc: "will be able to receive and accept job offers.",
      approveTechnicianTitle: "Approve Technician?",
      assigned: "Assigned",
      available: "Available",
      averageRating: "Average Rating",
      awaitingResponse: "Awaiting Response",
      awaitingTech: "Awaiting Tech",
      booking: "Booking",
      bookingId: "Booking ID",
      bookingsOverTime: "Bookings over time",
      businessDetails: "Business Details",
      chatMessages: "Chat Messages",
      clear: "Clear",
      clearDates: "Clear dates",
      clearFilters: "Clear Filters",
      completedJobs: "Completed Jobs",
      connected: "Connected",
      contribution: "Contribution",
      costPerBooking: "Cost per booking (CAC, est.)",
      created: "Created",
      criticalIssues: "Critical Issues",
      currentRoles: "Current Roles",
      customer: "Customer",
      customerEvidence: "Customer Evidence",
      customerStatement: "Customer Statement",
      dashboardLoadError: "Failed to load dashboard data",
      decision: "Decision",
      deleteReview: "Delete Review",
      deleteReviewTitle: "Delete this review?",
      demand: "DEMAND",
      discrepancies: "Discrepancies",
      dismissDispute: "Dismiss Dispute",
      dispute: "Dispute",
      disputeResolution: "Dispute Resolution",
      emergency: "Emergency",
      escalate: "Escalate",
      event: "Event",
      filterByRole: "Filter by role",
      filterByStatus: "Filter by status",
      filteredBookings: "Filtered Bookings",
      fullRefund: "Full Refund to Customer",
      grossMarginJob: "Gross margin/job",
      issue: "Issue",
      jobOffersVsAccepted: "Job offers vs accepted",
      jobReport: "Job Report",
      jobs: "jobs",
      joined: "Joined",
      last100Checked: "Last 100 checked",
      loadingDashboard: "Loading dashboard...",
      location: "Location",
      lowRatings: "Low Ratings (≤2)",
      manageBookings: "View and manage all platform bookings",
      manageDisputes: "Manage and resolve customer disputes",
      manageUserRoles: "Manage User Roles",
      manageUsers: "Manage platform users and their roles",
      method: "Method",
      metrics: "Metrics",
      moderateReviews: "Monitor and moderate platform reviews",
      mollieStatus: "Mollie Status",
      noBookings: "No bookings found",
      noBookingsDesc: "There are no bookings on the platform yet.",
      noBookingsMatch: "No bookings match your filters",
      noBookingsMatchDesc: "Try adjusting your search or filters.",
      noBookingsYet: "No bookings yet",
      noDescription: "No description provided",
      noDiscrepancies: "No payment discrepancies found",
      noDisputes: "No disputes found",
      noDisputesDesc: "There are no disputes matching this filter.",
      noEvents: "No Events",
      noEventsDesc: "No payment events recorded yet",
      noResponse: "No Response",
      noResponseYet: "No response yet",
      noReviews: "No reviews found",
      noReviewsDesc: "There are no reviews matching your criteria.",
      noRoles: "No roles",
      noRolesAssigned: "No roles assigned",
      notConfigured: "Not Configured",
      noTechnicians: "No technicians found",
      noTechniciansDesc: "There are no technician applications matching this filter yet.",
      notSet: "Not set",
      noUsersDesc: "No users found matching your criteria.",
      orExactAmount: "Or enter exact amount",
      overdue: "Overdue",
      overdueSla: "Overdue SLA",
      partialRefund: "Partial Refund",
      paymentDiscrepancies: "Payment Discrepancies",
      paymentDiscrepanciesDesc: "Payments with mismatched status between Mollie and database",
      paymentEventLedger: "Payment Event Ledger",
      paymentEventLedgerDesc: "Recent payment state transitions and actions",
      paymentId: "Payment ID",
      paymentReconciliation: "Payment Reconciliation",
      payments: "Payments",
      paymentSplit: "Payment Split",
      pendingReview: "Pending Review",
      pendingVerification: "Pending Verification",
      phone: "Phone",
      platformMarginOverTime: "Platform margin over time",
      platformOverview: "Platform overview and metrics",
      price: "Price",
      pricing: "Pricing",
      problemDetails: "Problem Details",
      problemPhotos: "Problem Photos",
      propertyManager: "Property Manager",
      propertyMgrs: "Property Mgrs",
      rating: "Rating",
      recentEvents: "Recent Events",
      reconciliationDesc: "Monitor and resolve payment discrepancies between Mollie and database",
      reconsider: "Reconsider",
      refreshPage: "Refresh Page",
      reject: "Reject",
      rejectApplicationTitle: "Reject Application?",
      rejected: "Rejected",
      rejectedOn: "Rejected on",
      rejectionReason: "Rejection Reason",
      rejectionReasonPlaceholder: "Explain why the application is being rejected...",
      rejectionReasonRequired: "Rejection Reason *",
      rejectTechDesc: "will be notified that their application was rejected.",
      releaseToTechnician: "Release to Technician",
      remaining: "remaining",
      remove: "Remove",
      repeatRate: "Repeat rate",
      replied: "Replied",
      requestEvidence: "Request Evidence",
      requestMoreEvidence: "Request More Evidence",
      requestResponse: "Request Response",
      requireImmediateAction: "Require immediate action",
      resolution: "Resolution",
      resolutionNotesLabel: "Resolution Notes (visible to customer)",
      resolveExecute: "Resolve & Execute Payment",
      respondedOn: "Responded on",
      response: "Response",
      retention: "RETENTION",
      review: "Review",
      reviewApplications: "Review and approve technician applications",
      reviewDetails: "Review Details",
      reviews: "Reviews",
      revoke: "Revoke",
      reworkRequired: "Rework Required",
      roles: "Roles",
      saveNotes: "Save Notes",
      searchBookingsPlaceholder: "Search by customer, technician, address, ID...",
      searching: "Searching",
      searchReviewsPlaceholder: "Search by customer, technician, or review text...",
      searchUsersPlaceholder: "Search by name or phone...",
      selectDateRange: "Select date range",
      selectResolution: "Select resolution",
      service: "Service",
      servicesOffered: "Services Offered",
      shouldBeReviewed: "Should be reviewed",
      showing: "Showing",
      sla: "SLA",
      slaBreached: "SLA breached",
      slaDeadline: "SLA Deadline",
      slowConnectionRetry: "This may be due to a slow connection. Please try again.",
      splitPayment: "Split Payment",
      supply: "SUPPLY",
      technician: "Technician",
      technicianEvidence: "Technician Evidence",
      technicianResponse: "Technician Response",
      techResponded: "Tech Responded",
      techResponses: "Tech Responses",
      timeline: "Timeline",
      totalPayments: "Total Payments",
      totalReviews: "Total Reviews",
      totalTechnicians: "Total Technicians",
      totalUsers: "Total Users",
      tryAgain: "Try Again",
      twoPlusBookings: "2+ bookings",
      unavailable: "Unavailable",
      unitEconomics: "UNIT ECONOMICS",
      user: "User",
      userManagement: "User Management",
      verifiedTechs: "Verified Techs",
      warnings: "Warnings",
      withResponse: "With Response",
      technicianApproved: "Technician approved",
      applicationRejected: "Application rejected",
      technicianApprovedDesc: "The technician can now receive job offers",
      applicationRejectedDesc: "The technician has been notified of the rejection",
      rejectionReasonRequiredTitle: "Rejection reason required",
      rejectionReasonRequiredDesc: "Please provide a reason for rejecting this application",
      unknown: "Unknown",
      notProvided: "Not provided",
      kvk: "KVK",
      vat: "VAT",
      company: "Company",
      discMolliePaidDbPending: "Mollie Paid, DB Pending",
      discEscrowNotReleased: "Escrow Not Released",
      discMollieRefundedDbNot: "Mollie Refunded, DB Not",
      discDbRefundedMollieNot: "DB Refunded, Mollie Not",
      discPaymentFailedStuck: "Payment Failed/Stuck",
      discNoMollieId: "No Mollie ID",
      releaseEscrow: "Release Escrow",
      releaseEscrowDescStart: "This will release",
      releaseEscrowDescEnd: "to the technician. This action cannot be undone. Are you sure?",
      releasePayment: "Release Payment",
      disputeResolvedToast: "Dispute resolved and payment action executed",
      resolveDisputeFailed: "Failed to resolve dispute",
      evidenceRequestedFrom: "Evidence requested from",
      notesSaved: "Notes saved",
      disputeEscalated: "Dispute escalated",
      unassigned: "Unassigned",
      problem: "Problem",
      diagnosis: "Diagnosis",
      technicianNotes: "Technician Notes",
      partsUsed: "Parts Used",
      before: "Before",
      after: "After",
      disputeNotesPlaceholder: "Internal notes about this dispute...",
      explainDecisionPlaceholder: "Explain the decision...",
      customerRefund: "Customer Refund",
      split: "Split",
      reviewDeleted: "Review deleted",
      deleteReviewFailed: "Failed to delete review",
      unknownTechnician: "Unknown Technician",
      noText: "No text",
      noReviewText: "No review text provided",
      star: "Star",
      stars: "Stars",
      deleteReviewConfirmStart: "This will permanently remove this",
      deleteReviewConfirmMid: "-star review by",
      deleteReviewConfirmEnd: "This action cannot be undone.",
      roleAdded: "Role added successfully",
      addRoleFailed: "Failed to add role",
      roleRemoved: "Role removed successfully",
      removeRoleFailed: "Failed to remove role",
      userAlreadyHasRole: "User already has this role",
      unnamedUser: "Unnamed User",
      thisUser: "this user",
      autoReleaseHours: "Auto-Release Hours",
      autoReleaseHoursDesc: "Hours after job completion before escrow auto-releases (if no dispute)",
      disputeWindow: "Dispute Window",
      disputeWindowDesc: "Hours after completion during which users can open a dispute",
      disputeSla: "Dispute SLA",
      disputeSlaDesc: "Target resolution time for disputes",
      emergencySearchTimeout: "Emergency Search Timeout",
      emergencySearchTimeoutDesc: "Minutes to search for technician before auto-refund (emergency bookings)",
      scheduledSearchTimeout: "Scheduled Search Timeout",
      scheduledSearchTimeoutDesc: "Hours before scheduled time to start searching / timeout",
      technicianOfferTimeout: "Technician Offer Timeout",
      technicianOfferTimeoutDesc: "Seconds a technician has to accept a job offer",
      noShowGracePeriod: "No-Show Grace Period",
      noShowGracePeriodDesc: "Minutes after ETA before technician can mark customer as no-show",
      unitHours: "hours",
      unitMinutes: "minutes",
      unitSeconds: "seconds",
      platformSettings: "Platform Settings",
      settingsDescription: "Configure timers, SLAs, and run security tests",
      timerConfiguration: "Timer Configuration",
      timerConfigDescription: "Configure platform timers for escrow, disputes, and technician dispatch",
      saveChanges: "Save Changes",
      reset: "Reset",
      securityTests: "Security Tests",
      securityTestsDescription: "Run automated RLS (Row Level Security) verification tests",
      rlsTestsVerifyIsolation: "RLS Tests verify data isolation",
      rlsTestsWarning: "These tests ensure unauthenticated users cannot access sensitive data. Run before each production deployment.",
      runRlsTests: "Run RLS Tests",
      allTestsPassed: "All tests passed",
      vulnerabilitiesDetected: "Vulnerabilities detected",
      passed: "passed",
      failed: "failed",
      of: "of",
      tests: "tests",
      rlsTestsPassed: "RLS tests passed",
      rlsTestsFailedReview: "RLS tests failed - review results",
      failedToRunTests: "Failed to run tests",
      failedToLoadSettings: "Failed to load settings",
      settingsSavedSuccessfully: "Settings saved successfully",
      failedToSaveSettings: "Failed to save settings",
    },
    // Common UI elements
    ui: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      save: 'Save',
      cancel: 'Cancel',
      confirm: 'Confirm',
      delete: 'Delete',
      edit: 'Edit',
      view: 'View',
      close: 'Close',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      search: 'Search',
      filter: 'Filter',
      sort: 'Sort',
      export: 'Export',
      import: 'Import',
      download: 'Download',
      upload: 'Upload',
      refresh: 'Refresh',
      retry: 'Retry',
      noData: 'No data',
      noResults: 'No results',
      selectAll: 'Select All',
      deselectAll: 'Deselect All',
      actions: 'Actions',
      status: 'Status',
      date: 'Date',
      time: 'Time',
      amount: 'Amount',
      total: 'Total',
      subtotal: 'Subtotal',
      tax: 'Tax',
      fee: 'Fee',
      discount: 'Discount',
      description: 'Description',
      notes: 'Notes',
      details: 'Details',
      information: 'Information',
      contact: 'Contact',
      phone: 'Phone',
      email: 'Email',
      name: 'Name',
      address: 'Address',
      city: 'City',
      country: 'Country',
      postalCode: 'Postal Code',
      today: 'Today',
      yesterday: 'Yesterday',
      thisWeek: 'This Week',
      thisMonth: 'This Month',
      lastMonth: 'Last Month',
      year: 'Year',
      all: 'All',
      none: 'None',
      yes: 'Yes',
      no: 'No',
      or: 'or',
      and: 'and',
    },
    // Service names (for footer and other places)
    serviceNames: {
      plumbing: 'Plumbing',
      electrician: 'Electrical',
      hvac: 'HVAC',
      boiler: 'Boiler Repair',
      locksmith: 'Locksmith',
      appliance: 'Appliances',
      roofing: 'Roofing',
      cleaning: 'Cleaning',
      painting: 'Painting',
      flooring: 'Flooring',
      security: 'Security',
      outdoor: 'Outdoor & Garden',
      pest_control: 'Pest Control',
      renovation: 'Renovation',
      handyman: 'Handyman',
      moving: 'Moving',
      smart_home: 'Smart Home',
      sustainable: 'Sustainable Home',
      other: 'Other Services',
    },
    // Popular booking activities
    popularActivities: {
      emergencyPlumbing: 'Emergency plumbing repair',
      lockReplacement: 'Lock replacement',
      emergencyElectrical: 'Emergency electrical repair',
      boilerRepair: 'Boiler repair/installation',
      drainUnclogging: 'Drain unclogging',
      emergencyLocksmith: 'Emergency locksmith',
    },
    // Support & Contact
    supportForm: {
      contactSupport: 'Contact Support',
      contactDesc: 'Send us a message and we\'ll get back to you within 24 hours.',
      name: 'Name',
      namePlaceholder: 'Your name',
      email: 'Email',
      emailPlaceholder: 'your@email.com',
      subject: 'Subject',
      subjectPlaceholder: 'How can we help?',
      message: 'Message',
      messagePlaceholder: 'Describe your issue or question in detail...',
      sendMessage: 'Send Message',
      messageSent: 'Message Sent!',
      messageSentDesc: 'We\'ll get back to you within 24 hours.',
      thankYou: 'Thank you for contacting us. We\'ll get back to you within 24 hours.',
      sendAnother: 'Send Another Message',
      sendFailed: 'Failed to send message',
      sendFailedDesc: 'Please try again or email us directly.',
      needHelp: 'Need Help?',
      needHelpDesc: 'Have a question or issue? Send us a message and we\'ll get back to you within 24 hours.',
    },
    // Chatbot
    chatbot: {
      assistant: 'Bricos Assistant',
      greeting: 'Hi! I\'m Bricos Assistant. I can help answer questions about our services, bookings, payments, and more. How can I help you today?',
      askPlaceholder: 'Ask a question...',
      quickQuestions: 'Quick questions:',
      relatedQuestions: 'Related questions:',
      errorMessage: 'I apologize, but I\'m having trouble connecting right now. Please try again in a moment, or visit our Help page for FAQs.',
      q1: 'How quickly can a technician arrive?',
      q2: 'How does escrow payment work?',
      q3: 'What if I\'m not satisfied with the work?',
      q4: 'How do I cancel a booking?',
    },
    // Tech sidebar extra
    techSidebar: {
      disputes: 'Disputes',
    },
    // Disputes page
    disputesPage: {
      myDisputes: 'My Disputes',
      disputeCount: 'dispute(s)',
      noDisputes: 'No Disputes',
      allCompleted: 'All your jobs have been completed successfully.',
      backToDashboard: 'Back to Dashboard',
      bookingRef: 'Booking',
      fileDispute: 'File Dispute',
    },
    // Dispute Detail page
    disputeDetail: {
      title: 'Dispute Details',
      notFound: 'Dispute Not Found',
      backToDisputes: 'Back to Disputes',
      disputeInfo: 'Dispute Information',
      reason: 'Reason',
      description: 'Description',
      filedOn: 'Filed On',
      service: 'Service',
      relatedBooking: 'Related Booking',
      amount: 'Amount',
      yourEvidence: 'Your Evidence',
      noPhotos: 'No photos submitted',
      technicianResponse: 'Technician Response',
      resolution: 'Resolution',
      decision: 'Decision',
      notes: 'Notes',
      refund: 'Refund',
      addEvidence: 'Add Additional Evidence',
      addEvidenceDesc: 'You can submit additional information or photos to support your case.',
      additionalInfo: 'Additional Information',
      additionalInfoPlaceholder: 'Provide any additional details...',
      additionalPhotos: 'Additional Photos',
      submitEvidence: 'Submit Additional Evidence',
      resolutionExpected: 'Resolution expected by',
      pendingReview: 'Pending Review',
      underReview: 'Under Review',
      awaitingResponse: 'Awaiting Your Response',
      awaitingTechnician: 'Awaiting Technician',
      escalated: 'Escalated',
      resolved: 'Resolved',
    },
    // Support page
    supportPage: {
      title: 'Support',
      subtitle: 'Get help with your bookings',
      yourTickets: 'Your Tickets',
      selectTicket: 'Select a ticket',
      selectTicketDesc: 'Choose a ticket from the list or create a new one',
    },
    // Help page
    helpPage: {
      title: 'Help Center',
      subtitle: 'Find answers to common questions or contact our support team',
      aiAssistant: 'AI Assistant',
      aiAssistantDesc: 'Click the chat button in the bottom right for instant answers to common questions',
      emergencyLine: 'Urgent booking help',
      emergencyLineDesc: 'For urgent booking issues, contact us and we\'ll prioritise your request',
      responseTime: 'Response Time',
      responseTimeDesc: 'We typically respond to all inquiries within 24 hours',
      faq: 'Frequently Asked Questions',
      booking: 'Booking',
      payments: 'Payments',
      technicians: 'Technicians',
      accountCategory: 'Account',
      stillNeedHelp: 'Still need help?',
      stillNeedHelpDesc: "Can't find what you're looking for? Send us a message and our support team will get back to you.",
    },
    // Tracking extras
    trackingExtra: {
      lookingForTechnician: 'Looking for a Technician',
      lookingForTechnicianDesc: "We're matching your job with the best available technician. You'll be notified as soon as one accepts.",
      bookingCreated: 'Booking created',
      cancelBooking: 'Cancel Booking',
      cancelFailed: 'Failed to cancel booking',
      bookingCancelled: 'Booking cancelled',
    },
    // Tech Payouts page
    payoutsPage: {
      title: 'Payout History',
      available: 'Available',
      clearedWindow: 'Cleared dispute window',
      inHold: 'In Hold',
      disputeWindow: '-day dispute window',
      totalEarned: 'Total Earned',
      nextPayout: 'Next payout: Weekly on Monday',
      noPayouts: 'No payouts yet',
      platformFee: 'Platform fee',
    },
    // Tech Dashboard extras
    techExtra: {
      inDisputeWindow: 'In Dispute Window',
      dayHold: '-day hold',
      availableForPayout: 'Available',
    },
    // PM Properties page extras
    pmProperties: {
      propertyManagement: 'Property Management',
      propertiesManaged: 'properties managed',
      bulkUpload: 'Bulk Upload',
      noPropertiesYet: 'No properties yet',
      addFirstProperty: 'Add your first property to start managing it',
    },
    // PM Jobs page extras
    pmJobs: {
      jobManagement: 'Job Management',
      activeCount: 'active',
      completedCount: 'completed',
      escalatedCount: 'escalated',
      refresh: 'Refresh',
      allStatus: 'All Status',
      dispatch: 'Dispatch',
      autoDispatch: 'Auto-dispatch (Registry + Network)',
      reassign: 'Reassign',
      contact: 'Contact',
      track: 'Track',
      bookNewJob: 'Book New Job',
      noActiveJobs: 'No active jobs',
      noActiveJobsDesc: 'All jobs are completed or none match your filter',
      noCompletedJobs: 'No completed jobs',
      noCompletedJobsDesc: 'Jobs will appear here once completed',
      slaBreached: 'SLA Breached',
      remaining: 'remaining',
      escalation: 'Escalation',
    },
    // PM Reports page extras
    pmReports: {
      analyticsReports: 'Analytics & Reports',
      analyticsSubtitle: 'Analytics, contractor performance, and SLA tracking',
      exportAll: 'Export All',
      exporting: 'Exporting...',
      totalSpent: 'Total Spent',
      completedJobs: 'Completed Jobs',
      avgJobCost: 'Avg Job Cost',
      monthlySpendingJobs: 'Monthly Spending & Jobs',
      jobsByService: 'Jobs by Service',
      noSpendingData: 'No spending data yet',
      noServiceData: 'No service data yet',
      vsLastMonth: 'vs last month',
      overview: 'Overview',
      contractors: 'Contractors',
      sla: 'SLA',
      costs: 'Costs',
      spendingByProperty: 'Spending by Property',
      jobReports: 'Job Reports',
      searchReports: 'Search reports...',
      noCompletedJobsReport: 'No completed jobs to report on yet',
      downloadReport: 'Download Report',
      technicianReports: 'Technician Reports',
      noMatchingReports: 'No matching reports found',
      adjustSearch: 'Try adjusting your search query',
      reportsAfterCompletion: 'Reports will appear here after jobs are completed',
      generating: 'Generating...',
      report: 'Report',
      jobs: 'jobs',
      matchingOf: 'of',
    },
    // PM Settings page
    pmSettings: {
      title: 'Settings',
      manageOrgPrefs: 'Manage your organization and preferences',
      organization: 'Organization',
      team: 'Team',
      security: 'Security',
      auditLog: 'Audit Log',
      preferences: 'Preferences',
      orgDetails: 'Organization Details',
      companyInfo: 'Your company information',
      companyName: 'Company Name',
      billingEmail: 'Billing Email',
      phone: 'Phone',
      address: 'Address',
      city: 'City',
      postalCode: 'Postal Code',
      saveChanges: 'Save Changes',
      saving: 'Saving...',
      defaultSettings: 'Default Settings',
      defaultSettingsDesc: 'Set defaults for new properties and bookings',
      autoAssign: 'Auto-assign nearest technician',
      autoAssignDesc: 'Automatically select the closest available technician',
      emergencyDefault: 'Emergency mode by default',
      emergencyDefaultDesc: 'All new bookings start as emergency',
      defaultResponseSLA: 'Default response SLA',
      defaultResponseSLADesc: 'Target response time for technicians',
      reportSettings: 'Report Settings',
      reportSettingsDesc: 'Configure how reports are generated and sent',
      includePhotos: 'Include photos in reports',
      includePhotosDesc: 'Attach before/after photos to job reports',
      autoEmailReports: 'Auto-email reports to owner',
      autoEmailReportsDesc: 'Send completed job reports to property owner',
      reportFormat: 'Report format',
      reportFormatDesc: 'Default export format',
    },
    // PM Disputes page
    pmDisputes: {
      disputeManagement: 'Dispute Management',
      title: 'Disputes',
      subtitle: 'Track and monitor dispute resolutions for your properties',
      totalDisputes: 'Total Disputes',
      pending: 'Pending',
      underReview: 'Under Review',
      resolved: 'Resolved',
      avgResolution: 'Avg Resolution',
      customerFavor: 'Customer Favor',
      pastSLADeadline: 'Past SLA Deadline',
      pastSLADeadlineDesc: 'These disputes have exceeded the resolution deadline and may require escalation.',
      searchPlaceholder: 'Search by reason, property, or address...',
      filterByStatus: 'Filter by status',
      allStatuses: 'All Statuses',
      awaitingTech: 'Awaiting Tech',
      awaitingCustomer: 'Awaiting Customer',
      escalated: 'Escalated',
      active: 'Active',
      noActiveDisputes: 'No Active Disputes',
      allResolved: 'All disputes have been resolved.',
      noSearchMatch: 'No disputes match your search criteria.',
      noResolvedDisputes: 'No Resolved Disputes',
      resolvedWillAppear: 'Resolved disputes will appear here.',
      loadingDisputes: 'Loading disputes...',
      noOrgSelected: 'No Organization Selected',
      selectOrgDesc: 'Please select an organization to view disputes.',
      noDescription: 'No description provided',
      techResponded: 'Tech Responded',
      overdue: 'Overdue',
      due: 'Due',
    },
    // PM Contractors page
    pmContractors: {
      contractorHub: 'Contractor Hub',
      title: 'Contractors',
      subtitle: 'Manage your contractor network, contracts, and approve extra work requests',
      approvals: 'Approvals',
    },
    // Service categories (full translations for ServiceSelector/Services)
    serviceCategories: {
      plumbing: {
        label: 'Plumbing & Water',
        description: 'Emergency plumbing, leak repairs, drain cleaning and more',
        eta: '45-90 min',
        activities: ['Emergency plumbing repair', 'Leak detection and repair', 'Drain unclogging', 'Sewer line cleaning', 'Water heater installation/repair', 'Pipe replacement', 'Bathroom/kitchen fixture installation'],
      },
      electrician: {
        label: 'Electrical',
        description: 'Electrical repairs, installations, and safety inspections',
        eta: '30-60 min',
        activities: ['Emergency electrical repair', 'Wiring installation/repair', 'Circuit breaker replacement', 'Lighting installation', 'Smart home device setup', 'Generator installation/maintenance', 'Electrical safety inspection', 'Smart home installation', 'Phone repair', 'Computer repair'],
      },
      hvac: {
        label: 'HVAC',
        description: 'Heating, ventilation, and air conditioning services',
        eta: '60-120 min',
        activities: ['Boiler repair/installation', 'Radiator maintenance', 'Air conditioning installation/repair', 'Ventilation system cleaning', 'Thermostat installation', 'Heat pump service', 'Insulation installation', 'Stove installation'],
      },
      boiler: {
        label: 'Boiler Repair',
        description: 'Expert boiler repair and central heating services',
        eta: '45-90 min',
        activities: ['Boiler repair/installation', 'Radiator maintenance', 'Central heating repair', 'Thermostat installation', 'Gas leak detection', 'Pipe repair', 'Underfloor heating'],
      },
      appliance: {
        label: 'Appliance Services',
        description: 'Repair and installation of household appliances',
        eta: '60-120 min',
        activities: ['Washing machine repair', 'Dishwasher repair', 'Refrigerator/freezer repair', 'Oven/stove repair', 'Dryer repair', 'Appliance installation'],
      },
      cleaning: {
        label: 'Cleaning & Maintenance',
        description: 'Deep cleaning, carpet cleaning, and maintenance services',
        eta: 'Same day',
        activities: ['Deep home cleaning', 'Carpet cleaning', 'Window cleaning', 'Window fitter', 'Gutter cleaning', 'Pressure washing (patio, driveway)', 'Chimney cleaning', 'Post-renovation cleaning'],
      },
      painting: {
        label: 'Painting & Decorating',
        description: 'Interior and exterior painting, wallpaper, and plastering',
        eta: '1-2 days',
        activities: ['Interior painting', 'Exterior painting', 'Wallpaper installation/removal', 'Paperhanger', 'Plastering and wall repair', 'Plasterer', 'Decorative finishes'],
      },
      flooring: {
        label: 'Flooring',
        description: 'Tile, hardwood, laminate, and carpet installation',
        eta: '1-3 days',
        activities: ['Tile installation/repair', 'Tiler', 'Hardwood floor installation/refinishing', 'Laminate flooring installation', 'Carpet installation', 'Flooring specialist'],
      },
      locksmith: {
        label: 'Locksmith',
        description: 'Lock replacement, emergency lockout, and security services',
        eta: '20-45 min',
        activities: ['Lock replacement', 'Emergency locksmith', 'Key cutting', 'Lock installation', 'Safe opening', 'Car lockout', 'Security upgrades', 'Master key systems'],
      },
      security: {
        label: 'Security',
        description: 'Security cameras, alarm systems, and smart locks',
        eta: 'Same day',
        activities: ['Lock replacement', 'Emergency locksmith', 'Security camera installation', 'Alarm system setup', 'Smart lock installation'],
      },
      outdoor: {
        label: 'Outdoor & Garden',
        description: 'Lawn care, tree trimming, fencing, and outdoor construction',
        eta: 'Same day',
        activities: ['Lawn care and mowing', 'Tree trimming/removal', 'Fence installation/repair', 'Patio/deck construction', 'Snow removal', 'Gardener', 'Sun screen installer', 'Facade specialist'],
      },
      pest_control: {
        label: 'Pest Control',
        description: 'Rodent, insect, termite, and bed bug extermination',
        eta: 'Same day',
        activities: ['Rodent extermination', 'Insect extermination', 'Termite treatment', 'Bed bug removal'],
      },
      renovation: {
        label: 'Renovation & Construction',
        description: 'Kitchen, bathroom remodeling, roofing, and more',
        eta: 'Varies',
        activities: ['Kitchen remodeling', 'Kitchen specialist', 'Bathroom remodeling', 'Basement finishing', 'Roof repair/replacement', 'Roofer', 'Drywall installation', 'Bricklayer', 'Carpenter', 'Tiler', 'Architect', 'Window fitter'],
      },
      other: {
        label: 'Other Services',
        description: 'Handyman services, furniture assembly, and specialized work',
        eta: 'Varies',
        activities: ['Handyman services', 'Handy help', 'Furniture assembly', 'Home energy audit', 'Solar panel installation', 'Water damage restoration', 'Mold remediation', 'Fire damage restoration', 'Quotation service'],
      },
      handyman: {
        label: 'Handyman',
        description: 'General repairs, installations, and odd jobs around your home',
        eta: 'Same day',
        activities: ['General repairs', 'Furniture assembly', 'Picture hanging', 'Shelf installation', 'Door repairs', 'Minor carpentry', 'Odd jobs'],
      },
      moving: {
        label: 'Moving Company',
        description: 'Professional moving, packing, and relocation services',
        eta: 'Scheduled',
        activities: ['Home moving', 'Office relocation', 'Packing services', 'Furniture transport', 'Piano moving', 'Storage solutions'],
      },
      smart_home: {
        label: 'Smart Home',
        description: 'Smart home installation, automation, and tech setup',
        eta: 'Same day',
        activities: ['Smart thermostat installation', 'Smart lighting setup', 'Home automation', 'WiFi optimization', 'Smart security setup', 'Voice assistant integration', 'Computer repair', 'Phone repair'],
      },
      sustainable: {
        label: 'Sustainable Home',
        description: 'Eco-friendly upgrades, insulation, and energy efficiency',
        eta: '1-3 days',
        activities: ['Solar panel installation', 'Insulation installation', 'Energy audit', 'Double glazing', 'Heat pump installation', 'Rainwater harvesting', 'Green roof installation'],
      },
    },
  },
  nl: {
    // Common
    common: {
      back: 'Terug',
      backToHome: 'Terug naar home',
      submit: 'Verzenden',
      submitting: 'Verzenden...',
      loading: 'Laden...',
      signIn: 'Inloggen',
      signUp: 'Registreren',
      signInSignUp: 'Inloggen / Registreren',
      returnHome: 'Terug naar Home',
      next: 'Volgende',
      cancel: 'Annuleren',
      save: 'Opslaan',
      edit: 'Bewerken',
      delete: 'Verwijderen',
      view: 'Bekijken',
      close: 'Sluiten',
      confirm: 'Bevestigen',
      search: 'Zoeken',
      filter: 'Filteren',
      all: 'Alle',
      learnMore: 'Meer Info',
      getStarted: 'Aan de Slag',
      bookNow: 'Nu Boeken',
      contactUs: 'Neem Contact Op',
      optional: 'optioneel',
    },
    // Reviews
    reviews: {
      rateService: 'Beoordeel deze service',
      howWasExperience: 'Hoe was je ervaring met',
      selectRating: 'Selecteer een beoordeling',
      ratingPoor: 'Slecht',
      ratingFair: 'Matig',
      ratingGood: 'Goed',
      ratingVeryGood: 'Zeer Goed',
      ratingExcellent: 'Uitstekend!',
      writeReview: 'Schrijf een review',
      shareExperience: 'Deel je ervaring met deze monteur...',
      submitReview: 'Review Verzenden',
      thankYou: 'Bedankt voor je feedback!',
      alreadyReviewed: 'Je hebt deze klus al beoordeeld',
    },
    // Navbar
    navbar: {
      home: 'Home',
      services: 'Diensten',
      howItWorks: 'Hoe Het Werkt',
      plans: 'Abonnementen',
      propertyManagers: 'Vastgoedbeheerders',
      becomeTechnician: 'Word Monteur',
      forTechnicians: 'Voor vakmensen',
      dashboard: 'Dashboard',
      signIn: 'Inloggen',
      signOut: 'Uitloggen',
    },
    // Hero
    hero: {
      badge: 'Gescreende vakmensen · Veilig betalen',
      title: 'Elke klus in huis, één vertrouwd platform',
      titleHighlight: 'voor klanten & vakmensen',
      subtitle: 'Vind een gescreende vakman voor elektra, sloten, verwarming, schoonmaak en meer — boek direct bij spoed, of vergelijk offertes voor geplande klussen. Vakmensen vinden lokale klussen en worden veilig betaald.',
      cta: 'Vind een vakman',
      secondaryCta: 'Werk als vakman',
      trust: 'Vertrouwd door heel Nederland',
      trustVerified: 'Geverifieerde vakmensen',
      trustEscrow: 'Betalingen beschermd via escrow',
      trustPricing: 'Transparante prijzen',
    },
    // For Technicians
    forTechnicians: {
      badge: 'Voor vakmensen',
      title: 'Laat je bedrijf groeien met Bricos',
      subtitle: 'Word gekoppeld aan lokale klanten, bepaal je eigen prijzen en word veilig betaald — geen leadkosten om achteraan te jagen.',
      benefit1: {
        title: 'Vaste lokale klussen',
        description: 'Ontvang klusaanbiedingen en offerteaanvragen van klanten bij jou in de buurt.',
      },
      benefit2: {
        title: 'Op tijd betaald',
        description: 'Betalingen staan in escrow en worden aan jou vrijgegeven zodra de klus klaar is.',
      },
      benefit3: {
        title: 'Bouw je reputatie op',
        description: 'Verdien beoordelingen en reviews die je meer werk opleveren.',
      },
      cta: 'Word vakman',
    },
    // Services
    services: {
      title: 'Diensten voor elk huis',
      subtitle: 'Van spoedklussen tot geplande projecten — elektra, sloten, verwarming, schoonmaak en meer.',
      electrician: {
        title: 'Elektricien',
        description: 'Elektrische reparaties, bedrading, meterkast en stroomuitval',
        eta: 'Snelle respons',
      },
      locksmith: {
        title: 'Slotenmaker',
        description: 'Slotreparaties, buitensluiting, sleutels en beveiligingsupgrades',
        eta: 'Snelle respons',
      },
      boiler: {
        title: 'CV-monteur',
        description: 'Verwarmingssystemen, ketelreparaties, onderhoud en CV-problemen',
        eta: 'Snelle respons',
      },
      from: 'Vanaf',
      warranty: 'Kwaliteit gegarandeerd',
      comingSoonTitle: 'Meer diensten binnenkort',
      comingSoonDesc: 'We breiden uit door heel Nederland — deze disciplines komen eraan.',
      comingSoonBadge: 'Binnenkort',
    },
    // How It Works
    howItWorks: {
      title: 'Hoe Het Werkt',
      subtitle: 'Vind de juiste vakman in een paar eenvoudige stappen',
      step1: {
        title: 'Vertel ons de klus',
        description: 'Beschrijf wat je nodig hebt — voor elk van onze dienstcategorieën.',
      },
      step2: {
        title: 'Word gekoppeld of vergelijk offertes',
        description: 'Boek direct een beschikbare vakman, of ontvang offertes en kies degene die je aanstaat.',
      },
      step3: {
        title: 'De klus wordt geklaard',
        description: 'Volg de voortgang en chat met je vakman tot de klus klaar is.',
      },
      step4: {
        title: 'Betaal veilig',
        description: 'Je betaling staat in escrow en wordt pas 3 dagen na afronding aan de vakman uitbetaald — en alleen als er geen geschil is geopend.',
      },
    },
    // Plans
    plans: {
      title: 'Abonnementen',
      subtitle: 'Voordelen voor vaste klanten',
      annual: {
        title: 'Jaarabonnement',
        price: '€149/jaar',
        features: [
          'Veiligheidscontrole met korting (€99 i.p.v. normale prijs)',
          '10% korting op voorrijkosten',
          'Voorrang bij boekingen',
          'Escrow-beschermde betalingen',
          '2x loyaliteitspunten op boekingen',
        ],
      },
      payAsYouGo: {
        title: 'Betaal Per Keer',
        price: 'Geen verplichting',
        features: [
          'Betaal per dienst',
          'Standaard responstijden',
          'Escrow-beschermde betalingen',
          'Spaar loyaliteitspunten',
        ],
      },
      cta: 'Aan de Slag',
    },
    // Property Managers
    propertyManagers: {
      title: 'Voor Vastgoedbeheerders',
      subtitle: 'Beheer al je panden op één plek',
      features: {
        multiProperty: 'Multi-pand dashboard',
        bulkPricing: 'Volumekortingen',
        sla: 'Prioriteit SLA respons',
        billing: 'Gecentraliseerde facturatie',
        reports: 'Gedetailleerde rapportages',
        maintenance: 'Onderhoudspaspoort per unit',
      },
      cta: 'Start Gratis Proefperiode',
    },
    // Technician Onboarding
    technicianOnboarding: {
      title: 'Word Monteur',
      subtitle: 'Sluit je aan bij ons netwerk van geverifieerde professionals',
      signInRequired: 'Inloggen vereist',
      signInMessage: 'Maak een account aan of log in om je aan te melden als monteur.',
      applicationSubmitted: 'Aanmelding Verzonden!',
      applicationMessage: 'Bedankt voor je aanmelding bij Bricos. Ons team beoordeelt je aanvraag en neemt binnen 2-3 werkdagen contact met je op.',
      step1: {
        title: 'Kies je specialisatie',
        subtitle: 'Selecteer het type dienst waarin je gespecialiseerd bent',
      },
      step2: {
        title: 'Jouw Gegevens',
        subtitle: 'Vertel ons over jezelf en je ervaring',
      },
      form: {
        fullName: 'Volledige Naam',
        fullNamePlaceholder: 'Jan Jansen',
        phone: 'Telefoonnummer',
        phonePlaceholder: '+31 6 12345678',
        companyName: 'Bedrijfsnaam',
        companyNamePlaceholder: 'Jouw Bedrijf BV',
        kvkNumber: 'KVK-nummer',
        kvkNumberPlaceholder: '12345678',
        vatNumber: 'BTW-nummer',
        vatNumberPlaceholder: 'NL123456789B01',
        experience: 'Ervaring & Kwalificaties',
        experiencePlaceholder: 'Beschrijf je ervaring, certificeringen en relevante kwalificaties...',
        submitApplication: 'Aanmelding Verzenden',
      },
      benefits: {
        title: 'Waarom Bricos?',
        fairPay: 'Eerlijke betaling',
        fairPayDesc: 'Geen leadkosten – zie je uitbetaling voordat je accepteert',
        fastPayouts: 'Snelle uitbetalingen',
        fastPayoutsDesc: 'Wekelijkse uitbetalingen, volgende dag voor toppresteerders',
        protection: 'Bescherming',
        protectionDesc: 'Geschillenoplossing en klantcommunicatie geregeld',
        flexibility: 'Flexibiliteit',
        flexibilityDesc: 'Bepaal je eigen beschikbaarheid en werkgebied',
      },
      errors: {
        selectService: 'Selecteer een dienst',
        applicationExists: 'Aanmelding bestaat al',
        applicationExistsMessage: 'Je hebt al een aanmelding ingediend. Wacht op beoordeling.',
        submitError: 'Fout',
        submitErrorMessage: 'Aanmelding verzenden mislukt. Probeer opnieuw.',
        nameMin: 'Naam moet minimaal 2 tekens bevatten',
        phoneMin: 'Voer een geldig telefoonnummer in',
        experienceMin: 'Beschrijf je ervaring (minimaal 20 tekens)',
        companyRequired: 'Bedrijfsnaam is verplicht',
        kvkRequired: 'KVK-nummer is verplicht',
        kvkFormat: 'KVK-nummer moet 8 cijfers bevatten',
      },
    },
    // Onboarding
    onboarding: {
      welcome: 'Welkom bij Bricos!',
      welcomeDesc: 'Het platform voor elke klus in huis. Boek gescreende vakmensen voor elektra, loodgieter, slotenmaker en meer — boek direct of vergelijk offertes, alles met vaste prijzen en betalingen beschermd via escrow.',
      howItWorks: 'Snel & transparant',
      howItWorksDesc: 'Kies een dienst, beschrijf je probleem en word binnen enkele minuten gekoppeld aan een geverifieerde monteur. Volg hun aankomst realtime op de kaart.',
      safePayments: 'Veilige escrow-betalingen',
      safePaymentsDesc: 'Je betaling wordt veilig vastgehouden in escrow totdat de klus naar tevredenheid is afgerond. Geen verborgen kosten, geen verrassingen.',
      skip: 'Overslaan',
      next: 'Volgende',
      bookFirst: 'Boek je eerste reparatie',
    },
    // Footer
    footer: {
      description: 'De vertrouwde marktplaats die klanten verbindt met gescreende vakmensen door heel Nederland — voor elke klus in huis, met spoed of gepland.',
      services: 'Diensten',
      moreServices: 'Meer Diensten',
      company: 'Bedrijf',
      legal: 'Juridisch',
      termsOfService: 'Algemene Voorwaarden',
      privacyPolicy: 'Privacybeleid',
      helpCenter: 'Helpcentrum',
      contact: 'Contact',
      phone: '',
      email: 'support@bricos.nl',
      contactComingSoon: 'Telefoon- & e-mailondersteuning binnenkort beschikbaar',
      location: 'Nederland',
      rights: 'Alle rechten voorbehouden.',
    },
    // Auth
    auth: {
      signIn: 'Inloggen',
      signUp: 'Registreren',
      signInShort: 'Inloggen',
      signUpShort: 'Registreren',
      createAccount: 'Account Aanmaken',
      creatingAccount: 'Account aanmaken...',
      signingIn: 'Inloggen...',
      welcomeBack: 'Welkom terug',
      createYourAccount: 'Maak je account aan',
      signInDesc: 'Log in om je dashboard en boekingen te bekijken',
      signUpDesc: 'Registreer om gescreende vakmensen te boeken en je klussen te volgen',
      fullName: 'Volledige Naam',
      fullNamePlaceholder: 'Jan Jansen',
      email: 'E-mail',
      emailPlaceholder: 'jij@voorbeeld.com',
      password: 'Wachtwoord',
      confirmPassword: 'Bevestig Wachtwoord',
      forgotPassword: 'Wachtwoord vergeten?',
      noAccount: 'Nog geen account?',
      hasAccount: 'Heb je al een account?',
      resetPassword: 'Wachtwoord Resetten',
      newPassword: 'Nieuw Wachtwoord',
      iAmA: 'Ik ben een...',
      homeowner: 'Huiseigenaar / Gebruiker',
      homeownerDesc: 'Boek gescreende vakmensen voor je woning',
      technician: 'Monteur',
      technicianDesc: 'Word dienstverlener',
      propertyManager: 'Vastgoedbeheerder',
      propertyManagerDesc: 'Beheer meerdere panden',
      emergencyServices: 'Gescreende vakmensen, wanneer je ze nodig hebt',
      authDescription: 'Elektriciens, slotenmakers en CV-monteurs aan je deur. Vaste prijzen, escrow-betalingen en kwaliteitsgarantie op reparaties.',
      avgArrival: 'Gem. aankomst',
      rating: 'Beoordeling',
      jobsDone: 'Klussen gedaan',
    },
    // Dashboard
    dashboard: {
      welcome: 'Welkom terug',
      manageBookings: 'Beheer je boekingen en volg reparaties',
      newBooking: 'Nieuwe Boeking',
      loyaltyPoints: 'Loyaliteitspunten',
      tier: 'niveau',
      ptsTo: 'punten naar',
      activeJobs: 'Actieve Klussen',
      trackLatestJob: 'Volg laatste klus',
      completedJobs: 'Voltooide Klussen',
      viewHistory: 'Bekijk geschiedenis',
      activeBookings: 'Actieve Boekingen',
      recentRepairs: 'Recente Reparaties',
      viewAll: 'Bekijk alles',
      report: 'Rapport',
      noBookingsYet: 'Nog geen boekingen',
      bookFirstService: 'Boek je eerste klus en word gekoppeld aan een gescreende vakman.',
      recentActivity: 'Recente Activiteit',
      noBookings: 'Geen actieve boekingen',
      bookService: 'Boek een Dienst',
      customerDashboard: 'Klant Dashboard',
      openDisputes: 'Open Geschillen',
      viewDisputes: 'Bekijk Geschillen',
    },
    // Dashboard Header
    dashboardHeader: {
      admin: 'Admin',
      adminPanel: 'Admin Paneel',
      notifications: 'Meldingen',
      noNewNotifications: 'Geen nieuwe meldingen',
      profile: 'Profiel',
      settings: 'Instellingen',
      bookingHistory: 'Boekingsgeschiedenis',
      servicePlans: 'Serviceabonnementen',
      helpSupport: 'Hulp & Ondersteuning',
      signOut: 'Uitloggen',
    },
    // Dashboard Footer
    dashboardFooter: {
      helpCenter: 'Helpcentrum',
      contact: 'Contact',
      support247: 'Ondersteuning',
      privacy: 'Privacy',
      terms: 'Voorwaarden',
    },
    // Tracking
    tracking: {
      trackTechnician: 'Volg Je Monteur',
      booking: 'Boeking',
      paymentConfirmed: 'Betaling Bevestigd!',
      paymentFailed: 'De betaling is niet voltooid. Probeer opnieuw te boeken.',
      paymentPending: 'We konden je betaling nog niet bevestigen — als je hebt betaald, wordt dit zo bijgewerkt.',
      paymentEscrowMsg: 'Je betaling wordt veilig bewaard in escrow. Een monteur wordt spoedig toegewezen.',
      estimatedArrival: 'Geschatte Aankomst',
      minutes: 'minuten',
      jobProgress: 'Voortgang Klus',
      bookingConfirmed: 'Boeking Bevestigd',
      bookingConfirmedDesc: 'Je boeking is bevestigd',
      technicianAssigned: 'Monteur Toegewezen',
      technicianAssignedDesc: 'Een monteur is toegewezen aan je klus',
      onTheWay: 'Onderweg',
      onTheWayDesc: 'Je monteur is onderweg',
      jobCompleted: 'Klus Voltooid',
      jobCompletedDesc: 'De klus is succesvol afgerond',
      yourTechnician: 'Jouw Monteur',
      specialist: 'Specialist',
      verified: 'Geverifieerd',
      jobsCompleted: 'klussen voltooid',
      call: 'Bellen',
      message: 'Bericht',
      jobDetails: 'Klusdetails',
      location: 'Locatie',
      serviceType: 'Type Dienst',
      emergency: 'Spoed',
      standard: 'Standaard',
      fixedPrice: 'Vaste Prijs',
      bookingNotFound: 'Boeking niet gevonden',
      backToDashboard: 'Terug naar Dashboard',
      technicianArrived: 'Monteur Is Gearriveerd',
      technicianArrivedDesc: 'is begonnen aan je klus. Bevestig de aankomst om de timer te starten.',
      confirmArrival: 'Bevestig Aankomst Monteur',
      arrivalConfirmed: 'Aankomst bevestigd! De klustimer loopt nu.',
      arrivalConfirmFailed: 'Aankomst bevestigen mislukt',
      confirmedAt: 'Bevestigd om',
      approvedExtraWork: 'Goedgekeurd Extra Werk',
      total: 'Totaal',
      enRoute: 'Onderweg',
      navigateToJob: 'Navigeer naar de kluslocatie',
      iHaveArrived: 'Ik Ben Gearriveerd',
      travelTime: 'Reistijd',
      pricing: 'Prijzen',
      callOutFee: 'Voorrijkosten',
      hourlyRate: 'Per uur',
      estimated: 'geschat',
      jobDetailsExpand: 'Klusdetails',
      kmAway: 'km afstand',
      customer: 'Klant',
    },
    // History
    history: {
      title: 'Onderhoudsgeschiedenis',
      completedJobs: 'voltooide klussen',
      noCompletedJobs: 'Nog geen voltooide klussen',
      bookFirstService: 'Boek Je Eerste Dienst',
      report: 'Rapport',
      invoice: 'Factuur',
      rate: 'Beoordelen',
    },
    // Settings
    settings: {
      title: 'Instellingen',
      backToDashboard: 'Terug naar dashboard',
      profile: 'Profiel',
      email: 'E-mail',
      emailCannotChange: 'E-mail kan niet worden gewijzigd',
      fullName: 'Volledige Naam',
      fullNamePlaceholder: 'Jan Jansen',
      phoneNumber: 'Telefoonnummer',
      phonePlaceholder: '+31 6 12345678',
      language: 'Taal',
      saveChanges: 'Wijzigingen Opslaan',
      saving: 'Opslaan...',
      profileUpdated: 'Profiel bijgewerkt',
      changesSaved: 'Je wijzigingen zijn opgeslagen.',
      error: 'Fout',
      updateError: 'Profiel bijwerken mislukt. Probeer opnieuw.',
      security: 'Beveiliging',
      changePassword: 'Wachtwoord Wijzigen',
      changePasswordDesc: 'Werk je wachtwoord bij om je account veilig te houden.',
      resetPassword: 'Wachtwoord Resetten',
      account: 'Account',
      signOut: 'Uitloggen',
      deleteAccount: 'Account Verwijderen',
      deleteAccountTitle: 'Je account verwijderen?',
      deleteAccountDesc: 'Deze actie kan niet ongedaan worden gemaakt. Dit verwijdert permanent je account en alle gegevens van onze servers.',
      cancel: 'Annuleren',
      accountDeletion: 'Account verwijdering',
      contactSupport: 'Neem contact op met support om je account te verwijderen.',
    },
    // Language
    language: {
      select: 'Taal',
      en: 'English',
      nl: 'Nederlands',
      fr: 'Français',
    },
    // Pricing Transparency
    pricing: {
      badge: 'Transparante Prijzen',
      title: 'Weet Precies Wat Je Betaalt',
      subtitle: 'Geen verborgen kosten. Alle kosten vooraf duidelijk.',
      fixedPrice: {
        title: 'Vaste Prijs',
        description: 'Prijs staat vast voordat de klus begint. Je weet het exacte bedrag vooraf. Ideaal voor standaard reparaties en installaties.',
        services: 'Slotenmakers, Elektriciens, Loodgieters',
      },
      hourlyRate: {
        title: 'Uurtarief',
        description: 'Betaal voorrijkosten + uurtarief voor daadwerkelijk gewerkte uren. Ideaal voor klussen met variabele duur.',
        services: 'Schoonmaak, Schilderwerk, Renovaties',
      },
      examples: {
        fixedJob: {
          type: 'Vaste Prijs Klus',
          service: 'Slot Vervangen',
          note: 'Prijs vooraf afgesproken. Geen verrassingen.',
        },
        fixedUrgent: {
          type: 'Vast + Spoed',
          service: 'Nood Buitensluiting',
          note: 'Zelfde vaste prijs + spoedtoeslag voor prioriteitsdispatch.',
        },
        hourlyJob: {
          type: 'Uurtarief Klus',
          service: 'Huishoudelijke Schoonmaak',
          note: 'Betaal alleen voor daadwerkelijk gewerkte uren.',
        },
        hourlyUrgent: {
          type: 'Uurtarief + Spoed',
          service: 'Spoed Schoonmaak',
          note: 'Spoed voegt €25/uur toe voor directe beschikbaarheid.',
        },
      },
      labels: {
        servicePrice: 'Serviceprijs',
        serviceFee: 'Servicekosten',
        emergencySurcharge: 'Spoedtoeslag',
        callOutFee: 'Voorrijkosten',
        hourlyRate: 'Uurtarief',
        total: 'Totaal',
        urgent: 'Spoed',
        hours: 'uur',
        perHour: 'per uur',
        urgentPerHour: 'spoed',
      },
      trust: {
        title: 'Je Geld is Beschermd',
        subtitle: 'Betaling in escrow tot je bevestigt dat de klus goed is gedaan.',
        warranty: 'Kwaliteit gegarandeerd',
        verified: 'Geverifieerde monteurs',
        support: 'Klantenservice',
      },
    },
    // Satisfaction Guarantee
    satisfaction: {
      badge: 'Tevredenheidsgarantie',
      title: 'Jouw Vertrouwen, Onze Prioriteit',
      subtitle: 'Wij staan achter elke klus met uitgebreide bescherming',
      moneyBack: 'Escrow Bescherming',
      moneyBackDesc: 'Je betaling wordt veilig bewaard en pas vrijgegeven als je tevreden bent. Geschillen worden afgehandeld via ons gestructureerd oplossingsproces.',
      warranty: 'Kwaliteitsfocus',
      warrantyDesc: 'Problemen na een klus? Ons geschillenproces helpt een eerlijke uitkomst te vinden voor iedereen.',
      escrow: 'Veilige Escrow',
      escrowDesc: 'Je geld staat in escrow en wordt pas 3 dagen na afronding aan de vakman uitbetaald, als er geen geschil openstaat.',
      verified: 'Professionele Monteurs',
      verifiedDesc: 'Monteurs registreren zich met hun KvK-nummer, certificeringen en verzekeringsgegevens.',
    },
    // Booking
    booking: {
      step1Title: 'Kies Dienst',
      step2Title: 'Details',
      step3Title: 'Bevestigen',
      whatDoYouNeed: 'Wat heb je nodig?',
      searchOrBrowse: 'Zoek of blader door onze diensten',
      searchPlaceholder: 'Zoek diensten... (bijv. lek, slot, ketel)',
      popularServices: 'Populaire diensten',
      noServicesFound: 'Geen diensten gevonden voor',
      clearSearch: 'Zoekopdracht wissen',
      emergency: 'Spoed',
      emergencyArrival: 'Prioriteit verzending',
      scheduled: 'Gepland',
      scheduledSave: 'Flexibele planning',
      selectSpecificService: 'Selecteer een specifieke dienst:',
      continue: 'Doorgaan',
      tellUsMore: 'Vertel ons meer',
      provideDetails: 'Geef details over je probleem en locatie',
      preferredDate: 'Voorkeursdatum',
      describeProblem: 'Beschrijf het probleem',
      problemPlaceholder: 'Bijv. Stroom uitgevallen in de keuken, geen lichten werken...',
      streetAddress: 'Straatnaam',
      addressPlaceholder: 'Begin met typen van je adres...',
      city: 'Stad',
      cityPlaceholder: 'Amsterdam',
      postalCode: 'Postcode',
      postalCodePlaceholder: '1000 AA',
      reviewBooking: 'Boeking Bekijken',
      confirmBooking: 'Bevestig je boeking',
      reviewDetails: 'Bekijk de details en voltooi de betaling',
      location: 'Locatie',
      estimatedArrival: 'Geschatte aankomst',
      scheduledFor: 'Gepland voor',
      serviceType: 'Type dienst',
      pricingModel: 'Prijsmodel',
      hourlyRateLabel: 'Uurtarief',
      fixedPriceLabel: 'Vaste prijs',
      callOutFee: 'Voorrijkosten',
      hourlyRate: 'Uurtarief',
      estimatedHours: 'Geschatte uren',
      hourlySubtotal: 'Subtotaal uren',
      serviceFee: 'Servicekosten',
      emergencySurcharge: 'Spoedtoeslag',
      included: 'inbegrepen',
      hourlyNote: 'Eindprijs wordt berekend op basis van daadwerkelijk gewerkte uren. Je moet extra tijd goedkeuren voordat het werk doorgaat.',
      servicePrice: 'Serviceprijs',
      estimatedTotal: 'Geschat Totaal',
      total: 'Totaal',
      finalPriceNote: 'Eindprijs gebaseerd op daadwerkelijke uren',
      escrowProtection: 'Escrow Betalingsbescherming',
      escrowDescription: 'Je betaling wordt veilig in escrow bewaard en pas 3 dagen na afronding van de klus aan de vakman uitbetaald, mits er geen geschil openstaat — zo ben je beschermd tot je tevreden bent.',
      redirectingPayment: 'Doorsturen naar betaling...',
      payWithIdeal: 'Betaal met iDEAL / Kaart',
      signInToBook: 'Log in om te boeken',
      signInRequired: 'Je moet ingelogd zijn om een dienst te boeken.',
      estimatedDuration: 'Geschatte Duur',
      estimatedDurationDesc: 'Deze dienst wordt per uur gefactureerd. Hoe lang schat je dat de klus duurt?',
      estimatedHoursLabel: 'Geschatte uren',
      hours: 'uur',
      technicianConfirm: 'De monteur bevestigt de schatting bij aankomst. Je moet extra tijd goedkeuren.',
    },
    // Service Landing
    serviceLanding: {
      professional: 'Professionele',
      services: 'Diensten',
      availableDesc: 'Boek gescreende professionals, doorgaans ter plaatse binnen',
      eta: 'Aankomsttijd',
      warranty: 'Kwaliteit Gegarandeerd',
      rating: 'Beoordeling',
      book: 'Boek',
      callNow: 'Bel Nu',
      startingPrice: 'startprijs',
      perHour: 'per uur',
      callOutFee: 'voorrijkosten',
      serviceFee: 'servicekosten',
      features: {
        fixedPricing: 'Vaste transparante prijzen',
        noHiddenFees: 'Geen verborgen kosten',
        verifiedTechnicians: 'Geverifieerde & verzekerde monteurs',
        warrantyIncluded: 'Kwaliteit gegarandeerd',
        payAfterCompletion: 'Betaal na voltooiing',
        emergencySurcharge: 'Spoedtoeslag',
      },
      ourServices: 'Onze',
      comprehensive: 'Uitgebreide',
      servicesFor: 'diensten voor al je behoeften',
      whyChoose: 'Waarom Kiezen voor Bricos voor',
      fastResponse: 'Snelle Respons',
      fastResponseDesc: 'Onze monteurs worden snel verzonden',
      evenForEmergencies: 'zelfs voor noodgevallen',
      verifiedExperts: 'Geverifieerde Experts',
      verifiedExpertsDesc: 'Alle monteurs zijn gescreend, gecertificeerd en volledig verzekerd.',
      trustedByThousands: 'Eerlijke prijzen, gescreende vakmensen',
      trustedByThousandsDesc: 'Boek met vertrouwen — van begin tot eind beschermd via escrow.',
      otherServices: 'Andere Diensten Die Je Misschien Nodig Hebt',
      moreServices: 'Meer Diensten',
    },
    // Property Manager Portal
    pm: {
      portal: 'Vastgoedbeheerder Portaal',
      dashboard: 'Dashboard',
      properties: 'Panden',
      contractors: 'Aannemers',
      jobs: 'Klussen',
      bookService: 'Dienst Boeken',
      disputes: 'Geschillen',
      reports: 'Rapporten',
      billing: 'Facturatie',
      team: 'Team',
      settings: 'Instellingen',
      signOut: 'Uitloggen',
      organizations: 'Organisaties',
      organizationSettings: 'Organisatie Instellingen',
      addProperty: 'Pand Toevoegen',
      bulkBook: 'Bulk Boeken',
      propertyName: 'Pandnaam',
      address: 'Adres',
      city: 'Stad',
      postalCode: 'Postcode',
      numberOfUnits: 'Aantal Units',
      adding: 'Toevoegen...',
      totalUnits: 'Totaal Units',
      activeJobs: 'Actieve Klussen',
      completed: 'Voltooid',
      totalSpent: 'Totaal Besteed',
      avgJobCost: 'Gem. Kluskosten',
      monthlySpending: 'Maandelijkse Uitgaven',
      last6Months: 'Laatste 6 maanden',
      jobsByProperty: 'Klussen per Pand',
      recentJobs: 'Recente Klussen',
      noRecentJobs: 'Geen recente klussen',
      viewAllJobs: 'Bekijk Alle Klussen',
      yourProperties: 'Jouw Panden',
      noProperties: 'Nog geen panden',
      addFirstProperty: 'Voeg je eerste pand toe',
      units: 'units',
      manageUnits: 'Beheer Units',
      // Contractor management
      allContractors: 'Alle Aannemers',
      external: 'Extern',
      platformLinked: 'Platform Gekoppeld',
      pending: 'In Afwachting',
      withContract: 'Met Contract',
      searchContractors: 'Zoek aannemers...',
      addContractor: 'Aannemer Toevoegen',
      inviteSent: 'Uitnodiging Verzonden',
      accepted: 'Geaccepteerd',
      active: 'Actief',
      suspended: 'Opgeschort',
      sendInvite: 'Uitnodiging Versturen',
      resendInvite: 'Opnieuw Uitnodigen',
      uploadContract: 'Contract Uploaden',
      managePricing: 'Prijzen Beheren',
      manageCoverage: 'Dekking Beheren',
      viewContract: 'Contract Bekijken',
      suspend: 'Opschorten',
      reactivate: 'Heractiveren',
      noContractors: 'Geen aannemers',
      addFirstContractor: 'Voeg je eerste aannemer toe',
      // Roles
      owner: 'Eigenaar',
      admin: 'Admin',
      dispatcher: 'Dispatcher',
      finance: 'Financiën',
      readonly: 'Alleen Lezen',
      manageDescription: 'Beheer je panden en onderhoud',
      addNewProperty: 'Nieuw Pand Toevoegen',
      noJobsYet: 'Nog geen klussen',
      jobReports: 'Klusrapporten',
      viewAllReports: 'Bekijk alle rapporten',
      report: 'Rapport',
      // Billing page
      financeInvoicing: 'Financiën & Facturatie',
      billingInvoices: 'Facturatie & Facturen',
      billingSubtitle: 'Beheer facturen en boekhoudkoppeling',
      totalInvoices: 'Totaal Facturen',
      paid: 'Betaald',
      overdue: 'Achterstallig',
      monthlyInvoicesRevenue: 'Maandelijkse Facturen & Omzet',
      invoiceStatus: 'Factuurstatus',
      invoicesByProperty: 'Facturen per Pand',
      noInvoiceData: 'Nog geen factuurgegevens',
      noStatusData: 'Nog geen statusgegevens',
      allInvoices: 'Alle Facturen',
      connections: 'Koppelingen',
      allCaughtUp: 'Alles bijgewerkt!',
      noPendingInvoices: 'Geen openstaande facturen',
      noPaidInvoices: 'Nog geen betaalde facturen',
      connectedProperties: 'Gekoppelde Panden',
      connectNew: 'Nieuwe Koppeling',
      loadingConnections: 'Koppelingen laden...',
      noAccountingConnections: 'Geen boekhoudkoppelingen',
      connectAccountingDesc: 'Koppel je panden aan boekhoudsoftware om facturen automatisch te synchroniseren',
      connectAccountingSoftware: 'Boekhoudsoftware Koppelen',
      connected: 'Gekoppeld',
      lastSync: 'Laatste sync',
      connect: 'Koppelen',
      billingPreference: 'Facturatievoorkeuren',
      monthlyConsolidated: 'Maandelijks Gecombineerd',
      monthlyConsolidatedDesc: 'Eén factuur voor alle klussen aan het einde van de maand',
      perJob: 'Per Klus',
      perJobDesc: 'Factuur voor elke voltooide klus',
      perBuilding: 'Per Gebouw',
      perBuildingDesc: 'Aparte factuur per pand',
      accountingIntegration: 'Boekhoudkoppeling',
      connectAccountingAutoSync: 'Koppel je boekhoudsoftware om facturen automatisch te synchroniseren',
      supported: 'Ondersteund',
      configureIntegration: 'Koppeling Instellen',
      export: 'Exporteren',
      exportDesc: 'Exporteer je facturen naar verschillende formaten voor boekhouding en administratie.',
      exportCSV: 'Exporteer CSV',
      exportExcel: 'Exporteer Excel',
      mt940Format: 'MT940 Formaat',
      mt940Desc: 'MT940 formaat is compatibel met de meeste Nederlandse bankafstemmingssystemen.',
      selectProperty: 'Selecteer Pand',
      chooseProperty: 'Kies een pand',
      accountingSystem: 'Boekhoudsysteem',
      chooseAccountingSoftware: 'Kies boekhoudsoftware',
      comingSoon: 'Binnenkort',
      afasConfiguration: 'AFAS Configuratie',
      afasTokenDesc: 'AFAS gebruikt token-gebaseerde authenticatie. Voer hieronder je API-token en omgevingssleutel in.',
      apiToken: 'API Token',
      enterAfasToken: 'Voer je AFAS API-token in',
      environmentKey: 'Omgevingssleutel',
      afasEnvironmentDesc: 'Je AFAS-omgevingsidentificatie (te vinden in je AFAS-portaal)',
      oauthConnectDesc: 'Klikken op "Koppelen" opent een popup om te autoriseren',
      forProperty: 'voor',
      connecting: 'Koppelen...',
      cancel: 'Annuleren',
      disconnectAccounting: 'Boekhoudkoppeling Verbreken',
      disconnectConfirm: 'Weet je zeker dat je deze boekhoudkoppeling wilt verbreken? Toekomstige facturen worden niet meer automatisch gesynchroniseerd.',
      disconnect: 'Verbreken',
      disconnecting: 'Verbreken...',
      syncInvoices: 'Synchroniseren',
      generateInvoices: 'Genereren',
      vsLastMonth: 'vs vorige maand',
      noInvoicesToExport: 'Geen facturen om te exporteren',
      createInvoicesFirst: 'Maak eerst facturen aan voordat je exporteert.',
      exportComplete: 'Export voltooid',
      exportFailed: 'Export mislukt',
      noPaidForMT940: 'Geen betaalde facturen',
      mt940RequiresPaid: 'MT940 export vereist minstens één betaalde factuur.',
    },
    // Technician Portal
    tech: {
      portal: 'Monteur Portaal',
      overview: 'Overzicht',
      activeJobs: 'Actieve Klussen',
      calendar: 'Kalender',
      earnings: 'Verdiensten',
      reviews: 'Reviews',
      settings: 'Instellingen',
      payoutHistory: 'Uitbetalingsgeschiedenis',
      signOut: 'Uitloggen',
      online: 'Online',
      offline: 'Offline',
      available: 'Beschikbaar',
      noTechnicianProfile: 'Geen Monteur Profiel',
      noProfileMessage: 'Je hebt nog geen actief monteur profiel.',
      goHome: 'Ga naar Home',
      applyAsTechnician: 'Solliciteren als Monteur',
      hello: 'Hallo',
      youreOnline: 'Je bent online en klaar voor klussen',
      youreOffline: 'Je bent momenteel offline',
      newJobs: 'nieuwe klussen',
      incomingJobs: 'Inkomende Klussen',
      pending: 'In Afwachting',
      totalEarned: 'Totaal Verdiend',
      completed: 'Voltooid',
      thisMonth: 'deze maand',
      rating: 'Beoordeling',
      weeklyEarnings: 'Wekelijkse Verdiensten',
      last7Days: 'Laatste 7 dagen',
      jobsByService: 'Klussen per Dienst',
      activeJobsTitle: 'Actieve Klussen',
      noActiveJobs: 'Geen actieve klussen',
      waitingForJobs: 'Wachten op nieuwe klusaanbiedingen',
      recentReviews: 'Recente Reviews',
      noReviews: 'Nog geen reviews',
      allReviews: 'Alle Reviews',
      showLess: 'Minder Tonen',
      startJob: 'Start Klus',
      completeJob: 'Klus Voltooien',
      openChat: 'Chat Openen',
      downloadReport: 'Rapport Downloaden',
      requestPause: 'Pauze Aanvragen',
      cancelPause: 'Pauze Annuleren',
      pauseRequested: 'Pauze Aangevraagd',
      paused: 'Gepauzeerd',
      waitingForJobsDesc: 'Je wordt bericht wanneer een nieuwe klus in de buurt beschikbaar is',
      jobHistory: 'Klusgeschiedenis',
      arrivedStartJob: 'Ik ben er - Start Klus',
      pausePendingApproval: 'Pauzeverzoek wacht op goedkeuring klant',
      clientConfirmed: 'Klant bevestigd',
      awaitingClientConfirmation: 'Wachten op bevestiging klant',
      completedJobs: 'Voltooide Klussen',
      viewAll: 'Bekijk alles',
      goOnlineForJobs: 'Ga online om klusaanbiedingen te ontvangen',
      disputeStatusPending: "In afwachting",
      disputeStatusUnderReview: "In behandeling",
      disputeStatusAwaitingTechnician: "Wacht op jouw reactie",
      disputeStatusAwaitingCustomer: "Wacht op klant",
      disputeStatusResolved: "Opgelost",
      disputeStatusEscalated: "Geëscaleerd",
      disputeStatusDismissed: "Afgewezen",
      notATechnician: "Geen monteur",
      needTechProfileDisputes: "Je hebt een monteursprofiel nodig om geschillen te bekijken.",
      backToDashboard: "Terug naar dashboard",
      myDisputes: "Mijn geschillen",
      activeLower: "actief",
      resolvedLower: "opgelost",
      noDisputes: "Geen geschillen",
      noDisputesMessage: "Goed werk! Geen enkele klant heeft een geschil ingediend.",
      activeDisputes: "Actieve geschillen",
      responseRequired: "Reactie vereist",
      jobDetails: "Klusdetails",
      customersComplaint: "Klacht van klant",
      customerEvidence: "Bewijs van klant",
      resolution: "Uitkomst",
      filed: "Ingediend",
      outcome: "Uitkomst",
      payoutStatusPending: "In afwachting",
      payoutStatusProcessing: "In verwerking",
      payoutStatusCompleted: "Voltooid",
      payoutStatusFailed: "Mislukt",
      projectStatusNotified: "Nieuw",
      projectStatusAccepted: "Geaccepteerd",
      projectStatusDeclined: "Afgewezen",
      projectStatusFilled: "Vervuld",
      projectStatusExpired: "Verlopen",
      projectStart: "Start",
      projectBudget: "Budget",
      photos: "foto's",
      acceptAndSubmitQuote: "Accepteer & stuur offerte",
      pass: "Overslaan",
      submitQuote: "Offerte indienen",
      dashboard: "Dashboard",
      projectOpportunities: "Projectkansen",
      projectOpportunitiesSubtitle: "Renovatie- en bouwprojecten die bij jouw vaardigheden passen",
      tabNew: "Nieuw",
      tabAccepted: "Geaccepteerd",
      tabPast: "Eerder",
      noNewProjects: "Momenteel geen nieuwe projectkansen. Kom snel terug!",
      noAcceptedProjects: "Nog geen geaccepteerde projecten.",
      noPastProjects: "Geen eerdere kansen.",
      projectAcceptedToast: "Project geaccepteerd! Je kunt nu je offerte indienen.",
      projectAcceptFailedToast: "Project accepteren mislukt.",
      projectDeclinedToast: "Project afgewezen.",
      projectDeclineFailedToast: "Project afwijzen mislukt.",
      loading: "Laden...",
    },

    projects: {
      statusDraft: "Concept",
      statusMatching: "Matchen",
      statusAwaitingQuotes: "Wacht op offertes",
      statusQuoteAccepted: "Offerte geaccepteerd",
      statusActive: "Actief",
      statusInProgress: "Bezig",
      statusCompleted: "Voltooid",
      statusCancelled: "Geannuleerd",
      statusDisputed: "Geschil",
      dashboard: "Dashboard",
      title: "Mijn projecten",
      subtitle: "Beheer je renovatie- en bouwprojecten",
      newProject: "Nieuw project",
      emptyTitle: "Nog geen projecten",
      emptyDescription: "Plaats je eerste renovatie- of bouwproject en word gekoppeld aan 3 gescreende professionals.",
      postAProject: "Plaats een project",
      notFound: "Project niet gevonden.",
      backToProjects: "Terug naar projecten",
      posted: "Geplaatst",
      matchedTechnicians: "Je 3 gematchte vakmensen",
      waitingForTechnician: "Wachten op vakman…",
      slot: "Plek",
      of: "van",
      technician: "Vakman",
      jobs: "klussen",
      quoteSubmitted: "Offerte ingediend",
      quotePending: "Offerte in afwachting",
      daysLeftToSubmit: "dagen om in te dienen",
      waitingForQuotes: "Wachten op offertes…",
      compareQuotes: "Offertes vergelijken",
      received: "ontvangen",
      tabOverview: "Overzicht",
      tabMessages: "Berichten",
      tabDocuments: "Documenten",
      projectProgress: "Projectvoortgang",
      milestoneDone: "Klaar",
      milestoneUpcoming: "Aankomend",
      projectDetails: "Projectdetails",
      location: "Locatie",
      startDate: "Startdatum",
      duration: "Duur",
      budget: "Budget",
      noMessages: "Nog geen berichten. Begin een gesprek!",
      typeMessage: "Typ een bericht…",
      documentsEmpty: "Documenten verschijnen hier zodra het project vordert.",
      thisProjectIs: "Dit project is",
      acceptQuote: "Offerte accepteren",
      hiringPrefix: "Je huurt",
      hiringFor: "voor",
      hiringSuffix: "— dit brengt hen direct op de hoogte en maakt je project aan. Klaar om door te gaan?",
      cancel: "Annuleren",
      confirmAndHire: "Bevestigen & inhuren",
      quoteAcceptedToast: "Offerte geaccepteerd! Je project is nu actief.",
      quoteAcceptFailedToast: "Offerte accepteren mislukt.",
      messageSentToast: "Bericht verzonden",
      messageSendFailedToast: "Bericht verzenden mislukt",
      stepBasics: "Projectbasis",
      stepLocation: "Locatie & planning",
      stepBudget: "Budget & controle",
      back: "Terug",
      next: "Volgende",
      newProjectSubtitle: "Vind gescreende professionals voor je renovatie- of bouwproject",
      matchingTitle: "Je project matchen…",
      matchingSubtitle: "De beste vakmensen op het platform voor je project zoeken",
      findTechnicians: "Vind mijn 3 vakmensen",
      fieldTitle: "Projecttitel",
      fieldCategory: "Categorie",
      fieldDescription: "Omschrijving",
      charactersMinimum: "tekens minimaal",
      fieldPhotos: "Foto's (optioneel, max 8)",
      photosHint: "Help vakmensen de omvang te begrijpen",
      uploading: "Uploaden...",
      dropPhotos: "Zet foto's hier neer",
      dragDropPhotos: "Sleep hierheen of klik om te uploaden",
      fieldAddress: "Adres van pand",
      fieldCity: "Plaats",
      fieldPostalCode: "Postcode",
      fieldStartDate: "Gewenste startdatum",
      fieldTimeline: "Flexibiliteit planning",
      timelineFixed: "Vaste datum",
      timelineFlex2Weeks: "Flexibel binnen 2 weken",
      timelineFlex1Month: "Flexibel binnen een maand",
      fieldDuration: "Geschatte projectduur",
      selectDuration: "Kies duur",
      fieldBudget: "Budgetbereik (optioneel)",
      shareWithTechnicians: "Delen met vakmensen",
      selectBudget: "Kies budgetbereik",
      reviewTitle: "Controleer je project",
      reviewFieldTitle: "Titel",
      maxPhotosToast: "Maximaal 8 foto's toegestaan",
      createFailedToast: "Project aanmaken mislukt. Probeer opnieuw.",
      validationEnterTitle: "Voer een titel in",
      validationSelectCategory: "Kies een categorie",
      validationDescriptionNeeds: "Omschrijving heeft",
      validationMoreCharacters: "meer tekens nodig",
      validationEnterAddress: "Voer adres in",
      validationEnterCity: "Voer plaats in",
      validationEnterPostalCode: "Voer postcode in",
      validationSelectStartDate: "Kies startdatum",
      validationSelectDuration: "Kies duur",
      placeholderTitle: "bijv. \"Badkamerrenovatie — 2 badkamers\"",
      placeholderDescription: "Beschrijf het benodigde werk, de huidige staat en eventuele specifieke wensen…",
      placeholderAddress: "Straat + huisnummer",
      placeholderCity: "Rotterdam",
    },

    referrals: {
      nav: "Vrienden uitnodigen",
      title: "Nodig vrienden uit, verdien punten",
      subtitle: "Deel Bricos. Als een vriend zich aanmeldt en zijn eerste klus voltooit, krijgen jullie allebei {n} loyaliteitspunten.",
      yourLink: "Jouw uitnodigingslink",
      linkPending: "Beschikbaar na lancering",
      copied: "Gekopieerd!",
      share: "Delen",
      email: "E-mail",
      yourCode: "Jouw code",
      statInvited: "Uitgenodigd",
      statJoined: "Beloond",
      statPoints: "Punten verdiend",
      howItWorks: "Hoe het werkt",
      step1: "Deel je persoonlijke uitnodigingslink met vrienden.",
      step2: "Zij melden zich aan en boeken hun eerste klus.",
      step3: "Jullie krijgen allebei {n} loyaliteitspunten zodra deze is voltooid.",
      shareMessage: "Ik gebruik Bricos voor klussen in huis — gescreende vakmensen, vaste prijzen. Meld je aan via mijn link:",
      dashboardTitle: "Nodig vrienden uit, verdien punten",
      dashboardSubtitle: "Jullie krijgen allebei 250 loyaliteitspunten als een vriend zijn eerste klus voltooit",
    },

    pricingGuide: {
      from: "vanaf",
      perHour: "/uur",
      serviceFee: "servicekosten",
      basePrice: "Vaste prijs",
      callOutFee: "Voorrijkosten",
      standard: "Standaard / gepland",
      emergency: "Spoed",
      emergencySurcharge: "Spoedtoeslag",
      costOf: "Wat kost",
      costInNl: "in Nederland?",
      heroSub: "Echte, vaste prijzen — vooraf zichtbaar voordat je boekt. Geen offertes najagen, geen verrassingen.",
      priceNote: "Prijzen zijn indicatieve vanaf-prijzen; de exacte vaste prijs wordt vóór het boeken bevestigd.",
      tp1Title: "Vast & vooraf",
      tp1Desc: "Je ziet de prijs vóór het boeken — niet achteraf.",
      tp2Title: "Beschermd via escrow",
      tp2Desc: "We houden de betaling vast tot de klus naar tevredenheid is afgerond.",
      tp3Title: "Gescreend & verzekerd",
      tp3Desc: "Elke vakman is gecontroleerd op ID, diploma's en verzekering.",
      whatsIncluded: "Wat is inbegrepen",
      typicalEta: "Gemiddelde aankomst",
      ctaTitle: "Klaar om tegen een vaste prijs te boeken?",
      ctaSub: "Bekijk je exacte prijs en boek een gescreende vakman in een paar tikken.",
      bookCta: "Boek",
      learnMore: "Meer info",
      otherPrices: "Prijzen van andere diensten",
      hubTitle: "Prijzen voor klussen in Nederland (2026) — vast & transparant",
      hubHeading: "Wat kosten klussen in Nederland?",
      hubSub: "Bij de meeste platforms moet je offertes najagen. Wij publiceren echte, vaste prijzen — je ziet de prijs vóór je boekt, en die staat in escrow tot de klus klaar is.",
      howItWorks: "Hoe onze prijzen werken",
      hp1Title: "Vaste prijs + vast tarief",
      hp1Desc: "Een vaste serviceprijs + een vast tarief van €{fee}. That's it.",
      hp2Title: "Spoed is een vaste toeslag",
      hp2Desc: "Met spoed nodig? Een duidelijke, vaste spoedtoeslag — nooit een mysterieuze opslag.",
      hp3Title: "Veilig betaald via escrow",
      hp3Desc: "Je geld staat vast tot de klus klaar is en jij tevreden bent.",
      trustLine: "Gescreende, verzekerde vakmensen — vaste prijzen — escrow-beschermd",
      ctaBtn: "Bekijk prijs & boek",
    },
    // Admin Portal
    admin: {
      portal: 'Admin Portaal',
      dashboard: 'Dashboard',
      bookings: 'Boekingen',
      users: 'Gebruikers',
      technicians: 'Monteurs',
      disputes: 'Geschillen',
      reconciliation: 'Reconciliatie',
      settings: 'Instellingen',
      signOut: 'Uitloggen',
      totalBookings: 'Totaal Boekingen',
      totalRevenue: 'Totale Omzet',
      activeTechnicians: 'Actieve Monteurs',
      openDisputes: 'Open Geschillen',
      recentBookings: 'Recente Boekingen',
      manageRoles: 'Rollen Beheren',
      addRole: 'Rol Toevoegen',
      removeRole: 'Rol Verwijderen',
      searchUsers: 'Zoek gebruikers...',
      noUsers: 'Geen gebruikers gevonden',
      userDetails: 'Gebruiker Details',
      bookingDetails: 'Boeking Details',
      // Statuses
      pending: 'In Afwachting',
      confirmed: 'Bevestigd',
      technicianAssigned: 'Monteur Toegewezen',
      inProgress: 'In Uitvoering',
      completed: 'Voltooid',
      cancelled: 'Geannuleerd',
      underReview: 'In Beoordeling',
      awaitingTechnician: 'Wachten op Monteur',
      awaitingCustomer: 'Wachten op Klant',
      resolved: 'Opgelost',
      escalated: 'Geëscaleerd',
      acceptanceRate: "Acceptatiegraad",
      activeJobs: "Actieve klussen",
      activeUsersPerDay: "Actieve gebruikers per dag",
      actor: "Actor",
      addNewRole: "Nieuwe rol toevoegen",
      addOrRemoveRoles: "Rollen toevoegen of verwijderen voor",
      adminDashboard: "Beheerdersdashboard",
      adminNotes: "Beheerdersnotities",
      adminNotesOptional: "Beheerdersnotities (optioneel)",
      adminNotesPlaceholder: "Interne notities over dit besluit...",
      admins: "Beheerders",
      admin: "Beheer",
      ago: "geleden",
      all: "Alle",
      allClear: "Alles in orde",
      allDisputes: "Alle geschillen",
      allRatings: "Alle beoordelingen",
      allRoles: "Alle rollen",
      allStatuses: "Alle statussen",
      allTechnicians: "Alle monteurs",
      annualPlans: "Jaarabonnementen",
      appliedOn: "Aangemeld op",
      appliedRecently: "Recent aangemeld",
      approve: "Goedkeuren",
      approved: "Goedgekeurd",
      approvedOnly: "Alleen goedgekeurd",
      approveTechDesc: "kan dan klusaanbiedingen ontvangen en accepteren.",
      approveTechnicianTitle: "Monteur goedkeuren?",
      assigned: "Toegewezen",
      available: "Beschikbaar",
      averageRating: "Gemiddelde beoordeling",
      awaitingResponse: "Wacht op reactie",
      awaitingTech: "Wacht op monteur",
      booking: "Boeking",
      bookingId: "Boeking-ID",
      bookingsOverTime: "Boekingen in de tijd",
      businessDetails: "Bedrijfsgegevens",
      chatMessages: "Chatberichten",
      clear: "Wissen",
      clearDates: "Datums wissen",
      clearFilters: "Filters wissen",
      completedJobs: "Voltooide klussen",
      connected: "Gekoppeld",
      contribution: "Bijdrage",
      costPerBooking: "Kosten per boeking (CAC, schatting)",
      created: "Aangemaakt",
      criticalIssues: "Kritieke problemen",
      currentRoles: "Huidige rollen",
      customer: "Klant",
      customerEvidence: "Bewijs van klant",
      customerStatement: "Verklaring van klant",
      dashboardLoadError: "Dashboardgegevens laden mislukt",
      decision: "Besluit",
      deleteReview: "Beoordeling verwijderen",
      deleteReviewTitle: "Deze beoordeling verwijderen?",
      demand: "VRAAG",
      discrepancies: "Afwijkingen",
      dismissDispute: "Geschil afwijzen",
      dispute: "Geschil",
      disputeResolution: "Geschiloplossing",
      emergency: "Spoed",
      escalate: "Escaleren",
      event: "Gebeurtenis",
      filterByRole: "Filter op rol",
      filterByStatus: "Filter op status",
      filteredBookings: "Gefilterde boekingen",
      fullRefund: "Volledige terugbetaling aan klant",
      grossMarginJob: "Brutomarge/klus",
      issue: "Probleem",
      jobOffersVsAccepted: "Klusaanbiedingen vs. geaccepteerd",
      jobReport: "Klusrapport",
      jobs: "klussen",
      joined: "Lid sinds",
      last100Checked: "Laatste 100 gecontroleerd",
      loadingDashboard: "Dashboard laden...",
      location: "Locatie",
      lowRatings: "Lage beoordelingen (≤2)",
      manageBookings: "Bekijk en beheer alle boekingen",
      manageDisputes: "Beheer en los klantgeschillen op",
      manageUserRoles: "Gebruikersrollen beheren",
      manageUsers: "Beheer platformgebruikers en hun rollen",
      method: "Methode",
      metrics: "Statistieken",
      moderateReviews: "Beoordelingen volgen en modereren",
      mollieStatus: "Mollie-status",
      noBookings: "Geen boekingen gevonden",
      noBookingsDesc: "Er zijn nog geen boekingen op het platform.",
      noBookingsMatch: "Geen boekingen voldoen aan je filters",
      noBookingsMatchDesc: "Pas je zoekopdracht of filters aan.",
      noBookingsYet: "Nog geen boekingen",
      noDescription: "Geen omschrijving opgegeven",
      noDiscrepancies: "Geen betalingsafwijkingen gevonden",
      noDisputes: "Geen geschillen gevonden",
      noDisputesDesc: "Er zijn geen geschillen voor dit filter.",
      noEvents: "Geen gebeurtenissen",
      noEventsDesc: "Nog geen betalingsgebeurtenissen vastgelegd",
      noResponse: "Geen reactie",
      noResponseYet: "Nog geen reactie",
      noReviews: "Geen beoordelingen gevonden",
      noReviewsDesc: "Er zijn geen beoordelingen voor je criteria.",
      noRoles: "Geen rollen",
      noRolesAssigned: "Geen rollen toegewezen",
      notConfigured: "Niet geconfigureerd",
      noTechnicians: "Geen monteurs gevonden",
      noTechniciansDesc: "Er zijn nog geen aanmeldingen voor dit filter.",
      notSet: "Niet ingesteld",
      noUsersDesc: "Geen gebruikers gevonden voor je criteria.",
      orExactAmount: "Of voer exact bedrag in",
      overdue: "Achterstallig",
      overdueSla: "SLA verstreken",
      partialRefund: "Gedeeltelijke terugbetaling",
      paymentDiscrepancies: "Betalingsafwijkingen",
      paymentDiscrepanciesDesc: "Betalingen met afwijkende status tussen Mollie en database",
      paymentEventLedger: "Betalingsgebeurtenissen",
      paymentEventLedgerDesc: "Recente betalingsstatussen en acties",
      paymentId: "Betaling-ID",
      paymentReconciliation: "Betalingsreconciliatie",
      payments: "Betalingen",
      paymentSplit: "Betalingsverdeling",
      pendingReview: "Wacht op beoordeling",
      pendingVerification: "Wacht op verificatie",
      phone: "Telefoon",
      platformMarginOverTime: "Platformmarge in de tijd",
      platformOverview: "Platformoverzicht en statistieken",
      price: "Prijs",
      pricing: "Prijzen",
      problemDetails: "Probleemdetails",
      problemPhotos: "Probleemfoto's",
      propertyManager: "Vastgoedbeheerder",
      propertyMgrs: "Beheerders",
      rating: "Beoordeling",
      recentEvents: "Recente gebeurtenissen",
      reconciliationDesc: "Betalingsafwijkingen tussen Mollie en database bewaken en oplossen",
      reconsider: "Heroverwegen",
      refreshPage: "Pagina vernieuwen",
      reject: "Afwijzen",
      rejectApplicationTitle: "Aanmelding afwijzen?",
      rejected: "Afgewezen",
      rejectedOn: "Afgewezen op",
      rejectionReason: "Reden van afwijzing",
      rejectionReasonPlaceholder: "Leg uit waarom de aanmelding wordt afgewezen...",
      rejectionReasonRequired: "Reden van afwijzing *",
      rejectTechDesc: "krijgt bericht dat de aanmelding is afgewezen.",
      releaseToTechnician: "Uitbetalen aan monteur",
      remaining: "resterend",
      remove: "Verwijderen",
      repeatRate: "Herhaalgraad",
      replied: "Beantwoord",
      requestEvidence: "Bewijs opvragen",
      requestMoreEvidence: "Meer bewijs opvragen",
      requestResponse: "Reactie vragen",
      requireImmediateAction: "Vereist directe actie",
      resolution: "Uitkomst",
      resolutionNotesLabel: "Notities uitkomst (zichtbaar voor klant)",
      resolveExecute: "Oplossen & betaling uitvoeren",
      respondedOn: "Gereageerd op",
      response: "Reactie",
      retention: "RETENTIE",
      review: "Beoordeling",
      reviewApplications: "Aanmeldingen van monteurs beoordelen en goedkeuren",
      reviewDetails: "Beoordelingsdetails",
      reviews: "Beoordelingen",
      revoke: "Intrekken",
      reworkRequired: "Herstel vereist",
      roles: "Rollen",
      saveNotes: "Notities opslaan",
      searchBookingsPlaceholder: "Zoek op klant, monteur, adres, ID...",
      searching: "Zoeken",
      searchReviewsPlaceholder: "Zoek op klant, monteur of tekst...",
      searchUsersPlaceholder: "Zoek op naam of telefoon...",
      selectDateRange: "Kies datumbereik",
      selectResolution: "Kies uitkomst",
      service: "Dienst",
      servicesOffered: "Aangeboden diensten",
      shouldBeReviewed: "Moet worden bekeken",
      showing: "Toont",
      sla: "SLA",
      slaBreached: "SLA overschreden",
      slaDeadline: "SLA-deadline",
      slowConnectionRetry: "Dit kan door een trage verbinding komen. Probeer opnieuw.",
      splitPayment: "Betaling splitsen",
      supply: "AANBOD",
      technician: "Monteur",
      technicianEvidence: "Bewijs van monteur",
      technicianResponse: "Reactie van monteur",
      techResponded: "Monteur gereageerd",
      techResponses: "Reacties monteur",
      timeline: "Tijdlijn",
      totalPayments: "Totaal betalingen",
      totalReviews: "Totaal beoordelingen",
      totalTechnicians: "Totaal monteurs",
      totalUsers: "Totaal gebruikers",
      tryAgain: "Opnieuw proberen",
      twoPlusBookings: "2+ boekingen",
      unavailable: "Niet beschikbaar",
      unitEconomics: "UNIT-ECONOMIE",
      user: "Gebruiker",
      userManagement: "Gebruikersbeheer",
      verifiedTechs: "Geverifieerde monteurs",
      warnings: "Waarschuwingen",
      withResponse: "Met reactie",
      technicianApproved: "Monteur goedgekeurd",
      applicationRejected: "Aanmelding afgewezen",
      technicianApprovedDesc: "De monteur kan nu klusaanbiedingen ontvangen",
      applicationRejectedDesc: "De monteur is op de hoogte gebracht van de afwijzing",
      rejectionReasonRequiredTitle: "Reden van afwijzing vereist",
      rejectionReasonRequiredDesc: "Geef een reden op voor het afwijzen van deze aanmelding",
      unknown: "Onbekend",
      notProvided: "Niet opgegeven",
      kvk: "KVK",
      vat: "BTW",
      company: "Bedrijf",
      discMolliePaidDbPending: "Mollie betaald, DB in afwachting",
      discEscrowNotReleased: "Escrow niet vrijgegeven",
      discMollieRefundedDbNot: "Mollie terugbetaald, DB niet",
      discDbRefundedMollieNot: "DB terugbetaald, Mollie niet",
      discPaymentFailedStuck: "Betaling mislukt/vastgelopen",
      discNoMollieId: "Geen Mollie-ID",
      releaseEscrow: "Escrow vrijgeven",
      releaseEscrowDescStart: "Dit geeft",
      releaseEscrowDescEnd: "vrij aan de monteur. Deze actie kan niet ongedaan worden gemaakt. Weet je het zeker?",
      releasePayment: "Betaling vrijgeven",
      disputeResolvedToast: "Geschil opgelost en betaalactie uitgevoerd",
      resolveDisputeFailed: "Geschil oplossen mislukt",
      evidenceRequestedFrom: "Bewijs opgevraagd bij",
      notesSaved: "Notities opgeslagen",
      disputeEscalated: "Geschil geëscaleerd",
      unassigned: "Niet toegewezen",
      problem: "Probleem",
      diagnosis: "Diagnose",
      technicianNotes: "Notities monteur",
      partsUsed: "Gebruikte onderdelen",
      before: "Voor",
      after: "Na",
      disputeNotesPlaceholder: "Interne notities over dit geschil...",
      explainDecisionPlaceholder: "Licht het besluit toe...",
      customerRefund: "Terugbetaling klant",
      split: "Verdelen",
      reviewDeleted: "Beoordeling verwijderd",
      deleteReviewFailed: "Beoordeling verwijderen mislukt",
      unknownTechnician: "Onbekende monteur",
      noText: "Geen tekst",
      noReviewText: "Geen beoordelingstekst opgegeven",
      star: "Ster",
      stars: "Sterren",
      deleteReviewConfirmStart: "Dit verwijdert permanent deze",
      deleteReviewConfirmMid: "-sterrenbeoordeling van",
      deleteReviewConfirmEnd: "Deze actie kan niet ongedaan worden gemaakt.",
      roleAdded: "Rol succesvol toegevoegd",
      addRoleFailed: "Rol toevoegen mislukt",
      roleRemoved: "Rol succesvol verwijderd",
      removeRoleFailed: "Rol verwijderen mislukt",
      userAlreadyHasRole: "Gebruiker heeft deze rol al",
      unnamedUser: "Naamloze gebruiker",
      thisUser: "deze gebruiker",
      autoReleaseHours: "Uren tot auto-vrijgave",
      autoReleaseHoursDesc: "Uren na voltooiing voordat escrow automatisch vrijgeeft (zonder geschil)",
      disputeWindow: "Geschilperiode",
      disputeWindowDesc: "Uren na voltooiing waarin gebruikers een geschil kunnen openen",
      disputeSla: "Geschil-SLA",
      disputeSlaDesc: "Streeftijd voor het oplossen van geschillen",
      emergencySearchTimeout: "Time-out spoedzoekopdracht",
      emergencySearchTimeoutDesc: "Minuten om een monteur te zoeken vóór auto-terugbetaling (spoedboekingen)",
      scheduledSearchTimeout: "Time-out geplande zoekopdracht",
      scheduledSearchTimeoutDesc: "Uren vóór de geplande tijd om te starten met zoeken / time-out",
      technicianOfferTimeout: "Time-out klusaanbod",
      technicianOfferTimeoutDesc: "Seconden die een monteur heeft om een klusaanbod te accepteren",
      noShowGracePeriod: "No-show-coulanceperiode",
      noShowGracePeriodDesc: "Minuten na ETA voordat de monteur de klant als no-show kan markeren",
      unitHours: "uur",
      unitMinutes: "minuten",
      unitSeconds: "seconden",
      platformSettings: "Platforminstellingen",
      settingsDescription: "Configureer timers en SLA's en voer beveiligingstests uit",
      timerConfiguration: "Timerconfiguratie",
      timerConfigDescription: "Configureer platformtimers voor escrow, geschillen en monteurstoewijzing",
      saveChanges: "Wijzigingen opslaan",
      reset: "Herstellen",
      securityTests: "Beveiligingstests",
      securityTestsDescription: "Voer geautomatiseerde RLS (Row Level Security)-verificatietests uit",
      rlsTestsVerifyIsolation: "RLS-tests verifiëren data-isolatie",
      rlsTestsWarning: "Deze tests zorgen ervoor dat niet-geverifieerde gebruikers geen gevoelige data kunnen inzien. Voer uit vóór elke productie-deploy.",
      runRlsTests: "RLS-tests uitvoeren",
      allTestsPassed: "Alle tests geslaagd",
      vulnerabilitiesDetected: "Kwetsbaarheden gedetecteerd",
      passed: "geslaagd",
      failed: "mislukt",
      of: "van",
      tests: "tests",
      rlsTestsPassed: "RLS-tests geslaagd",
      rlsTestsFailedReview: "RLS-tests mislukt - bekijk resultaten",
      failedToRunTests: "Tests uitvoeren mislukt",
      failedToLoadSettings: "Instellingen laden mislukt",
      settingsSavedSuccessfully: "Instellingen succesvol opgeslagen",
      failedToSaveSettings: "Instellingen opslaan mislukt",
    },
    // Common UI elements
    ui: {
      loading: 'Laden...',
      error: 'Fout',
      success: 'Succes',
      save: 'Opslaan',
      cancel: 'Annuleren',
      confirm: 'Bevestigen',
      delete: 'Verwijderen',
      edit: 'Bewerken',
      view: 'Bekijken',
      close: 'Sluiten',
      back: 'Terug',
      next: 'Volgende',
      previous: 'Vorige',
      search: 'Zoeken',
      filter: 'Filteren',
      sort: 'Sorteren',
      export: 'Exporteren',
      import: 'Importeren',
      download: 'Downloaden',
      upload: 'Uploaden',
      refresh: 'Verversen',
      retry: 'Opnieuw',
      noData: 'Geen gegevens',
      noResults: 'Geen resultaten',
      selectAll: 'Alles Selecteren',
      deselectAll: 'Alles Deselecteren',
      actions: 'Acties',
      status: 'Status',
      date: 'Datum',
      time: 'Tijd',
      amount: 'Bedrag',
      total: 'Totaal',
      subtotal: 'Subtotaal',
      tax: 'BTW',
      fee: 'Kosten',
      discount: 'Korting',
      description: 'Beschrijving',
      notes: 'Notities',
      details: 'Details',
      information: 'Informatie',
      contact: 'Contact',
      phone: 'Telefoon',
      email: 'E-mail',
      name: 'Naam',
      address: 'Adres',
      city: 'Stad',
      country: 'Land',
      postalCode: 'Postcode',
      today: 'Vandaag',
      yesterday: 'Gisteren',
      thisWeek: 'Deze Week',
      thisMonth: 'Deze Maand',
      lastMonth: 'Vorige Maand',
      year: 'Jaar',
      all: 'Alles',
      none: 'Geen',
      yes: 'Ja',
      no: 'Nee',
      or: 'of',
      and: 'en',
    },
    serviceNames: {
      plumbing: 'Loodgieter',
      electrician: 'Elektricien',
      hvac: 'Airco & Ventilatie',
      boiler: 'CV-monteur',
      locksmith: 'Slotenmaker',
      appliance: 'Apparaten',
      roofing: 'Dakwerk',
      cleaning: 'Schoonmaak',
      painting: 'Schilderwerk',
      flooring: 'Vloeren',
      security: 'Beveiliging',
      outdoor: 'Tuin & Buiten',
      pest_control: 'Ongediertebestrijding',
      renovation: 'Renovatie',
      handyman: 'Klusjesman',
      moving: 'Verhuizen',
      smart_home: 'Smart Home',
      sustainable: 'Duurzaam Wonen',
      other: 'Overige Diensten',
    },
    popularActivities: {
      emergencyPlumbing: 'Spoed loodgieter reparatie',
      lockReplacement: 'Slot vervangen',
      emergencyElectrical: 'Spoed elektrische reparatie',
      boilerRepair: 'Ketel reparatie/installatie',
      drainUnclogging: 'Afvoer ontstoppen',
      emergencyLocksmith: 'Spoed slotenmaker',
    },
    supportForm: {
      contactSupport: 'Contact Support',
      contactDesc: 'Stuur ons een bericht en we reageren binnen 24 uur.',
      name: 'Naam',
      namePlaceholder: 'Je naam',
      email: 'E-mail',
      emailPlaceholder: 'jij@voorbeeld.com',
      subject: 'Onderwerp',
      subjectPlaceholder: 'Hoe kunnen we helpen?',
      message: 'Bericht',
      messagePlaceholder: 'Beschrijf je probleem of vraag in detail...',
      sendMessage: 'Bericht Versturen',
      messageSent: 'Bericht Verzonden!',
      messageSentDesc: 'We reageren binnen 24 uur.',
      thankYou: 'Bedankt voor je bericht. We reageren binnen 24 uur.',
      sendAnother: 'Nog een Bericht Versturen',
      sendFailed: 'Bericht versturen mislukt',
      sendFailedDesc: 'Probeer opnieuw of mail ons direct.',
      needHelp: 'Hulp Nodig?',
      needHelpDesc: 'Heb je een vraag of probleem? Stuur ons een bericht en we reageren binnen 24 uur.',
    },
    chatbot: {
      assistant: 'Bricos Assistent',
      greeting: 'Hallo! Ik ben de Bricos Assistent. Ik kan vragen beantwoorden over onze diensten, boekingen, betalingen en meer. Hoe kan ik je helpen?',
      askPlaceholder: 'Stel een vraag...',
      quickQuestions: 'Snelle vragen:',
      relatedQuestions: 'Gerelateerde vragen:',
      errorMessage: 'Sorry, ik heb momenteel verbindingsproblemen. Probeer het later opnieuw of bezoek onze Hulppagina voor veelgestelde vragen.',
      q1: 'Hoe snel kan een monteur komen?',
      q2: 'Hoe werkt escrow betaling?',
      q3: 'Wat als ik niet tevreden ben met het werk?',
      q4: 'Hoe annuleer ik een boeking?',
    },
    techSidebar: {
      disputes: 'Geschillen',
    },
    disputesPage: {
      myDisputes: 'Mijn Geschillen',
      disputeCount: 'geschil(len)',
      noDisputes: 'Geen Geschillen',
      allCompleted: 'Al je klussen zijn succesvol afgerond.',
      backToDashboard: 'Terug naar Dashboard',
      bookingRef: 'Boeking',
      fileDispute: 'Geschil Melden',
    },
    disputeDetail: {
      title: 'Geschildetails',
      notFound: 'Geschil Niet Gevonden',
      backToDisputes: 'Terug naar Geschillen',
      disputeInfo: 'Geschilgegevens',
      reason: 'Reden',
      description: 'Beschrijving',
      filedOn: 'Ingediend Op',
      service: 'Dienst',
      relatedBooking: 'Gerelateerde Boeking',
      amount: 'Bedrag',
      yourEvidence: 'Jouw Bewijs',
      noPhotos: "Geen foto's ingediend",
      technicianResponse: 'Reactie Monteur',
      resolution: 'Uitkomst',
      decision: 'Beslissing',
      notes: 'Opmerkingen',
      refund: 'Terugbetaling',
      addEvidence: 'Extra Bewijs Toevoegen',
      addEvidenceDesc: "Je kunt extra informatie of foto's indienen om je zaak te ondersteunen.",
      additionalInfo: 'Aanvullende Informatie',
      additionalInfoPlaceholder: 'Geef aanvullende details...',
      additionalPhotos: "Extra Foto's",
      submitEvidence: 'Extra Bewijs Indienen',
      resolutionExpected: 'Uitkomst verwacht voor',
      pendingReview: 'In Afwachting van Beoordeling',
      underReview: 'In Beoordeling',
      awaitingResponse: 'Wachten op Jouw Reactie',
      awaitingTechnician: 'Wachten op Monteur',
      escalated: 'Geëscaleerd',
      resolved: 'Opgelost',
    },
    supportPage: {
      title: 'Ondersteuning',
      subtitle: 'Hulp bij je boekingen',
      yourTickets: 'Jouw Tickets',
      selectTicket: 'Selecteer een ticket',
      selectTicketDesc: 'Kies een ticket uit de lijst of maak een nieuw ticket aan',
    },
    helpPage: {
      title: 'Helpcentrum',
      subtitle: 'Vind antwoorden op veelgestelde vragen of neem contact op',
      aiAssistant: 'AI Assistent',
      aiAssistantDesc: 'Klik op de chatknop rechtsonder voor directe antwoorden',
      emergencyLine: 'Hulp bij spoedboekingen',
      emergencyLineDesc: 'Voor urgente boekingsproblemen: neem contact op en we behandelen je verzoek met voorrang',
      responseTime: 'Reactietijd',
      responseTimeDesc: 'We reageren doorgaans binnen 24 uur op alle vragen',
      faq: 'Veelgestelde Vragen',
      booking: 'Boekingen',
      payments: 'Betalingen',
      technicians: 'Monteurs',
      accountCategory: 'Account',
      stillNeedHelp: 'Nog hulp nodig?',
      stillNeedHelpDesc: 'Niet gevonden wat je zoekt? Stuur ons een bericht en ons supportteam neemt contact met je op.',
    },
    trackingExtra: {
      lookingForTechnician: 'Monteur Zoeken',
      lookingForTechnicianDesc: 'We koppelen je klus aan de best beschikbare monteur. Je wordt bericht zodra iemand accepteert.',
      bookingCreated: 'Boeking aangemaakt',
      cancelBooking: 'Boeking Annuleren',
      cancelFailed: 'Annuleren mislukt',
      bookingCancelled: 'Boeking geannuleerd',
    },
    payoutsPage: {
      title: 'Uitbetalingsgeschiedenis',
      available: 'Beschikbaar',
      clearedWindow: 'Geschilperiode verstreken',
      inHold: 'In Wacht',
      disputeWindow: '-dagen geschilperiode',
      totalEarned: 'Totaal Verdiend',
      nextPayout: 'Volgende uitbetaling: Wekelijks op maandag',
      noPayouts: 'Nog geen uitbetalingen',
      platformFee: 'Platformkosten',
    },
    techExtra: {
      inDisputeWindow: 'In Geschilperiode',
      dayHold: '-dagen wacht',
      availableForPayout: 'Beschikbaar',
    },
    pmProperties: {
      propertyManagement: 'Vastgoedbeheer',
      propertiesManaged: 'panden beheerd',
      bulkUpload: 'Bulk Uploaden',
      noPropertiesYet: 'Nog geen panden',
      addFirstProperty: 'Voeg je eerste pand toe om te beginnen',
    },
    pmJobs: {
      jobManagement: 'Klussenbeheer',
      activeCount: 'actief',
      completedCount: 'voltooid',
      escalatedCount: 'geëscaleerd',
      refresh: 'Vernieuwen',
      allStatus: 'Alle Statussen',
      dispatch: 'Inzetten',
      autoDispatch: 'Auto-inzet (Register + Netwerk)',
      reassign: 'Hertoewijzen',
      contact: 'Contact',
      track: 'Volgen',
      bookNewJob: 'Nieuwe Klus Boeken',
      noActiveJobs: 'Geen actieve klussen',
      noActiveJobsDesc: 'Alle klussen zijn voltooid of er zijn geen overeenkomsten',
      noCompletedJobs: 'Geen voltooide klussen',
      noCompletedJobsDesc: 'Klussen verschijnen hier wanneer ze zijn voltooid',
      slaBreached: 'SLA Overschreden',
      remaining: 'resterend',
      escalation: 'Escalatie',
    },
    pmReports: {
      analyticsReports: 'Analyse & Rapporten',
      analyticsSubtitle: 'Analyses, aannemerprestaties en SLA-tracking',
      exportAll: 'Alles Exporteren',
      exporting: 'Exporteren...',
      totalSpent: 'Totaal Uitgegeven',
      completedJobs: 'Voltooide Klussen',
      avgJobCost: 'Gem. Kluskosten',
      monthlySpendingJobs: 'Maandelijkse Uitgaven & Klussen',
      jobsByService: 'Klussen per Dienst',
      noSpendingData: 'Nog geen uitgavegegevens',
      noServiceData: 'Nog geen dienstgegevens',
      vsLastMonth: 'vs vorige maand',
      overview: 'Overzicht',
      contractors: 'Aannemers',
      sla: 'SLA',
      costs: 'Kosten',
      spendingByProperty: 'Uitgaven per Pand',
      jobReports: 'Klusrapporten',
      searchReports: 'Zoek rapporten...',
      noCompletedJobsReport: 'Nog geen voltooide klussen om te rapporteren',
      downloadReport: 'Rapport Downloaden',
      technicianReports: 'Monteursrapporten',
      noMatchingReports: 'Geen overeenkomende rapporten gevonden',
      adjustSearch: 'Pas je zoekopdracht aan',
      reportsAfterCompletion: 'Rapporten verschijnen hier na voltooiing',
      generating: 'Genereren...',
      report: 'Rapport',
      jobs: 'klussen',
      matchingOf: 'van',
    },
    pmSettings: {
      title: 'Instellingen',
      manageOrgPrefs: 'Beheer je organisatie en voorkeuren',
      organization: 'Organisatie',
      team: 'Team',
      security: 'Beveiliging',
      auditLog: 'Auditlog',
      preferences: 'Voorkeuren',
      orgDetails: 'Organisatiegegevens',
      companyInfo: 'Je bedrijfsinformatie',
      companyName: 'Bedrijfsnaam',
      billingEmail: 'Facturatie E-mail',
      phone: 'Telefoon',
      address: 'Adres',
      city: 'Stad',
      postalCode: 'Postcode',
      saveChanges: 'Wijzigingen Opslaan',
      saving: 'Opslaan...',
      defaultSettings: 'Standaardinstellingen',
      defaultSettingsDesc: 'Stel standaardwaarden in voor nieuwe panden en boekingen',
      autoAssign: 'Automatisch dichtstbijzijnde monteur toewijzen',
      autoAssignDesc: 'Automatisch de dichtstbijzijnde beschikbare monteur selecteren',
      emergencyDefault: 'Spoed standaard aan',
      emergencyDefaultDesc: 'Alle nieuwe boekingen beginnen als spoed',
      defaultResponseSLA: 'Standaard respons-SLA',
      defaultResponseSLADesc: 'Streeftijd voor monteurrespons',
      reportSettings: 'Rapportinstellingen',
      reportSettingsDesc: 'Configureer hoe rapporten worden gegenereerd en verzonden',
      includePhotos: "Foto's in rapporten opnemen",
      includePhotosDesc: "Voor/na foto's bijvoegen bij klusrapporten",
      autoEmailReports: 'Rapporten automatisch e-mailen naar eigenaar',
      autoEmailReportsDesc: 'Voltooide klusrapporten naar pandeigenaar sturen',
      reportFormat: 'Rapportformaat',
      reportFormatDesc: 'Standaard exportformaat',
    },
    pmDisputes: {
      disputeManagement: 'Geschillenbeheer',
      title: 'Geschillen',
      subtitle: 'Volg en monitor geschillenresoluties voor je panden',
      totalDisputes: 'Totaal Geschillen',
      pending: 'In Afwachting',
      underReview: 'In Beoordeling',
      resolved: 'Opgelost',
      avgResolution: 'Gem. Doorlooptijd',
      customerFavor: 'Klant Gelijk',
      pastSLADeadline: 'SLA Deadline Verstreken',
      pastSLADeadlineDesc: 'Deze geschillen hebben de resolutiedeadline overschreden en vereisen mogelijk escalatie.',
      searchPlaceholder: 'Zoek op reden, pand of adres...',
      filterByStatus: 'Filter op status',
      allStatuses: 'Alle Statussen',
      awaitingTech: 'Wacht op Monteur',
      awaitingCustomer: 'Wacht op Klant',
      escalated: 'Geëscaleerd',
      active: 'Actief',
      noActiveDisputes: 'Geen Actieve Geschillen',
      allResolved: 'Alle geschillen zijn opgelost.',
      noSearchMatch: 'Geen geschillen gevonden voor je zoekcriteria.',
      noResolvedDisputes: 'Geen Opgeloste Geschillen',
      resolvedWillAppear: 'Opgeloste geschillen verschijnen hier.',
      loadingDisputes: 'Geschillen laden...',
      noOrgSelected: 'Geen Organisatie Geselecteerd',
      selectOrgDesc: 'Selecteer een organisatie om geschillen te bekijken.',
      noDescription: 'Geen beschrijving opgegeven',
      techResponded: 'Monteur Gereageerd',
      overdue: 'Achterstallig',
      due: 'Vervalt',
    },
    pmContractors: {
      contractorHub: 'Aannemer Hub',
      title: 'Aannemers',
      subtitle: 'Beheer je aannemernetwerk, contracten en goedkeuringen',
      approvals: 'Goedkeuringen',
    },
    serviceCategories: {
      plumbing: {
        label: 'Loodgieter & Water',
        description: 'Spoed loodgieter, lekreparaties, afvoer ontstoppen en meer',
        eta: '45-90 min',
        activities: ['Spoed loodgieter reparatie', 'Lekdetectie en reparatie', 'Afvoer ontstoppen', 'Rioolreiniging', 'Boiler installatie/reparatie', 'Leidingvervanging', 'Badkamer/keuken installatie'],
      },
      electrician: {
        label: 'Elektricien',
        description: 'Elektrische reparaties, installaties en veiligheidsinspecties',
        eta: '30-60 min',
        activities: ['Spoed elektrische reparatie', 'Bedrading installatie/reparatie', 'Zekeringkast vervanging', 'Verlichting installatie', 'Smart home apparaat installatie', 'Generator installatie/onderhoud', 'Elektrische veiligheidsinspectie', 'Smart home installatie', 'Telefoon reparatie', 'Computer reparatie'],
      },
      hvac: {
        label: 'Airco & Ventilatie',
        description: 'Verwarming, ventilatie en airconditioning diensten',
        eta: '60-120 min',
        activities: ['Ketel reparatie/installatie', 'Radiator onderhoud', 'Airconditioning installatie/reparatie', 'Ventilatiesysteem reiniging', 'Thermostaat installatie', 'Warmtepomp service', 'Isolatie installatie', 'Kachel installatie'],
      },
      boiler: {
        label: 'CV-ketel Reparatie',
        description: 'Deskundige ketelreparatie en centrale verwarming',
        eta: '45-90 min',
        activities: ['Ketel reparatie/installatie', 'Radiator onderhoud', 'Centrale verwarming reparatie', 'Thermostaat installatie', 'Gaslek detectie', 'Leiding reparatie', 'Vloerverwarming'],
      },
      appliance: {
        label: 'Apparaten Service',
        description: 'Reparatie en installatie van huishoudelijke apparaten',
        eta: '60-120 min',
        activities: ['Wasmachine reparatie', 'Vaatwasser reparatie', 'Koelkast/vriezer reparatie', 'Oven/fornuis reparatie', 'Droger reparatie', 'Apparaat installatie'],
      },
      cleaning: {
        label: 'Schoonmaak & Onderhoud',
        description: 'Diepte reiniging, tapijt reiniging en onderhoudsdiensten',
        eta: 'Dezelfde dag',
        activities: ['Diepte reiniging woning', 'Tapijt reiniging', 'Ramen wassen', 'Glazenwasser', 'Dakgoot reiniging', 'Hogedrukreiniging (terras, oprit)', 'Schoorsteen vegen', 'Na-renovatie schoonmaak'],
      },
      painting: {
        label: 'Schilderwerk & Decoratie',
        description: 'Binnen- en buitenschilderwerk, behang en stucwerk',
        eta: '1-2 dagen',
        activities: ['Binnenschilderwerk', 'Buitenschilderwerk', 'Behang plaatsen/verwijderen', 'Behanger', 'Stucwerk en wandreparatie', 'Stukadoor', 'Decoratieve afwerkingen'],
      },
      flooring: {
        label: 'Vloeren',
        description: 'Tegels, hardhouten, laminaat en tapijt installatie',
        eta: '1-3 dagen',
        activities: ['Tegel installatie/reparatie', 'Tegelzetter', 'Hardhouten vloer installatie/renovatie', 'Laminaat vloer installatie', 'Tapijt installatie', 'Vloerspecialist'],
      },
      locksmith: {
        label: 'Slotenmaker',
        description: 'Slot vervanging, nood buitensluiting en beveiligingsdiensten',
        eta: '20-45 min',
        activities: ['Slot vervanging', 'Spoed slotenmaker', 'Sleutel bijmaken', 'Slot installatie', 'Kluis openen', 'Auto buitensluiting', 'Beveiligingsupgrades', 'Hoofdsleutelsystemen'],
      },
      security: {
        label: 'Beveiliging',
        description: 'Beveiligingscamera\'s, alarmsystemen en slimme sloten',
        eta: 'Dezelfde dag',
        activities: ['Slot vervanging', 'Spoed slotenmaker', 'Beveiligingscamera installatie', 'Alarmsysteem installatie', 'Slim slot installatie'],
      },
      outdoor: {
        label: 'Tuin & Buiten',
        description: 'Gazononderhoud, snoeien, hekwerk en buitenbouw',
        eta: 'Dezelfde dag',
        activities: ['Gazononderhoud en maaien', 'Bomen snoeien/verwijderen', 'Hekwerk installatie/reparatie', 'Terras/deck bouw', 'Sneeuwruimen', 'Hovenier', 'Zonnescherm monteur', 'Gevelspecialist'],
      },
      pest_control: {
        label: 'Ongediertebestrijding',
        description: 'Knaagdieren, insecten, termieten en bedwants bestrijding',
        eta: 'Dezelfde dag',
        activities: ['Knaagdierbestrijding', 'Insectenbestrijding', 'Termieten behandeling', 'Bedwants verwijdering'],
      },
      renovation: {
        label: 'Renovatie & Bouw',
        description: 'Keuken, badkamer verbouwing, dakwerk en meer',
        eta: 'Variabel',
        activities: ['Keuken verbouwing', 'Keukenspecialist', 'Badkamer verbouwing', 'Kelder afwerking', 'Dak reparatie/vervanging', 'Dakdekker', 'Gipsplaat installatie', 'Metselaar', 'Timmerman', 'Tegelzetter', 'Architect', 'Glazenwasser'],
      },
      other: {
        label: 'Overige Diensten',
        description: 'Klusjesman diensten, meubelmontage en gespecialiseerd werk',
        eta: 'Variabel',
        activities: ['Klusjesman diensten', 'Handige hulp', 'Meubelmontage', 'Energie-audit', 'Zonnepaneel installatie', 'Waterschade herstel', 'Schimmelverwijdering', 'Brandschade herstel', 'Offerte service'],
      },
      handyman: {
        label: 'Klusjesman',
        description: 'Algemene reparaties, installaties en klusjes in huis',
        eta: 'Dezelfde dag',
        activities: ['Algemene reparaties', 'Meubelmontage', 'Schilderij ophangen', 'Plank installatie', 'Deur reparaties', 'Klein timmerwerk', 'Diverse klusjes'],
      },
      moving: {
        label: 'Verhuisbedrijf',
        description: 'Professioneel verhuizen, inpakken en verplaatsen',
        eta: 'Gepland',
        activities: ['Woning verhuizing', 'Kantoor verhuizing', 'Inpakservice', 'Meubeltransport', 'Piano verhuizing', 'Opslagoplossingen'],
      },
      smart_home: {
        label: 'Smart Home',
        description: 'Smart home installatie, automatisering en tech setup',
        eta: 'Dezelfde dag',
        activities: ['Slimme thermostaat installatie', 'Slimme verlichting setup', 'Huisautomatisering', 'WiFi optimalisatie', 'Slimme beveiliging setup', 'Stemassistent integratie', 'Computer reparatie', 'Telefoon reparatie'],
      },
      sustainable: {
        label: 'Duurzaam Wonen',
        description: 'Milieuvriendelijke upgrades, isolatie en energiebesparing',
        eta: '1-3 dagen',
        activities: ['Zonnepaneel installatie', 'Isolatie installatie', 'Energie-audit', 'Dubbel glas', 'Warmtepomp installatie', 'Regenwateropvang', 'Groen dak installatie'],
      },
    },
  },
  fr: {
    // Common
    common: {
      back: 'Retour',
      backToHome: "Retour à l'accueil",
      submit: 'Soumettre',
      submitting: 'Envoi...',
      loading: 'Chargement...',
      signIn: 'Connexion',
      signUp: "S'inscrire",
      signInSignUp: "Connexion / S'inscrire",
      returnHome: "Retour à l'Accueil",
      next: 'Suivant',
      cancel: 'Annuler',
      save: 'Sauvegarder',
      edit: 'Modifier',
      delete: 'Supprimer',
      view: 'Voir',
      close: 'Fermer',
      confirm: 'Confirmer',
      search: 'Rechercher',
      filter: 'Filtrer',
      all: 'Tous',
      learnMore: 'En Savoir Plus',
      getStarted: 'Commencer',
      bookNow: 'Réserver',
      contactUs: 'Contactez-nous',
      optional: 'optionnel',
    },
    // Reviews
    reviews: {
      rateService: 'Évaluer ce service',
      howWasExperience: 'Comment était votre expérience avec',
      selectRating: 'Sélectionnez une note',
      ratingPoor: 'Mauvais',
      ratingFair: 'Passable',
      ratingGood: 'Bien',
      ratingVeryGood: 'Très Bien',
      ratingExcellent: 'Excellent!',
      writeReview: 'Écrire un avis',
      shareExperience: 'Partagez votre expérience avec ce technicien...',
      submitReview: 'Soumettre l\'avis',
      thankYou: 'Merci pour votre avis!',
      alreadyReviewed: 'Vous avez déjà évalué ce travail',
    },
    // Navbar
    navbar: {
      home: 'Accueil',
      services: 'Services',
      howItWorks: 'Comment Ça Marche',
      plans: 'Abonnements',
      propertyManagers: 'Gestionnaires Immobiliers',
      becomeTechnician: 'Devenir Technicien',
      forTechnicians: 'Pour les techniciens',
      dashboard: 'Tableau de Bord',
      signIn: 'Connexion',
      signOut: 'Déconnexion',
    },
    // Hero
    hero: {
      badge: 'Techniciens vérifiés · Paiements sécurisés',
      title: 'Tous vos travaux à la maison, une plateforme de confiance',
      titleHighlight: 'pour les clients & les techniciens',
      subtitle: "Trouvez un technicien vérifié pour l'électricité, la serrurerie, le chauffage, le nettoyage et plus encore — réservez instantanément en cas d'urgence, ou comparez les devis pour les travaux planifiés. Les techniciens trouvent des missions locales et sont payés en toute sécurité.",
      cta: 'Trouver un technicien',
      secondaryCta: 'Travailler comme technicien',
      trust: 'De confiance aux Pays-Bas',
      trustVerified: 'Techniciens vérifiés',
      trustEscrow: 'Paiements protégés par séquestre',
      trustPricing: 'Prix transparents',
    },
    // For Technicians
    forTechnicians: {
      badge: 'Pour les techniciens',
      title: 'Développez votre activité avec Bricos',
      subtitle: "Soyez mis en relation avec des clients locaux, fixez vos prix et soyez payé en toute sécurité — sans frais de prospection à courir.",
      benefit1: {
        title: 'Des missions locales régulières',
        description: "Recevez des offres de missions et des demandes de devis de clients près de chez vous.",
      },
      benefit2: {
        title: 'Payé à temps',
        description: "Les paiements sont conservés sous séquestre et vous sont versés une fois la mission terminée.",
      },
      benefit3: {
        title: 'Construisez votre réputation',
        description: "Gagnez des notes et des avis qui vous rapportent plus de travail.",
      },
      cta: 'Devenir technicien',
    },
    // Services
    services: {
      title: 'Des services pour chaque maison',
      subtitle: "Des dépannages urgents aux projets planifiés — électricité, serrurerie, chauffage, nettoyage et plus encore.",
      electrician: {
        title: 'Électricien',
        description: "Réparations électriques, câblage, tableaux et pannes d'électricité",
        eta: 'Réponse rapide',
      },
      locksmith: {
        title: 'Serrurier',
        description: 'Réparations de serrures, ouvertures de portes, duplication de clés',
        eta: 'Réponse rapide',
      },
      boiler: {
        title: 'Chauffagiste',
        description: 'Systèmes de chauffage, réparations de chaudières, entretien',
        eta: 'Réponse rapide',
      },
      from: 'À partir de',
      warranty: 'Qualité garantie',
      comingSoonTitle: 'Plus de services bientôt',
      comingSoonDesc: 'Nous nous développons partout aux Pays-Bas — ces métiers arrivent bientôt.',
      comingSoonBadge: 'Bientôt',
    },
    // How It Works
    howItWorks: {
      title: 'Comment Ça Marche',
      subtitle: 'Trouvez le bon professionnel en quelques étapes simples',
      step1: {
        title: 'Décrivez la mission',
        description: 'Indiquez ce dont vous avez besoin — pour chacune de nos catégories de services.',
      },
      step2: {
        title: 'Soyez mis en relation ou comparez les devis',
        description: 'Réservez instantanément un technicien disponible, ou recevez des devis et choisissez celui qui vous convient.',
      },
      step3: {
        title: 'La mission est réalisée',
        description: "Suivez l'avancement et discutez avec votre technicien jusqu'à la fin de la mission.",
      },
      step4: {
        title: 'Payez en toute sécurité',
        description: 'Votre paiement est conservé sous séquestre et versé au technicien seulement 3 jours après la fin des travaux — et uniquement si aucun litige n\'a été ouvert.',
      },
    },
    // Plans
    plans: {
      title: 'Abonnements',
      subtitle: 'Avantages pour clients réguliers',
      annual: {
        title: 'Abonnement Annuel',
        price: '€149/an',
        features: [
          'Contrôle de sécurité à prix réduit (€99 au lieu du tarif normal)',
          '10% de réduction sur les frais de déplacement',
          'Priorité dans la file de réservation',
          'Paiements protégés par séquestre',
          '2x points de fidélité sur les réservations',
        ],
      },
      payAsYouGo: {
        title: 'Paiement à la Demande',
        price: 'Sans engagement',
        features: [
          'Paiement par service',
          'Temps de réponse standard',
          'Paiements protégés par séquestre',
          'Gagnez des points de fidélité',
        ],
      },
      cta: 'Commencer',
    },
    // Property Managers
    propertyManagers: {
      title: 'Pour Gestionnaires Immobiliers',
      subtitle: 'Gérez toutes vos propriétés en un seul endroit',
      features: {
        multiProperty: 'Tableau de bord multi-propriétés',
        bulkPricing: 'Remises de volume',
        sla: 'Réponse SLA prioritaire',
        billing: 'Facturation consolidée',
        reports: 'Rapports détaillés',
        maintenance: "Passeport d'entretien par unité",
      },
      cta: 'Essai Gratuit',
    },
    // Technician Onboarding
    technicianOnboarding: {
      title: 'Devenir Technicien',
      subtitle: 'Rejoignez notre réseau de professionnels vérifiés',
      signInRequired: 'Connexion requise',
      signInMessage: 'Veuillez créer un compte ou vous connecter pour postuler comme technicien.',
      applicationSubmitted: 'Candidature Soumise !',
      applicationMessage: 'Merci de postuler chez Bricos. Notre équipe examinera votre candidature et vous contactera dans 2-3 jours ouvrables.',
      step1: {
        title: 'Choisissez votre spécialité',
        subtitle: 'Sélectionnez le type de service dans lequel vous êtes spécialisé',
      },
      step2: {
        title: 'Vos Informations',
        subtitle: 'Parlez-nous de vous et de votre expérience',
      },
      form: {
        fullName: 'Nom Complet',
        fullNamePlaceholder: 'Jean Dupont',
        phone: 'Numéro de Téléphone',
        phonePlaceholder: '+31 6 12345678',
        companyName: "Nom de l'Entreprise",
        companyNamePlaceholder: 'Votre Entreprise SARL',
        kvkNumber: 'Numéro KVK',
        kvkNumberPlaceholder: '12345678',
        vatNumber: 'Numéro TVA',
        vatNumberPlaceholder: 'NL123456789B01',
        experience: 'Expérience & Qualifications',
        experiencePlaceholder: 'Décrivez votre expérience, certifications et qualifications pertinentes...',
        submitApplication: 'Soumettre Candidature',
      },
      benefits: {
        title: 'Pourquoi rejoindre Bricos ?',
        fairPay: 'Rémunération équitable',
        fairPayDesc: 'Pas de frais de leads – voyez votre paiement avant d\'accepter',
        fastPayouts: 'Paiements rapides',
        fastPayoutsDesc: 'Paiements hebdomadaires, le lendemain pour les meilleurs',
        protection: 'Protection',
        protectionDesc: 'Résolution des litiges et communication client gérées',
        flexibility: 'Flexibilité',
        flexibilityDesc: 'Définissez votre disponibilité et zone de travail',
      },
      errors: {
        selectService: 'Veuillez sélectionner un service',
        applicationExists: 'Candidature existante',
        applicationExistsMessage: 'Vous avez déjà soumis une candidature. Veuillez patienter.',
        submitError: 'Erreur',
        submitErrorMessage: 'Échec de la soumission. Veuillez réessayer.',
        nameMin: 'Le nom doit contenir au moins 2 caractères',
        phoneMin: 'Veuillez entrer un numéro de téléphone valide',
        experienceMin: 'Décrivez votre expérience (au moins 20 caractères)',
        companyRequired: "Le nom de l'entreprise est requis",
        kvkRequired: 'Le numéro KVK est requis',
        kvkFormat: 'Le numéro KVK doit contenir 8 chiffres',
      },
    },
    // Onboarding
    onboarding: {
      welcome: 'Bienvenue chez Bricos !',
      welcomeDesc: "La plateforme pour tous vos travaux à la maison. Réservez des techniciens vérifiés pour l'électricité, la plomberie, la serrurerie et plus — réservez instantanément ou comparez les devis, le tout à prix fixe et paiements protégés par séquestre.",
      howItWorks: 'Rapide & transparent',
      howItWorksDesc: "Sélectionnez un service, décrivez votre problème et soyez mis en relation avec un technicien vérifié en quelques minutes. Suivez son arrivée en temps réel sur la carte.",
      safePayments: 'Paiements séquestrés sécurisés',
      safePaymentsDesc: "Votre paiement est conservé en toute sécurité jusqu'à ce que le travail soit terminé à votre satisfaction. Pas de frais cachés, pas de surprises.",
      skip: 'Passer',
      next: 'Suivant',
      bookFirst: 'Réservez votre première réparation',
    },
    // Footer
    footer: {
      description: "La marketplace de confiance qui met en relation les clients avec des techniciens vérifiés partout aux Pays-Bas — pour tous vos travaux à la maison, urgents ou planifiés.",
      services: 'Services',
      moreServices: 'Plus de Services',
      company: 'Entreprise',
      legal: 'Mentions Légales',
      termsOfService: "Conditions d'Utilisation",
      privacyPolicy: 'Politique de Confidentialité',
      helpCenter: "Centre d'Aide",
      contact: 'Contact',
      phone: '',
      email: 'support@bricos.nl',
      contactComingSoon: 'Support téléphonique & email bientôt disponible',
      location: 'Pays-Bas',
      rights: 'Tous droits réservés.',
    },
    // Auth
    auth: {
      signIn: 'Connexion',
      signUp: "S'inscrire",
      signInShort: 'Se connecter',
      signUpShort: "S'inscrire",
      createAccount: 'Créer un Compte',
      creatingAccount: 'Création du compte...',
      signingIn: 'Connexion...',
      welcomeBack: 'Bienvenue',
      createYourAccount: 'Créez votre compte',
      signInDesc: 'Connectez-vous pour accéder à votre tableau de bord et réservations',
      signUpDesc: "Inscrivez-vous pour réserver des techniciens vérifiés et suivre vos missions",
      fullName: 'Nom Complet',
      fullNamePlaceholder: 'Jean Dupont',
      email: 'Email',
      emailPlaceholder: 'vous@exemple.com',
      password: 'Mot de passe',
      confirmPassword: 'Confirmer le Mot de passe',
      forgotPassword: 'Mot de passe oublié ?',
      noAccount: "Pas de compte ?",
      hasAccount: 'Déjà un compte ?',
      resetPassword: 'Réinitialiser le Mot de passe',
      newPassword: 'Nouveau Mot de passe',
      iAmA: 'Je suis un(e)...',
      homeowner: 'Propriétaire / Utilisateur',
      homeownerDesc: "Réservez des techniciens vérifiés pour votre maison",
      technician: 'Technicien',
      technicianDesc: 'Rejoignez en tant que prestataire',
      propertyManager: 'Gestionnaire Immobilier',
      propertyManagerDesc: 'Gérez plusieurs propriétés',
      emergencyServices: "Des techniciens vérifiés, quand vous en avez besoin",
      authDescription: "Électriciens, serruriers et chauffagistes à votre porte. Prix fixes, paiements séquestrés et réparations de qualité garantie.",
      avgArrival: 'Arrivée moy.',
      rating: 'Note',
      jobsDone: 'Travaux réalisés',
    },
    // Dashboard
    dashboard: {
      welcome: 'Bienvenue',
      manageBookings: 'Gérez vos réservations et suivez les réparations',
      newBooking: 'Nouvelle Réservation',
      loyaltyPoints: 'Points de Fidélité',
      tier: 'niveau',
      ptsTo: 'pts vers',
      activeJobs: 'Travaux Actifs',
      trackLatestJob: 'Suivre le dernier travail',
      completedJobs: 'Travaux Terminés',
      viewHistory: "Voir l'historique",
      activeBookings: 'Réservations Actives',
      recentRepairs: 'Réparations Récentes',
      viewAll: 'Voir tout',
      report: 'Rapport',
      noBookingsYet: 'Aucune réservation',
      bookFirstService: "Réservez votre première mission et soyez mis en relation avec un technicien vérifié.",
      recentActivity: 'Activité Récente',
      noBookings: 'Aucune réservation active',
      bookService: 'Réserver un Service',
      customerDashboard: 'Tableau de Bord Client',
      openDisputes: 'Litiges Ouverts',
      viewDisputes: 'Voir Litiges',
    },
    // Dashboard Header
    dashboardHeader: {
      admin: 'Admin',
      adminPanel: "Panneau d'Admin",
      notifications: 'Notifications',
      noNewNotifications: 'Aucune nouvelle notification',
      profile: 'Profil',
      settings: 'Paramètres',
      bookingHistory: 'Historique des Réservations',
      servicePlans: 'Abonnements',
      helpSupport: 'Aide & Support',
      signOut: 'Déconnexion',
    },
    // Dashboard Footer
    dashboardFooter: {
      helpCenter: "Centre d'Aide",
      contact: 'Contact',
      support247: 'Support',
      privacy: 'Confidentialité',
      terms: 'Conditions',
    },
    // Tracking
    tracking: {
      trackTechnician: 'Suivez Votre Technicien',
      booking: 'Réservation',
      paymentConfirmed: 'Paiement Confirmé !',
      paymentFailed: "Le paiement n'a pas été finalisé. Veuillez réessayer de réserver.",
      paymentPending: "Nous n'avons pas encore pu confirmer votre paiement — si vous l'avez effectué, cela sera mis à jour sous peu.",
      paymentEscrowMsg: 'Votre paiement est conservé en séquestre. Un technicien sera assigné sous peu.',
      estimatedArrival: 'Arrivée Estimée',
      minutes: 'minutes',
      jobProgress: 'Progression du Travail',
      bookingConfirmed: 'Réservation Confirmée',
      bookingConfirmedDesc: 'Votre réservation a été confirmée',
      technicianAssigned: 'Technicien Assigné',
      technicianAssignedDesc: 'Un technicien a été assigné à votre travail',
      onTheWay: 'En Route',
      onTheWayDesc: 'Votre technicien est en route',
      jobCompleted: 'Travail Terminé',
      jobCompletedDesc: 'Le travail a été complété avec succès',
      yourTechnician: 'Votre Technicien',
      specialist: 'Spécialiste',
      verified: 'Vérifié',
      jobsCompleted: 'travaux complétés',
      call: 'Appeler',
      message: 'Message',
      jobDetails: 'Détails du Travail',
      location: 'Emplacement',
      serviceType: 'Type de Service',
      emergency: 'Urgence',
      standard: 'Standard',
      fixedPrice: 'Prix Fixe',
      bookingNotFound: 'Réservation introuvable',
      backToDashboard: 'Retour au Tableau de Bord',
      technicianArrived: 'Le Technicien Est Arrivé',
      technicianArrivedDesc: 'a commencé à travailler. Veuillez confirmer son arrivée pour démarrer le chrono.',
      confirmArrival: "Confirmer l'Arrivée du Technicien",
      arrivalConfirmed: 'Arrivée confirmée ! Le chrono est lancé.',
      arrivalConfirmFailed: "Échec de la confirmation d'arrivée",
      confirmedAt: 'Confirmé à',
      approvedExtraWork: 'Travaux Supplémentaires Approuvés',
      total: 'Total',
      enRoute: 'En Route',
      navigateToJob: "Naviguer vers le lieu d'intervention",
      iHaveArrived: 'Je Suis Arrivé',
      travelTime: 'Temps de Trajet',
      pricing: 'Tarification',
      callOutFee: 'Frais de déplacement',
      hourlyRate: 'Horaire',
      estimated: 'estimé',
      jobDetailsExpand: 'Détails du Travail',
      kmAway: 'km de distance',
      customer: 'Client',
    },
    // History
    history: {
      title: "Historique d'Entretien",
      completedJobs: 'travaux terminés',
      noCompletedJobs: 'Aucun travail terminé',
      bookFirstService: 'Réservez Votre Premier Service',
      report: 'Rapport',
      invoice: 'Facture',
      rate: 'Noter',
    },
    // Settings
    settings: {
      title: 'Paramètres',
      backToDashboard: 'Retour au tableau de bord',
      profile: 'Profil',
      email: 'Email',
      emailCannotChange: "L'email ne peut pas être modifié",
      fullName: 'Nom Complet',
      fullNamePlaceholder: 'Jean Dupont',
      phoneNumber: 'Numéro de Téléphone',
      phonePlaceholder: '+31 6 12345678',
      language: 'Langue',
      saveChanges: 'Enregistrer',
      saving: 'Enregistrement...',
      profileUpdated: 'Profil mis à jour',
      changesSaved: 'Vos modifications ont été enregistrées.',
      error: 'Erreur',
      updateError: 'Échec de la mise à jour du profil. Veuillez réessayer.',
      security: 'Sécurité',
      changePassword: 'Changer le Mot de Passe',
      changePasswordDesc: 'Mettez à jour votre mot de passe pour sécuriser votre compte.',
      resetPassword: 'Réinitialiser le Mot de Passe',
      account: 'Compte',
      signOut: 'Déconnexion',
      deleteAccount: 'Supprimer le Compte',
      deleteAccountTitle: 'Supprimer votre compte ?',
      deleteAccountDesc: 'Cette action est irréversible. Cela supprimera définitivement votre compte et toutes vos données de nos serveurs.',
      cancel: 'Annuler',
      accountDeletion: 'Suppression du compte',
      contactSupport: 'Veuillez contacter le support pour supprimer votre compte.',
    },
    // Language
    language: {
      select: 'Langue',
      en: 'English',
      nl: 'Nederlands',
      fr: 'Français',
    },
    // Pricing Transparency
    pricing: {
      badge: 'Tarifs Transparents',
      title: 'Sachez Exactement Ce Que Vous Payez',
      subtitle: 'Pas de frais cachés. Tous les coûts affichés avant la réservation.',
      fixedPrice: {
        title: 'Prix Fixe',
        description: 'Le prix est fixé avant le début du travail. Vous connaissez le total exact à l\'avance. Parfait pour les réparations standard.',
        services: 'Serruriers, Électriciens, Plombiers',
      },
      hourlyRate: {
        title: 'Tarif Horaire',
        description: 'Payez les frais de déplacement + tarif horaire pour le temps réel travaillé. Idéal pour les travaux de durée variable.',
        services: 'Nettoyage, Peinture, Rénovations',
      },
      examples: {
        fixedJob: {
          type: 'Travail Prix Fixe',
          service: 'Remplacement de Serrure',
          note: 'Prix convenu à l\'avance. Pas de surprises.',
        },
        fixedUrgent: {
          type: 'Fixe + Urgent',
          service: 'Ouverture d\'Urgence',
          note: 'Même prix fixe + supplément urgent pour envoi prioritaire.',
        },
        hourlyJob: {
          type: 'Travail Tarif Horaire',
          service: 'Nettoyage à Domicile',
          note: 'Payez uniquement pour le temps réel travaillé.',
        },
        hourlyUrgent: {
          type: 'Horaire + Urgent',
          service: 'Nettoyage d\'Urgence',
          note: 'Urgent ajoute €25/h pour disponibilité immédiate.',
        },
      },
      labels: {
        servicePrice: 'Prix du service',
        serviceFee: 'Frais de service',
        emergencySurcharge: 'Supplément urgence',
        callOutFee: 'Frais de déplacement',
        hourlyRate: 'Tarif horaire',
        total: 'Total',
        urgent: 'Urgent',
        hours: 'h',
        perHour: 'par heure',
        urgentPerHour: 'urgent',
      },
      trust: {
        title: 'Votre Argent est Protégé',
        subtitle: 'Paiement en séquestre jusqu\'à confirmation du travail.',
        warranty: 'Qualité garantie',
        verified: 'Techniciens vérifiés',
        support: 'Support client',
      },
    },
    // Satisfaction Guarantee
    satisfaction: {
      badge: 'Satisfaction Garantie',
      title: 'Votre Confiance, Notre Priorité',
      subtitle: 'Nous garantissons chaque travail avec une protection complète',
      moneyBack: 'Protection Séquestre',
      moneyBackDesc: 'Votre paiement est conservé en sécurité et libéré uniquement quand vous êtes satisfait. Les litiges sont gérés via notre processus de résolution structuré.',
      warranty: 'Focus Qualité',
      warrantyDesc: 'Problèmes après un travail ? Notre processus de résolution des litiges aide à trouver un résultat équitable pour tous.',
      escrow: 'Séquestre Sécurisé',
      escrowDesc: 'Votre argent est conservé sous séquestre et versé au technicien seulement 3 jours après la fin, si aucun litige n\'est ouvert.',
      verified: 'Techniciens Professionnels',
      verifiedDesc: 'Les techniciens s\'inscrivent avec leur numéro KvK, certifications et informations d\'assurance.',
    },
    // Booking
    booking: {
      step1Title: 'Choisir Service',
      step2Title: 'Détails',
      step3Title: 'Confirmer',
      whatDoYouNeed: 'De quoi avez-vous besoin ?',
      searchOrBrowse: 'Recherchez ou parcourez nos services',
      searchPlaceholder: 'Rechercher services... (ex. fuite, serrure, chaudière)',
      popularServices: 'Services populaires',
      foundServices: '{count} services correspondants trouvés :',
      noServicesFound: 'Aucun service trouvé pour',
      clearSearch: 'Effacer la recherche',
      emergency: 'Urgence',
      emergencyArrival: 'Envoi prioritaire',
      scheduled: 'Planifié',
      scheduledSave: 'Planification flexible',
      selectSpecificService: 'Sélectionnez un service spécifique :',
      continue: 'Continuer',
      tellUsMore: 'Dites-nous en plus',
      provideDetails: 'Fournissez des détails sur votre problème et votre emplacement',
      preferredDate: 'Date préférée',
      describeProblem: 'Décrivez le problème',
      problemPlaceholder: 'Ex. Coupure de courant dans la cuisine, pas de lumière...',
      streetAddress: 'Adresse',
      addressPlaceholder: 'Commencez à saisir votre adresse...',
      city: 'Ville',
      cityPlaceholder: 'Amsterdam',
      postalCode: 'Code Postal',
      postalCodePlaceholder: '1000 AA',
      reviewBooking: 'Vérifier la Réservation',
      confirmBooking: 'Confirmez votre réservation',
      reviewDetails: 'Vérifiez les détails et effectuez le paiement',
      location: 'Emplacement',
      estimatedArrival: 'Arrivée estimée',
      scheduledFor: 'Planifié pour',
      serviceType: 'Type de service',
      pricingModel: 'Modèle de tarification',
      hourlyRateLabel: 'Tarif horaire',
      fixedPriceLabel: 'Prix fixe',
      callOutFee: 'Frais de déplacement',
      hourlyRate: 'Tarif horaire',
      estimatedHours: 'Heures estimées',
      hourlySubtotal: 'Sous-total horaire',
      serviceFee: 'Frais de service',
      emergencySurcharge: 'Supplément urgence',
      included: 'inclus',
      hourlyNote: 'Le prix final sera calculé sur la base des heures réelles travaillées. Vous devrez approuver tout temps supplémentaire.',
      servicePrice: 'Prix du service',
      estimatedTotal: 'Total Estimé',
      total: 'Total',
      finalPriceNote: 'Prix final basé sur les heures réelles',
      escrowProtection: 'Protection Paiement Séquestre',
      escrowDescription: 'Votre paiement est conservé en toute sécurité sous séquestre et versé au technicien seulement 3 jours après la fin des travaux, à condition qu\'aucun litige ne soit ouvert — vous êtes protégé jusqu\'à votre satisfaction.',
      redirectingPayment: 'Redirection vers le paiement...',
      payWithIdeal: 'Payer par iDEAL / Carte',
      signInToBook: 'Connectez-vous pour réserver',
      signInRequired: 'Vous devez être connecté pour réserver un service.',
      estimatedDuration: 'Durée Estimée',
      estimatedDurationDesc: 'Ce service est facturé à l\'heure. Combien de temps estimez-vous la durée du travail ?',
      estimatedHoursLabel: 'Heures estimées',
      hours: 'heures',
      technicianConfirm: 'Le technicien confirmera l\'estimation à son arrivée. Vous devrez approuver tout temps supplémentaire.',
    },
    // Service Landing
    serviceLanding: {
      professional: 'Professionnel',
      services: 'Services',
      availableDesc: 'Réservez des professionnels vérifiés, généralement sur place sous',
      eta: 'Délai',
      warranty: 'Qualité Garantie',
      rating: 'Note',
      book: 'Réserver',
      callNow: 'Appeler',
      startingPrice: 'à partir de',
      perHour: 'par heure',
      callOutFee: 'frais de déplacement',
      serviceFee: 'frais de service',
      features: {
        fixedPricing: 'Tarification fixe et transparente',
        noHiddenFees: 'Pas de frais cachés',
        verifiedTechnicians: 'Techniciens vérifiés et assurés',
        warrantyIncluded: 'Qualité garantie incluse',
        payAfterCompletion: 'Paiement après achèvement',
        emergencySurcharge: 'Supplément urgence',
      },
      ourServices: 'Nos',
      comprehensive: 'Services complets',
      servicesFor: 'pour tous vos besoins',
      whyChoose: 'Pourquoi Choisir Bricos pour',
      fastResponse: 'Réponse Rapide',
      fastResponseDesc: 'Nos techniciens sont envoyés rapidement',
      evenForEmergencies: 'même pour les urgences',
      verifiedExperts: 'Experts Vérifiés',
      verifiedExpertsDesc: 'Tous les techniciens sont vérifiés, certifiés et entièrement assurés.',
      trustedByThousands: 'Prix justes, pros vérifiés',
      trustedByThousandsDesc: 'Réservez en toute confiance — protégé par séquestre du début à la fin.',
      otherServices: 'Autres Services Dont Vous Pourriez Avoir Besoin',
      moreServices: 'Plus de Services',
    },
    // Property Manager Portal
    pm: {
      portal: 'Portail Gestionnaire',
      dashboard: 'Tableau de Bord',
      properties: 'Propriétés',
      contractors: 'Prestataires',
      jobs: 'Travaux',
      bookService: 'Réserver Service',
      disputes: 'Litiges',
      reports: 'Rapports',
      billing: 'Facturation',
      team: 'Équipe',
      settings: 'Paramètres',
      signOut: 'Déconnexion',
      organizations: 'Organisations',
      organizationSettings: 'Paramètres Organisation',
      addProperty: 'Ajouter Propriété',
      bulkBook: 'Réservation Groupée',
      propertyName: 'Nom de la Propriété',
      address: 'Adresse',
      city: 'Ville',
      postalCode: 'Code Postal',
      numberOfUnits: "Nombre d'Unités",
      adding: 'Ajout...',
      totalUnits: 'Total Unités',
      activeJobs: 'Travaux Actifs',
      completed: 'Terminés',
      totalSpent: 'Total Dépensé',
      avgJobCost: 'Coût Moyen',
      monthlySpending: 'Dépenses Mensuelles',
      last6Months: '6 derniers mois',
      jobsByProperty: 'Travaux par Propriété',
      recentJobs: 'Travaux Récents',
      noRecentJobs: 'Aucun travail récent',
      viewAllJobs: 'Voir Tous les Travaux',
      yourProperties: 'Vos Propriétés',
      noProperties: 'Aucune propriété',
      addFirstProperty: 'Ajoutez votre première propriété',
      units: 'unités',
      manageUnits: 'Gérer Unités',
      // Contractor management
      allContractors: 'Tous les Prestataires',
      external: 'Externes',
      platformLinked: 'Liés Plateforme',
      pending: 'En Attente',
      withContract: 'Avec Contrat',
      searchContractors: 'Rechercher prestataires...',
      addContractor: 'Ajouter Prestataire',
      inviteSent: 'Invitation Envoyée',
      accepted: 'Accepté',
      active: 'Actif',
      suspended: 'Suspendu',
      sendInvite: 'Envoyer Invitation',
      resendInvite: 'Renvoyer Invitation',
      uploadContract: 'Télécharger Contrat',
      managePricing: 'Gérer Tarifs',
      manageCoverage: 'Gérer Couverture',
      viewContract: 'Voir Contrat',
      suspend: 'Suspendre',
      reactivate: 'Réactiver',
      noContractors: 'Aucun prestataire',
      addFirstContractor: 'Ajoutez votre premier prestataire',
      // Roles
      owner: 'Propriétaire',
      admin: 'Admin',
      dispatcher: 'Dispatcheur',
      finance: 'Finance',
      readonly: 'Lecture Seule',
      manageDescription: 'Gérez vos propriétés et la maintenance',
      addNewProperty: 'Ajouter Nouvelle Propriété',
      noJobsYet: 'Pas encore de travaux',
      jobReports: 'Rapports de Travaux',
      viewAllReports: 'Voir tous les rapports',
      report: 'Rapport',
      // Billing page
      financeInvoicing: 'Finance & Facturation',
      billingInvoices: 'Facturation & Factures',
      billingSubtitle: 'Gérer les factures et l\'intégration comptable',
      totalInvoices: 'Total Factures',
      paid: 'Payé',
      overdue: 'En retard',
      monthlyInvoicesRevenue: 'Factures & Revenus Mensuels',
      invoiceStatus: 'Statut des Factures',
      invoicesByProperty: 'Factures par Propriété',
      noInvoiceData: 'Aucune donnée de facture',
      noStatusData: 'Aucune donnée de statut',
      allInvoices: 'Toutes les Factures',
      connections: 'Connexions',
      allCaughtUp: 'Tout est à jour !',
      noPendingInvoices: 'Aucune facture en attente',
      noPaidInvoices: 'Aucune facture payée',
      connectedProperties: 'Propriétés Connectées',
      connectNew: 'Nouvelle Connexion',
      loadingConnections: 'Chargement des connexions...',
      noAccountingConnections: 'Aucune connexion comptable',
      connectAccountingDesc: 'Connectez vos propriétés à un logiciel comptable pour synchroniser automatiquement les factures',
      connectAccountingSoftware: 'Connecter Logiciel Comptable',
      connected: 'Connecté',
      lastSync: 'Dernière sync',
      connect: 'Connecter',
      billingPreference: 'Préférence de Facturation',
      monthlyConsolidated: 'Mensuel Consolidé',
      monthlyConsolidatedDesc: 'Une facture pour tous les travaux en fin de mois',
      perJob: 'Par Travail',
      perJobDesc: 'Facture pour chaque travail terminé',
      perBuilding: 'Par Bâtiment',
      perBuildingDesc: 'Facture séparée par propriété',
      accountingIntegration: 'Intégration Comptable',
      connectAccountingAutoSync: 'Connectez votre logiciel comptable pour synchroniser automatiquement les factures',
      supported: 'Supporté',
      configureIntegration: 'Configurer l\'Intégration',
      export: 'Exporter',
      exportDesc: 'Exportez vos factures dans différents formats pour la comptabilité.',
      exportCSV: 'Exporter CSV',
      exportExcel: 'Exporter Excel',
      mt940Format: 'Format MT940',
      mt940Desc: 'Le format MT940 est compatible avec la plupart des systèmes bancaires néerlandais.',
      selectProperty: 'Sélectionner Propriété',
      chooseProperty: 'Choisir une propriété',
      accountingSystem: 'Système Comptable',
      chooseAccountingSoftware: 'Choisir logiciel comptable',
      comingSoon: 'Bientôt',
      afasConfiguration: 'Configuration AFAS',
      afasTokenDesc: 'AFAS utilise l\'authentification par token. Entrez votre token API et clé d\'environnement ci-dessous.',
      apiToken: 'Token API',
      enterAfasToken: 'Entrez votre token API AFAS',
      environmentKey: 'Clé d\'Environnement',
      afasEnvironmentDesc: 'Votre identifiant d\'environnement AFAS (trouvé dans votre portail AFAS)',
      oauthConnectDesc: 'Cliquer sur "Connecter" ouvrira un popup pour autoriser',
      forProperty: 'pour',
      connecting: 'Connexion...',
      cancel: 'Annuler',
      disconnectAccounting: 'Déconnecter Comptabilité',
      disconnectConfirm: 'Êtes-vous sûr de vouloir déconnecter cette intégration comptable ? Les futures factures ne seront plus synchronisées automatiquement.',
      disconnect: 'Déconnecter',
      disconnecting: 'Déconnexion...',
      syncInvoices: 'Synchroniser',
      generateInvoices: 'Générer',
      vsLastMonth: 'vs mois dernier',
      noInvoicesToExport: 'Aucune facture à exporter',
      createInvoicesFirst: 'Créez d\'abord des factures avant d\'exporter.',
      exportComplete: 'Export terminé',
      exportFailed: 'Échec de l\'export',
      noPaidForMT940: 'Aucune facture payée',
      mt940RequiresPaid: 'L\'export MT940 nécessite au moins une facture payée.',
    },
    // Technician Portal
    tech: {
      portal: 'Portail Technicien',
      overview: 'Aperçu',
      activeJobs: 'Travaux Actifs',
      calendar: 'Calendrier',
      earnings: 'Revenus',
      reviews: 'Avis',
      settings: 'Paramètres',
      payoutHistory: 'Historique Paiements',
      signOut: 'Déconnexion',
      online: 'En Ligne',
      offline: 'Hors Ligne',
      available: 'Disponible',
      noTechnicianProfile: 'Pas de Profil Technicien',
      noProfileMessage: "Vous n'avez pas encore de profil technicien actif.",
      goHome: 'Accueil',
      applyAsTechnician: 'Postuler comme Technicien',
      hello: 'Bonjour',
      youreOnline: 'Vous êtes en ligne et prêt pour des travaux',
      youreOffline: 'Vous êtes actuellement hors ligne',
      newJobs: 'nouveaux travaux',
      incomingJobs: 'Travaux Entrants',
      pending: 'En Attente',
      totalEarned: 'Total Gagné',
      completed: 'Terminés',
      thisMonth: 'ce mois',
      rating: 'Note',
      weeklyEarnings: 'Revenus Hebdomadaires',
      last7Days: '7 derniers jours',
      jobsByService: 'Travaux par Service',
      activeJobsTitle: 'Travaux Actifs',
      noActiveJobs: 'Aucun travail actif',
      waitingForJobs: 'En attente de nouvelles offres',
      recentReviews: 'Avis Récents',
      noReviews: 'Aucun avis',
      allReviews: 'Tous les Avis',
      showLess: 'Voir Moins',
      startJob: 'Commencer Travail',
      completeJob: 'Terminer Travail',
      openChat: 'Ouvrir Chat',
      downloadReport: 'Télécharger Rapport',
      requestPause: 'Demander Pause',
      cancelPause: 'Annuler Pause',
      pauseRequested: 'Pause Demandée',
      paused: 'En Pause',
      waitingForJobsDesc: 'Vous serez notifié quand un nouveau travail sera disponible à proximité',
      jobHistory: 'Historique Travaux',
      arrivedStartJob: 'Je suis arrivé - Commencer',
      pausePendingApproval: "Demande de pause en attente d'approbation du client",
      clientConfirmed: 'Client confirmé',
      awaitingClientConfirmation: 'En attente de confirmation client',
      completedJobs: 'Travaux Terminés',
      viewAll: 'Voir tout',
      goOnlineForJobs: 'Passez en ligne pour recevoir des offres',
      disputeStatusPending: "En attente",
      disputeStatusUnderReview: "En cours d'examen",
      disputeStatusAwaitingTechnician: "En attente de votre réponse",
      disputeStatusAwaitingCustomer: "En attente du client",
      disputeStatusResolved: "Résolu",
      disputeStatusEscalated: "Escaladé",
      disputeStatusDismissed: "Rejeté",
      notATechnician: "Pas un technicien",
      needTechProfileDisputes: "Vous avez besoin d'un profil technicien pour voir les litiges.",
      backToDashboard: "Retour au tableau de bord",
      myDisputes: "Mes litiges",
      activeLower: "actif",
      resolvedLower: "résolu",
      noDisputes: "Aucun litige",
      noDisputesMessage: "Beau travail ! Aucun client n'a déposé de litige contre vos interventions.",
      activeDisputes: "Litiges actifs",
      responseRequired: "Réponse requise",
      jobDetails: "Détails de l'intervention",
      customersComplaint: "Réclamation du client",
      customerEvidence: "Preuves du client",
      resolution: "Résolution",
      filed: "Déposé",
      outcome: "Résultat",
      payoutStatusPending: "En attente",
      payoutStatusProcessing: "En traitement",
      payoutStatusCompleted: "Terminé",
      payoutStatusFailed: "Échoué",
      projectStatusNotified: "Nouveau",
      projectStatusAccepted: "Accepté",
      projectStatusDeclined: "Refusé",
      projectStatusFilled: "Pourvu",
      projectStatusExpired: "Expiré",
      projectStart: "Début",
      projectBudget: "Budget",
      photos: "photos",
      acceptAndSubmitQuote: "Accepter & envoyer le devis",
      pass: "Passer",
      submitQuote: "Envoyer un devis",
      dashboard: "Tableau de bord",
      projectOpportunities: "Opportunités de projet",
      projectOpportunitiesSubtitle: "Projets de rénovation et de construction adaptés à vos compétences",
      tabNew: "Nouveau",
      tabAccepted: "Accepté",
      tabPast: "Passés",
      noNewProjects: "Aucune nouvelle opportunité pour le moment. Revenez bientôt !",
      noAcceptedProjects: "Aucun projet accepté pour l'instant.",
      noPastProjects: "Aucune opportunité passée.",
      projectAcceptedToast: "Projet accepté ! Vous pouvez maintenant envoyer votre devis.",
      projectAcceptFailedToast: "Échec de l'acceptation du projet.",
      projectDeclinedToast: "Projet refusé.",
      projectDeclineFailedToast: "Échec du refus du projet.",
      loading: "Chargement...",
    },

    projects: {
      statusDraft: "Brouillon",
      statusMatching: "Mise en relation",
      statusAwaitingQuotes: "En attente de devis",
      statusQuoteAccepted: "Devis accepté",
      statusActive: "Actif",
      statusInProgress: "En cours",
      statusCompleted: "Terminé",
      statusCancelled: "Annulé",
      statusDisputed: "En litige",
      dashboard: "Tableau de bord",
      title: "Mes projets",
      subtitle: "Gérez vos projets de rénovation et de construction",
      newProject: "Nouveau projet",
      emptyTitle: "Aucun projet pour l'instant",
      emptyDescription: "Publiez votre premier projet de rénovation ou de construction et soyez mis en relation avec 3 professionnels vérifiés.",
      postAProject: "Publier un projet",
      notFound: "Projet introuvable.",
      backToProjects: "Retour aux projets",
      posted: "Publié",
      matchedTechnicians: "Vos 3 techniciens sélectionnés",
      waitingForTechnician: "En attente du technicien…",
      slot: "Place",
      of: "sur",
      technician: "Technicien",
      jobs: "interventions",
      quoteSubmitted: "Devis envoyé",
      quotePending: "Devis en attente",
      daysLeftToSubmit: "jours pour soumettre",
      waitingForQuotes: "En attente de devis…",
      compareQuotes: "Comparer les devis",
      received: "reçu(s)",
      tabOverview: "Aperçu",
      tabMessages: "Messages",
      tabDocuments: "Documents",
      projectProgress: "Avancement du projet",
      milestoneDone: "Terminé",
      milestoneUpcoming: "À venir",
      projectDetails: "Détails du projet",
      location: "Emplacement",
      startDate: "Date de début",
      duration: "Durée",
      budget: "Budget",
      noMessages: "Aucun message. Démarrez une conversation !",
      typeMessage: "Écrivez un message…",
      documentsEmpty: "Les documents apparaîtront ici au fur et à mesure du projet.",
      thisProjectIs: "Ce projet est",
      acceptQuote: "Accepter le devis",
      hiringPrefix: "Vous engagez",
      hiringFor: "pour",
      hiringSuffix: "— cela les notifiera immédiatement et créera votre projet. Prêt à continuer ?",
      cancel: "Annuler",
      confirmAndHire: "Confirmer & engager",
      quoteAcceptedToast: "Devis accepté ! Votre projet est maintenant actif.",
      quoteAcceptFailedToast: "Échec de l'acceptation du devis.",
      messageSentToast: "Message envoyé",
      messageSendFailedToast: "Échec de l'envoi du message",
      stepBasics: "Bases du projet",
      stepLocation: "Lieu & calendrier",
      stepBudget: "Budget & vérification",
      back: "Retour",
      next: "Suivant",
      newProjectSubtitle: "Trouvez des professionnels vérifiés pour votre projet de rénovation ou de construction",
      matchingTitle: "Mise en relation de votre projet…",
      matchingSubtitle: "Recherche des meilleurs techniciens de la plateforme pour votre projet",
      findTechnicians: "Trouver mes 3 techniciens",
      fieldTitle: "Titre du projet",
      fieldCategory: "Catégorie",
      fieldDescription: "Description",
      charactersMinimum: "caractères minimum",
      fieldPhotos: "Photos (facultatif, max 8)",
      photosHint: "Aidez les techniciens à comprendre l'ampleur",
      uploading: "Téléversement...",
      dropPhotos: "Déposez les photos ici",
      dragDropPhotos: "Glissez-déposez ou cliquez pour téléverser",
      fieldAddress: "Adresse du bien",
      fieldCity: "Ville",
      fieldPostalCode: "Code postal",
      fieldStartDate: "Date de début souhaitée",
      fieldTimeline: "Flexibilité du calendrier",
      timelineFixed: "Date fixe",
      timelineFlex2Weeks: "Flexible sous 2 semaines",
      timelineFlex1Month: "Flexible sous un mois",
      fieldDuration: "Durée estimée du projet",
      selectDuration: "Choisir la durée",
      fieldBudget: "Fourchette de budget (facultatif)",
      shareWithTechnicians: "Partager avec les techniciens",
      selectBudget: "Choisir la fourchette de budget",
      reviewTitle: "Vérifiez votre projet",
      reviewFieldTitle: "Titre",
      maxPhotosToast: "8 photos maximum autorisées",
      createFailedToast: "Échec de la création du projet. Veuillez réessayer.",
      validationEnterTitle: "Saisissez un titre",
      validationSelectCategory: "Choisissez une catégorie",
      validationDescriptionNeeds: "La description nécessite",
      validationMoreCharacters: "caractères de plus",
      validationEnterAddress: "Saisissez l'adresse",
      validationEnterCity: "Saisissez la ville",
      validationEnterPostalCode: "Saisissez le code postal",
      validationSelectStartDate: "Choisissez la date de début",
      validationSelectDuration: "Choisissez la durée",
      placeholderTitle: "ex. « Rénovation de salle de bain — 2 salles de bain »",
      placeholderDescription: "Décrivez les travaux nécessaires, l'état actuel et toute exigence particulière…",
      placeholderAddress: "Rue + numéro",
      placeholderCity: "Rotterdam",
    },

    referrals: {
      nav: "Inviter des amis",
      title: "Invitez des amis, gagnez des points",
      subtitle: "Partagez Bricos. Quand un ami s'inscrit et termine sa première intervention, vous recevez tous les deux {n} points de fidélité.",
      yourLink: "Votre lien d'invitation",
      linkPending: "Disponible après le lancement",
      copied: "Copié !",
      share: "Partager",
      email: "E-mail",
      yourCode: "Votre code",
      statInvited: "Invités",
      statJoined: "Récompensés",
      statPoints: "Points gagnés",
      howItWorks: "Comment ça marche",
      step1: "Partagez votre lien d'invitation personnel avec vos amis.",
      step2: "Ils s'inscrivent et réservent leur première intervention.",
      step3: "Vous recevez tous les deux {n} points de fidélité une fois terminée.",
      shareMessage: "J'utilise Bricos pour les services à domicile — des pros vérifiés, des prix fixes. Inscrivez-vous via mon lien :",
      dashboardTitle: "Invitez des amis, gagnez des points",
      dashboardSubtitle: "Vous recevez tous les deux 250 points de fidélité quand un ami termine sa première intervention",
    },

    pricingGuide: {
      from: "à partir de",
      perHour: "/heure",
      serviceFee: "frais de service",
      basePrice: "Prix fixe",
      callOutFee: "Frais de déplacement",
      standard: "Standard / planifié",
      emergency: "Urgence",
      emergencySurcharge: "Supplément urgence",
      costOf: "Combien coûte",
      costInNl: "aux Pays-Bas ?",
      heroSub: "De vrais prix fixes — affichés avant de réserver. Pas de devis à chasser, pas de surprises.",
      priceNote: "Les prix sont des prix de départ indicatifs ; le prix fixe exact est confirmé avant la réservation.",
      tp1Title: "Fixe & à l'avance",
      tp1Desc: "Vous voyez le prix avant de réserver — pas après.",
      tp2Title: "Protégé par séquestre",
      tp2Desc: "Nous conservons le paiement jusqu'à ce que le travail soit fait à votre satisfaction.",
      tp3Title: "Vérifiés & assurés",
      tp3Desc: "Chaque pro est vérifié (identité, qualifications, assurance).",
      whatsIncluded: "Ce qui est inclus",
      typicalEta: "Arrivée typique",
      ctaTitle: "Prêt à réserver à prix fixe ?",
      ctaSub: "Voyez votre prix exact et réservez un pro vérifié en quelques clics.",
      bookCta: "Réserver",
      learnMore: "En savoir plus",
      otherPrices: "Prix des autres services",
      hubTitle: "Prix des services à domicile aux Pays-Bas (2026) — fixes & transparents",
      hubHeading: "Combien coûtent les services à domicile aux Pays-Bas ?",
      hubSub: "La plupart des plateformes vous font chasser des devis. Nous publions de vrais prix fixes — vous voyez le prix avant de réserver, et il est conservé en séquestre jusqu'à la fin du travail.",
      howItWorks: "Comment fonctionnent nos prix",
      hp1Title: "Prix fixe + frais forfaitaires",
      hp1Desc: "Un prix de service fixe + des frais forfaitaires de €{fee}. C'est tout.",
      hp2Title: "L'urgence est un supplément fixe",
      hp2Desc: "Besoin urgent ? Un supplément d'urgence clair et fixe — jamais une majoration mystère.",
      hp3Title: "Payé en toute sécurité via séquestre",
      hp3Desc: "Votre argent est conservé jusqu'à ce que le travail soit fait et que vous soyez satisfait.",
      trustLine: "Pros vérifiés et assurés — prix fixes — protégé par séquestre",
      ctaBtn: "Voir le prix & réserver",
    },
    // Admin Portal
    admin: {
      portal: 'Portail Admin',
      dashboard: 'Tableau de Bord',
      bookings: 'Réservations',
      users: 'Utilisateurs',
      technicians: 'Techniciens',
      disputes: 'Litiges',
      reconciliation: 'Réconciliation',
      settings: 'Paramètres',
      signOut: 'Déconnexion',
      totalBookings: 'Total Réservations',
      totalRevenue: 'Revenu Total',
      activeTechnicians: 'Techniciens Actifs',
      openDisputes: 'Litiges Ouverts',
      recentBookings: 'Réservations Récentes',
      manageRoles: 'Gérer Rôles',
      addRole: 'Ajouter Rôle',
      removeRole: 'Supprimer Rôle',
      searchUsers: 'Rechercher utilisateurs...',
      noUsers: 'Aucun utilisateur trouvé',
      userDetails: 'Détails Utilisateur',
      bookingDetails: 'Détails Réservation',
      // Statuses
      pending: 'En Attente',
      confirmed: 'Confirmé',
      technicianAssigned: 'Technicien Assigné',
      inProgress: 'En Cours',
      completed: 'Terminé',
      cancelled: 'Annulé',
      underReview: 'En Examen',
      awaitingTechnician: 'Attente Technicien',
      awaitingCustomer: 'Attente Client',
      resolved: 'Résolu',
      escalated: 'Escaladé',
      acceptanceRate: "Taux d'acceptation",
      activeJobs: "Interventions actives",
      activeUsersPerDay: "Utilisateurs actifs par jour",
      actor: "Acteur",
      addNewRole: "Ajouter un rôle",
      addOrRemoveRoles: "Ajouter ou retirer des rôles pour",
      adminDashboard: "Tableau de bord admin",
      adminNotes: "Notes admin",
      adminNotesOptional: "Notes admin (facultatif)",
      adminNotesPlaceholder: "Notes internes sur cette décision...",
      admins: "Admins",
      admin: "Admin",
      ago: "il y a",
      all: "Tous",
      allClear: "Tout est en ordre",
      allDisputes: "Tous les litiges",
      allRatings: "Toutes les notes",
      allRoles: "Tous les rôles",
      allStatuses: "Tous les statuts",
      allTechnicians: "Tous les techniciens",
      annualPlans: "Forfaits annuels",
      appliedOn: "Postulé le",
      appliedRecently: "Postulé récemment",
      approve: "Approuver",
      approved: "Approuvé",
      approvedOnly: "Approuvés uniquement",
      approveTechDesc: "pourra recevoir et accepter des offres d'intervention.",
      approveTechnicianTitle: "Approuver le technicien ?",
      assigned: "Attribué",
      available: "Disponible",
      averageRating: "Note moyenne",
      awaitingResponse: "En attente de réponse",
      awaitingTech: "Attente technicien",
      booking: "Réservation",
      bookingId: "ID réservation",
      bookingsOverTime: "Réservations dans le temps",
      businessDetails: "Détails de l'entreprise",
      chatMessages: "Messages du chat",
      clear: "Effacer",
      clearDates: "Effacer les dates",
      clearFilters: "Effacer les filtres",
      completedJobs: "Interventions terminées",
      connected: "Connecté",
      contribution: "Contribution",
      costPerBooking: "Coût par réservation (CAC, est.)",
      created: "Créé",
      criticalIssues: "Problèmes critiques",
      currentRoles: "Rôles actuels",
      customer: "Client",
      customerEvidence: "Preuves du client",
      customerStatement: "Déclaration du client",
      dashboardLoadError: "Échec du chargement du tableau de bord",
      decision: "Décision",
      deleteReview: "Supprimer l'avis",
      deleteReviewTitle: "Supprimer cet avis ?",
      demand: "DEMANDE",
      discrepancies: "Écarts",
      dismissDispute: "Rejeter le litige",
      dispute: "Litige",
      disputeResolution: "Résolution du litige",
      emergency: "Urgence",
      escalate: "Escalader",
      event: "Événement",
      filterByRole: "Filtrer par rôle",
      filterByStatus: "Filtrer par statut",
      filteredBookings: "Réservations filtrées",
      fullRefund: "Remboursement intégral au client",
      grossMarginJob: "Marge brute/intervention",
      issue: "Problème",
      jobOffersVsAccepted: "Offres vs acceptées",
      jobReport: "Rapport d'intervention",
      jobs: "interventions",
      joined: "Inscrit le",
      last100Checked: "100 derniers vérifiés",
      loadingDashboard: "Chargement du tableau de bord...",
      location: "Emplacement",
      lowRatings: "Notes basses (≤2)",
      manageBookings: "Voir et gérer toutes les réservations",
      manageDisputes: "Gérer et résoudre les litiges clients",
      manageUserRoles: "Gérer les rôles utilisateurs",
      manageUsers: "Gérer les utilisateurs et leurs rôles",
      method: "Méthode",
      metrics: "Métriques",
      moderateReviews: "Surveiller et modérer les avis",
      mollieStatus: "Statut Mollie",
      noBookings: "Aucune réservation trouvée",
      noBookingsDesc: "Il n'y a encore aucune réservation sur la plateforme.",
      noBookingsMatch: "Aucune réservation ne correspond à vos filtres",
      noBookingsMatchDesc: "Essayez d'ajuster votre recherche ou vos filtres.",
      noBookingsYet: "Aucune réservation",
      noDescription: "Aucune description fournie",
      noDiscrepancies: "Aucun écart de paiement trouvé",
      noDisputes: "Aucun litige trouvé",
      noDisputesDesc: "Aucun litige ne correspond à ce filtre.",
      noEvents: "Aucun événement",
      noEventsDesc: "Aucun événement de paiement enregistré",
      noResponse: "Aucune réponse",
      noResponseYet: "Pas encore de réponse",
      noReviews: "Aucun avis trouvé",
      noReviewsDesc: "Aucun avis ne correspond à vos critères.",
      noRoles: "Aucun rôle",
      noRolesAssigned: "Aucun rôle attribué",
      notConfigured: "Non configuré",
      noTechnicians: "Aucun technicien trouvé",
      noTechniciansDesc: "Aucune candidature ne correspond à ce filtre pour l'instant.",
      notSet: "Non défini",
      noUsersDesc: "Aucun utilisateur ne correspond à vos critères.",
      orExactAmount: "Ou saisir le montant exact",
      overdue: "En retard",
      overdueSla: "SLA dépassé",
      partialRefund: "Remboursement partiel",
      paymentDiscrepancies: "Écarts de paiement",
      paymentDiscrepanciesDesc: "Paiements avec statut différent entre Mollie et la base de données",
      paymentEventLedger: "Journal des paiements",
      paymentEventLedgerDesc: "Transitions et actions de paiement récentes",
      paymentId: "ID paiement",
      paymentReconciliation: "Rapprochement des paiements",
      payments: "Paiements",
      paymentSplit: "Répartition du paiement",
      pendingReview: "En attente d'examen",
      pendingVerification: "En attente de vérification",
      phone: "Téléphone",
      platformMarginOverTime: "Marge de la plateforme dans le temps",
      platformOverview: "Aperçu et métriques de la plateforme",
      price: "Prix",
      pricing: "Tarification",
      problemDetails: "Détails du problème",
      problemPhotos: "Photos du problème",
      propertyManager: "Gestionnaire immobilier",
      propertyMgrs: "Gestionnaires",
      rating: "Note",
      recentEvents: "Événements récents",
      reconciliationDesc: "Surveiller et résoudre les écarts de paiement entre Mollie et la base de données",
      reconsider: "Reconsidérer",
      refreshPage: "Actualiser la page",
      reject: "Refuser",
      rejectApplicationTitle: "Refuser la candidature ?",
      rejected: "Refusé",
      rejectedOn: "Refusé le",
      rejectionReason: "Motif du refus",
      rejectionReasonPlaceholder: "Expliquez pourquoi la candidature est refusée...",
      rejectionReasonRequired: "Motif du refus *",
      rejectTechDesc: "sera informé que sa candidature a été refusée.",
      releaseToTechnician: "Verser au technicien",
      remaining: "restant",
      remove: "Retirer",
      repeatRate: "Taux de répétition",
      replied: "Répondu",
      requestEvidence: "Demander des preuves",
      requestMoreEvidence: "Demander plus de preuves",
      requestResponse: "Demander une réponse",
      requireImmediateAction: "Nécessite une action immédiate",
      resolution: "Résolution",
      resolutionNotesLabel: "Notes de résolution (visibles par le client)",
      resolveExecute: "Résoudre & exécuter le paiement",
      respondedOn: "Répondu le",
      response: "Réponse",
      retention: "RÉTENTION",
      review: "Avis",
      reviewApplications: "Examiner et approuver les candidatures de techniciens",
      reviewDetails: "Détails de l'avis",
      reviews: "Avis",
      revoke: "Révoquer",
      reworkRequired: "Reprise requise",
      roles: "Rôles",
      saveNotes: "Enregistrer les notes",
      searchBookingsPlaceholder: "Rechercher par client, technicien, adresse, ID...",
      searching: "Recherche",
      searchReviewsPlaceholder: "Rechercher par client, technicien ou texte...",
      searchUsersPlaceholder: "Rechercher par nom ou téléphone...",
      selectDateRange: "Choisir une période",
      selectResolution: "Choisir une résolution",
      service: "Service",
      servicesOffered: "Services proposés",
      shouldBeReviewed: "À examiner",
      showing: "Affichage",
      sla: "SLA",
      slaBreached: "SLA non respecté",
      slaDeadline: "Échéance SLA",
      slowConnectionRetry: "Cela peut être dû à une connexion lente. Veuillez réessayer.",
      splitPayment: "Diviser le paiement",
      supply: "OFFRE",
      technician: "Technicien",
      technicianEvidence: "Preuves du technicien",
      technicianResponse: "Réponse du technicien",
      techResponded: "Technicien a répondu",
      techResponses: "Réponses techniciens",
      timeline: "Chronologie",
      totalPayments: "Total des paiements",
      totalReviews: "Total des avis",
      totalTechnicians: "Total des techniciens",
      totalUsers: "Total des utilisateurs",
      tryAgain: "Réessayer",
      twoPlusBookings: "2+ réservations",
      unavailable: "Indisponible",
      unitEconomics: "ÉCONOMIE UNITAIRE",
      user: "Utilisateur",
      userManagement: "Gestion des utilisateurs",
      verifiedTechs: "Techniciens vérifiés",
      warnings: "Avertissements",
      withResponse: "Avec réponse",
      technicianApproved: "Technicien approuvé",
      applicationRejected: "Candidature refusée",
      technicianApprovedDesc: "Le technicien peut désormais recevoir des offres",
      applicationRejectedDesc: "Le technicien a été informé du refus",
      rejectionReasonRequiredTitle: "Motif du refus requis",
      rejectionReasonRequiredDesc: "Veuillez indiquer un motif de refus de cette candidature",
      unknown: "Inconnu",
      notProvided: "Non fourni",
      kvk: "KVK",
      vat: "TVA",
      company: "Entreprise",
      discMolliePaidDbPending: "Mollie payé, BD en attente",
      discEscrowNotReleased: "Séquestre non versé",
      discMollieRefundedDbNot: "Mollie remboursé, BD non",
      discDbRefundedMollieNot: "BD remboursé, Mollie non",
      discPaymentFailedStuck: "Paiement échoué/bloqué",
      discNoMollieId: "Aucun ID Mollie",
      releaseEscrow: "Verser le séquestre",
      releaseEscrowDescStart: "Ceci versera",
      releaseEscrowDescEnd: "au technicien. Cette action est irréversible. Êtes-vous sûr ?",
      releasePayment: "Verser le paiement",
      disputeResolvedToast: "Litige résolu et action de paiement exécutée",
      resolveDisputeFailed: "Échec de la résolution du litige",
      evidenceRequestedFrom: "Preuves demandées à",
      notesSaved: "Notes enregistrées",
      disputeEscalated: "Litige escaladé",
      unassigned: "Non attribué",
      problem: "Problème",
      diagnosis: "Diagnostic",
      technicianNotes: "Notes du technicien",
      partsUsed: "Pièces utilisées",
      before: "Avant",
      after: "Après",
      disputeNotesPlaceholder: "Notes internes sur ce litige...",
      explainDecisionPlaceholder: "Expliquez la décision...",
      customerRefund: "Remboursement client",
      split: "Répartir",
      reviewDeleted: "Avis supprimé",
      deleteReviewFailed: "Échec de la suppression de l'avis",
      unknownTechnician: "Technicien inconnu",
      noText: "Aucun texte",
      noReviewText: "Aucun texte d'avis fourni",
      star: "Étoile",
      stars: "Étoiles",
      deleteReviewConfirmStart: "Ceci supprimera définitivement cet avis de",
      deleteReviewConfirmMid: "étoile(s) par",
      deleteReviewConfirmEnd: "Cette action est irréversible.",
      roleAdded: "Rôle ajouté avec succès",
      addRoleFailed: "Échec de l'ajout du rôle",
      roleRemoved: "Rôle retiré avec succès",
      removeRoleFailed: "Échec du retrait du rôle",
      userAlreadyHasRole: "L'utilisateur a déjà ce rôle",
      unnamedUser: "Utilisateur sans nom",
      thisUser: "cet utilisateur",
      autoReleaseHours: "Heures avant versement auto",
      autoReleaseHoursDesc: "Heures après la fin avant le versement automatique du séquestre (sans litige)",
      disputeWindow: "Fenêtre de litige",
      disputeWindowDesc: "Heures après la fin pendant lesquelles les utilisateurs peuvent ouvrir un litige",
      disputeSla: "SLA litige",
      disputeSlaDesc: "Délai cible de résolution des litiges",
      emergencySearchTimeout: "Délai recherche urgence",
      emergencySearchTimeoutDesc: "Minutes de recherche d'un technicien avant remboursement auto (urgences)",
      scheduledSearchTimeout: "Délai recherche planifiée",
      scheduledSearchTimeoutDesc: "Heures avant l'heure planifiée pour lancer la recherche / délai",
      technicianOfferTimeout: "Délai d'offre technicien",
      technicianOfferTimeoutDesc: "Secondes dont dispose un technicien pour accepter une offre",
      noShowGracePeriod: "Délai de grâce absence",
      noShowGracePeriodDesc: "Minutes après l'ETA avant que le technicien puisse marquer le client comme absent",
      unitHours: "heures",
      unitMinutes: "minutes",
      unitSeconds: "secondes",
      platformSettings: "Paramètres de la plateforme",
      settingsDescription: "Configurez les minuteurs, les SLA et lancez les tests de sécurité",
      timerConfiguration: "Configuration des minuteurs",
      timerConfigDescription: "Configurez les minuteurs pour le séquestre, les litiges et l'attribution des techniciens",
      saveChanges: "Enregistrer",
      reset: "Réinitialiser",
      securityTests: "Tests de sécurité",
      securityTestsDescription: "Lancer les tests automatisés de vérification RLS (Row Level Security)",
      rlsTestsVerifyIsolation: "Les tests RLS vérifient l'isolation des données",
      rlsTestsWarning: "Ces tests garantissent que les utilisateurs non authentifiés ne peuvent pas accéder aux données sensibles. À lancer avant chaque déploiement en production.",
      runRlsTests: "Lancer les tests RLS",
      allTestsPassed: "Tous les tests réussis",
      vulnerabilitiesDetected: "Vulnérabilités détectées",
      passed: "réussis",
      failed: "échoués",
      of: "sur",
      tests: "tests",
      rlsTestsPassed: "Tests RLS réussis",
      rlsTestsFailedReview: "Tests RLS échoués - examinez les résultats",
      failedToRunTests: "Échec de l'exécution des tests",
      failedToLoadSettings: "Échec du chargement des paramètres",
      settingsSavedSuccessfully: "Paramètres enregistrés avec succès",
      failedToSaveSettings: "Échec de l'enregistrement des paramètres",
    },
    // Common UI elements
    ui: {
      loading: 'Chargement...',
      error: 'Erreur',
      success: 'Succès',
      save: 'Sauvegarder',
      cancel: 'Annuler',
      confirm: 'Confirmer',
      delete: 'Supprimer',
      edit: 'Modifier',
      view: 'Voir',
      close: 'Fermer',
      back: 'Retour',
      next: 'Suivant',
      previous: 'Précédent',
      search: 'Rechercher',
      filter: 'Filtrer',
      sort: 'Trier',
      export: 'Exporter',
      import: 'Importer',
      download: 'Télécharger',
      upload: 'Envoyer',
      refresh: 'Actualiser',
      retry: 'Réessayer',
      noData: 'Aucune donnée',
      noResults: 'Aucun résultat',
      selectAll: 'Tout Sélectionner',
      deselectAll: 'Tout Désélectionner',
      actions: 'Actions',
      status: 'Statut',
      date: 'Date',
      time: 'Heure',
      amount: 'Montant',
      total: 'Total',
      subtotal: 'Sous-total',
      tax: 'Taxe',
      fee: 'Frais',
      discount: 'Remise',
      description: 'Description',
      notes: 'Notes',
      details: 'Détails',
      information: 'Information',
      contact: 'Contact',
      phone: 'Téléphone',
      email: 'Email',
      name: 'Nom',
      address: 'Adresse',
      city: 'Ville',
      country: 'Pays',
      postalCode: 'Code Postal',
      today: "Aujourd'hui",
      yesterday: 'Hier',
      thisWeek: 'Cette Semaine',
      thisMonth: 'Ce Mois',
      lastMonth: 'Mois Dernier',
      year: 'Année',
      all: 'Tout',
      none: 'Aucun',
      yes: 'Oui',
      no: 'Non',
      or: 'ou',
      and: 'et',
    },
    serviceNames: {
      plumbing: 'Plomberie',
      electrician: 'Électricien',
      hvac: 'Climatisation',
      boiler: 'Chauffagiste',
      locksmith: 'Serrurier',
      appliance: 'Électroménager',
      roofing: 'Toiture',
      cleaning: 'Nettoyage',
      painting: 'Peinture',
      flooring: 'Revêtement de Sol',
      security: 'Sécurité',
      outdoor: 'Jardin & Extérieur',
      pest_control: 'Dératisation',
      renovation: 'Rénovation',
      handyman: 'Bricoleur',
      moving: 'Déménagement',
      smart_home: 'Maison Connectée',
      sustainable: 'Habitat Durable',
      other: 'Autres Services',
    },
    popularActivities: {
      emergencyPlumbing: 'Réparation plomberie urgente',
      lockReplacement: 'Remplacement de serrure',
      emergencyElectrical: 'Réparation électrique urgente',
      boilerRepair: 'Réparation/installation chaudière',
      drainUnclogging: 'Débouchage de canalisation',
      emergencyLocksmith: 'Serrurier d\'urgence',
    },
    supportForm: {
      contactSupport: 'Contacter le Support',
      contactDesc: 'Envoyez-nous un message et nous vous répondrons sous 24 heures.',
      name: 'Nom',
      namePlaceholder: 'Votre nom',
      email: 'Email',
      emailPlaceholder: 'vous@exemple.com',
      subject: 'Sujet',
      subjectPlaceholder: 'Comment pouvons-nous vous aider ?',
      message: 'Message',
      messagePlaceholder: 'Décrivez votre problème ou question en détail...',
      sendMessage: 'Envoyer le Message',
      messageSent: 'Message Envoyé !',
      messageSentDesc: 'Nous vous répondrons sous 24 heures.',
      thankYou: 'Merci de nous avoir contactés. Nous vous répondrons sous 24 heures.',
      sendAnother: 'Envoyer un Autre Message',
      sendFailed: 'Échec de l\'envoi',
      sendFailedDesc: 'Veuillez réessayer ou nous envoyer un email directement.',
      needHelp: 'Besoin d\'Aide ?',
      needHelpDesc: 'Une question ou un problème ? Envoyez-nous un message et nous vous répondrons sous 24 heures.',
    },
    chatbot: {
      assistant: 'Assistant Bricos',
      greeting: 'Bonjour ! Je suis l\'Assistant Bricos. Je peux répondre à vos questions sur nos services, réservations, paiements et plus encore. Comment puis-je vous aider ?',
      askPlaceholder: 'Posez une question...',
      quickQuestions: 'Questions rapides :',
      relatedQuestions: 'Questions connexes :',
      errorMessage: 'Désolé, j\'ai des difficultés de connexion. Veuillez réessayer ou visitez notre page d\'Aide pour les FAQ.',
      q1: 'À quelle vitesse un technicien peut-il arriver ?',
      q2: 'Comment fonctionne le paiement séquestre ?',
      q3: 'Que faire si je ne suis pas satisfait du travail ?',
      q4: 'Comment annuler une réservation ?',
    },
    techSidebar: {
      disputes: 'Litiges',
    },
    disputesPage: {
      myDisputes: 'Mes Litiges',
      disputeCount: 'litige(s)',
      noDisputes: 'Aucun Litige',
      allCompleted: 'Tous vos travaux ont été complétés avec succès.',
      backToDashboard: 'Retour au Tableau de Bord',
      bookingRef: 'Réservation',
      fileDispute: 'Signaler un Litige',
    },
    disputeDetail: {
      title: 'Détails du Litige',
      notFound: 'Litige Non Trouvé',
      backToDisputes: 'Retour aux Litiges',
      disputeInfo: 'Informations du Litige',
      reason: 'Raison',
      description: 'Description',
      filedOn: 'Déposé le',
      service: 'Service',
      relatedBooking: 'Réservation Associée',
      amount: 'Montant',
      yourEvidence: 'Vos Preuves',
      noPhotos: 'Aucune photo soumise',
      technicianResponse: 'Réponse du Technicien',
      resolution: 'Résolution',
      decision: 'Décision',
      notes: 'Notes',
      refund: 'Remboursement',
      addEvidence: 'Ajouter des Preuves Supplémentaires',
      addEvidenceDesc: 'Vous pouvez soumettre des informations ou photos supplémentaires pour appuyer votre demande.',
      additionalInfo: 'Informations Supplémentaires',
      additionalInfoPlaceholder: 'Fournissez des détails supplémentaires...',
      additionalPhotos: 'Photos Supplémentaires',
      submitEvidence: 'Soumettre les Preuves',
      resolutionExpected: 'Résolution attendue pour le',
      pendingReview: 'En Attente de Révision',
      underReview: 'En Cours de Révision',
      awaitingResponse: 'En Attente de Votre Réponse',
      awaitingTechnician: 'En Attente du Technicien',
      escalated: 'Escaladé',
      resolved: 'Résolu',
    },
    supportPage: {
      title: 'Support',
      subtitle: 'Obtenez de l\'aide pour vos réservations',
      yourTickets: 'Vos Tickets',
      selectTicket: 'Sélectionner un ticket',
      selectTicketDesc: 'Choisissez un ticket dans la liste ou créez-en un nouveau',
    },
    helpPage: {
      title: 'Centre d\'Aide',
      subtitle: 'Trouvez des réponses à vos questions ou contactez notre équipe',
      aiAssistant: 'Assistant IA',
      aiAssistantDesc: 'Cliquez sur le bouton chat en bas à droite pour des réponses instantanées',
      emergencyLine: 'Aide pour réservations urgentes',
      emergencyLineDesc: 'Pour les problèmes urgents, contactez-nous et nous traiterons votre demande en priorité',
      responseTime: 'Temps de Réponse',
      responseTimeDesc: 'Nous répondons généralement à toutes les demandes sous 24 heures',
      faq: 'Questions Fréquentes',
      booking: 'Réservations',
      payments: 'Paiements',
      technicians: 'Techniciens',
      accountCategory: 'Compte',
      stillNeedHelp: 'Encore besoin d\'aide ?',
      stillNeedHelpDesc: 'Vous ne trouvez pas ce que vous cherchez ? Envoyez-nous un message.',
    },
    trackingExtra: {
      lookingForTechnician: 'Recherche d\'un Technicien',
      lookingForTechnicianDesc: 'Nous recherchons le meilleur technicien disponible. Vous serez notifié dès qu\'un technicien accepte.',
      bookingCreated: 'Réservation créée',
      cancelBooking: 'Annuler la Réservation',
      cancelFailed: 'Échec de l\'annulation',
      bookingCancelled: 'Réservation annulée',
    },
    payoutsPage: {
      title: 'Historique des Paiements',
      available: 'Disponible',
      clearedWindow: 'Période de litige écoulée',
      inHold: 'En Attente',
      disputeWindow: ' jours de période de litige',
      totalEarned: 'Total Gagné',
      nextPayout: 'Prochain paiement : Chaque lundi',
      noPayouts: 'Aucun paiement',
      platformFee: 'Frais de plateforme',
    },
    techExtra: {
      inDisputeWindow: 'En Période de Litige',
      dayHold: ' jours de rétention',
      availableForPayout: 'Disponible',
    },
    pmProperties: {
      propertyManagement: 'Gestion Immobilière',
      propertiesManaged: 'propriétés gérées',
      bulkUpload: 'Import en Masse',
      noPropertiesYet: 'Aucune propriété',
      addFirstProperty: 'Ajoutez votre première propriété pour commencer',
    },
    pmJobs: {
      jobManagement: 'Gestion des Travaux',
      activeCount: 'actifs',
      completedCount: 'terminés',
      escalatedCount: 'escaladés',
      refresh: 'Actualiser',
      allStatus: 'Tous les Statuts',
      dispatch: 'Envoyer',
      autoDispatch: 'Envoi auto (Registre + Réseau)',
      reassign: 'Réassigner',
      contact: 'Contacter',
      track: 'Suivre',
      bookNewJob: 'Nouveau Travail',
      noActiveJobs: 'Aucun travail actif',
      noActiveJobsDesc: 'Tous les travaux sont terminés ou aucun ne correspond à votre filtre',
      noCompletedJobs: 'Aucun travail terminé',
      noCompletedJobsDesc: 'Les travaux apparaîtront ici une fois terminés',
      slaBreached: 'SLA Dépassé',
      remaining: 'restant',
      escalation: 'Escalade',
    },
    pmReports: {
      analyticsReports: 'Analyses & Rapports',
      analyticsSubtitle: 'Analyses, performance prestataires et suivi SLA',
      exportAll: 'Tout Exporter',
      exporting: 'Export...',
      totalSpent: 'Total Dépensé',
      completedJobs: 'Travaux Terminés',
      avgJobCost: 'Coût Moyen',
      monthlySpendingJobs: 'Dépenses & Travaux Mensuels',
      jobsByService: 'Travaux par Service',
      noSpendingData: 'Aucune donnée de dépenses',
      noServiceData: 'Aucune donnée de service',
      vsLastMonth: 'vs mois dernier',
      overview: 'Aperçu',
      contractors: 'Prestataires',
      sla: 'SLA',
      costs: 'Coûts',
      spendingByProperty: 'Dépenses par Propriété',
      jobReports: 'Rapports de Travaux',
      searchReports: 'Rechercher rapports...',
      noCompletedJobsReport: 'Aucun travail terminé à rapporter',
      downloadReport: 'Télécharger Rapport',
      technicianReports: 'Rapports Techniciens',
      noMatchingReports: 'Aucun rapport correspondant',
      adjustSearch: 'Ajustez votre recherche',
      reportsAfterCompletion: 'Les rapports apparaîtront ici après les travaux',
      generating: 'Génération...',
      report: 'Rapport',
      jobs: 'travaux',
      matchingOf: 'de',
    },
    pmSettings: {
      title: 'Paramètres',
      manageOrgPrefs: 'Gérez votre organisation et vos préférences',
      organization: 'Organisation',
      team: 'Équipe',
      security: 'Sécurité',
      auditLog: 'Journal d\'Audit',
      preferences: 'Préférences',
      orgDetails: 'Détails de l\'Organisation',
      companyInfo: 'Informations de votre entreprise',
      companyName: 'Nom de l\'Entreprise',
      billingEmail: 'E-mail de Facturation',
      phone: 'Téléphone',
      address: 'Adresse',
      city: 'Ville',
      postalCode: 'Code Postal',
      saveChanges: 'Enregistrer',
      saving: 'Enregistrement...',
      defaultSettings: 'Paramètres par Défaut',
      defaultSettingsDesc: 'Définir les valeurs par défaut pour les nouvelles propriétés et réservations',
      autoAssign: 'Assigner automatiquement le technicien le plus proche',
      autoAssignDesc: 'Sélectionner automatiquement le technicien disponible le plus proche',
      emergencyDefault: 'Mode urgence par défaut',
      emergencyDefaultDesc: 'Toutes les nouvelles réservations commencent en urgence',
      defaultResponseSLA: 'SLA de réponse par défaut',
      defaultResponseSLADesc: 'Temps de réponse cible pour les techniciens',
      reportSettings: 'Paramètres des Rapports',
      reportSettingsDesc: 'Configurer la génération et l\'envoi des rapports',
      includePhotos: 'Inclure les photos dans les rapports',
      includePhotosDesc: 'Joindre les photos avant/après aux rapports',
      autoEmailReports: 'Envoyer les rapports par e-mail au propriétaire',
      autoEmailReportsDesc: 'Envoyer les rapports terminés au propriétaire',
      reportFormat: 'Format de rapport',
      reportFormatDesc: 'Format d\'export par défaut',
    },
    pmDisputes: {
      disputeManagement: 'Gestion des Litiges',
      title: 'Litiges',
      subtitle: 'Suivez et surveillez les résolutions de litiges pour vos propriétés',
      totalDisputes: 'Total Litiges',
      pending: 'En Attente',
      underReview: 'En Examen',
      resolved: 'Résolu',
      avgResolution: 'Délai Moyen',
      customerFavor: 'Faveur Client',
      pastSLADeadline: 'Délai SLA Dépassé',
      pastSLADeadlineDesc: 'Ces litiges ont dépassé le délai de résolution et peuvent nécessiter une escalade.',
      searchPlaceholder: 'Rechercher par raison, propriété ou adresse...',
      filterByStatus: 'Filtrer par statut',
      allStatuses: 'Tous les Statuts',
      awaitingTech: 'Attente Technicien',
      awaitingCustomer: 'Attente Client',
      escalated: 'Escaladé',
      active: 'Actif',
      noActiveDisputes: 'Aucun Litige Actif',
      allResolved: 'Tous les litiges ont été résolus.',
      noSearchMatch: 'Aucun litige ne correspond à vos critères.',
      noResolvedDisputes: 'Aucun Litige Résolu',
      resolvedWillAppear: 'Les litiges résolus apparaîtront ici.',
      loadingDisputes: 'Chargement des litiges...',
      noOrgSelected: 'Aucune Organisation Sélectionnée',
      selectOrgDesc: 'Veuillez sélectionner une organisation pour voir les litiges.',
      noDescription: 'Aucune description fournie',
      techResponded: 'Technicien a Répondu',
      overdue: 'En retard',
      due: 'Échéance',
    },
    pmContractors: {
      contractorHub: 'Hub Prestataires',
      title: 'Prestataires',
      subtitle: 'Gérez votre réseau de prestataires, contrats et approbations',
      approvals: 'Approbations',
    },
    serviceCategories: {
      plumbing: {
        label: 'Plomberie & Eau',
        description: 'Plomberie d\'urgence, réparation de fuites, débouchage et plus',
        eta: '45-90 min',
        activities: ['Réparation plomberie urgente', 'Détection et réparation de fuites', 'Débouchage de canalisation', 'Nettoyage des égouts', 'Installation/réparation chauffe-eau', 'Remplacement de tuyaux', 'Installation sanitaire cuisine/salle de bain'],
      },
      electrician: {
        label: 'Électricien',
        description: 'Réparations électriques, installations et inspections de sécurité',
        eta: '30-60 min',
        activities: ['Réparation électrique urgente', 'Installation/réparation câblage', 'Remplacement disjoncteur', 'Installation éclairage', 'Installation appareil domotique', 'Installation/maintenance générateur', 'Inspection sécurité électrique', 'Installation maison connectée', 'Réparation téléphone', 'Réparation ordinateur'],
      },
      hvac: {
        label: 'Climatisation',
        description: 'Services de chauffage, ventilation et climatisation',
        eta: '60-120 min',
        activities: ['Réparation/installation chaudière', 'Entretien radiateur', 'Installation/réparation climatisation', 'Nettoyage système de ventilation', 'Installation thermostat', 'Service pompe à chaleur', 'Installation isolation', 'Installation poêle'],
      },
      boiler: {
        label: 'Réparation Chaudière',
        description: 'Réparation experte de chaudière et chauffage central',
        eta: '45-90 min',
        activities: ['Réparation/installation chaudière', 'Entretien radiateur', 'Réparation chauffage central', 'Installation thermostat', 'Détection fuite de gaz', 'Réparation tuyaux', 'Chauffage au sol'],
      },
      appliance: {
        label: 'Électroménager',
        description: 'Réparation et installation d\'appareils électroménagers',
        eta: '60-120 min',
        activities: ['Réparation machine à laver', 'Réparation lave-vaisselle', 'Réparation réfrigérateur/congélateur', 'Réparation four/cuisinière', 'Réparation sèche-linge', 'Installation d\'appareils'],
      },
      cleaning: {
        label: 'Nettoyage & Entretien',
        description: 'Nettoyage en profondeur, tapis et services d\'entretien',
        eta: 'Même jour',
        activities: ['Nettoyage en profondeur', 'Nettoyage de tapis', 'Nettoyage de vitres', 'Vitrier', 'Nettoyage de gouttières', 'Nettoyage haute pression (terrasse, allée)', 'Ramonage de cheminée', 'Nettoyage après rénovation'],
      },
      painting: {
        label: 'Peinture & Décoration',
        description: 'Peinture intérieure et extérieure, papier peint et plâtrage',
        eta: '1-2 jours',
        activities: ['Peinture intérieure', 'Peinture extérieure', 'Pose/retrait papier peint', 'Tapissier', 'Plâtrage et réparation murs', 'Plâtrier', 'Finitions décoratives'],
      },
      flooring: {
        label: 'Revêtement de Sol',
        description: 'Carrelage, parquet, stratifié et pose de moquette',
        eta: '1-3 jours',
        activities: ['Pose/réparation carrelage', 'Carreleur', 'Pose/rénovation parquet', 'Pose de stratifié', 'Pose de moquette', 'Spécialiste revêtement de sol'],
      },
      locksmith: {
        label: 'Serrurier',
        description: 'Remplacement de serrure, ouverture d\'urgence et sécurité',
        eta: '20-45 min',
        activities: ['Remplacement de serrure', 'Serrurier d\'urgence', 'Reproduction de clés', 'Installation de serrure', 'Ouverture de coffre-fort', 'Ouverture de voiture', 'Améliorations sécurité', 'Systèmes passe-partout'],
      },
      security: {
        label: 'Sécurité',
        description: 'Caméras de surveillance, systèmes d\'alarme et serrures connectées',
        eta: 'Même jour',
        activities: ['Remplacement de serrure', 'Serrurier d\'urgence', 'Installation caméra de surveillance', 'Installation système d\'alarme', 'Installation serrure connectée'],
      },
      outdoor: {
        label: 'Jardin & Extérieur',
        description: 'Entretien pelouse, élagage, clôtures et construction extérieure',
        eta: 'Même jour',
        activities: ['Entretien et tonte de pelouse', 'Élagage/abattage d\'arbres', 'Installation/réparation de clôture', 'Construction terrasse/deck', 'Déneigement', 'Jardinier', 'Installateur store', 'Spécialiste façade'],
      },
      pest_control: {
        label: 'Dératisation',
        description: 'Extermination de rongeurs, insectes, termites et punaises',
        eta: 'Même jour',
        activities: ['Extermination de rongeurs', 'Extermination d\'insectes', 'Traitement termites', 'Élimination punaises de lit'],
      },
      renovation: {
        label: 'Rénovation & Construction',
        description: 'Rénovation cuisine, salle de bain, toiture et plus',
        eta: 'Variable',
        activities: ['Rénovation cuisine', 'Cuisiniste', 'Rénovation salle de bain', 'Aménagement sous-sol', 'Réparation/remplacement toiture', 'Couvreur', 'Pose de plaques de plâtre', 'Maçon', 'Charpentier', 'Carreleur', 'Architecte', 'Vitrier'],
      },
      other: {
        label: 'Autres Services',
        description: 'Services de bricolage, montage de meubles et travaux spécialisés',
        eta: 'Variable',
        activities: ['Services de bricolage', 'Aide à domicile', 'Montage de meubles', 'Audit énergétique', 'Installation panneaux solaires', 'Restauration dégâts des eaux', 'Traitement des moisissures', 'Restauration après incendie', 'Service de devis'],
      },
      handyman: {
        label: 'Bricoleur',
        description: 'Réparations générales, installations et petits travaux',
        eta: 'Même jour',
        activities: ['Réparations générales', 'Montage de meubles', 'Accrochage de tableaux', 'Installation d\'étagères', 'Réparations de portes', 'Petite menuiserie', 'Divers travaux'],
      },
      moving: {
        label: 'Déménagement',
        description: 'Déménagement professionnel, emballage et relocalisation',
        eta: 'Planifié',
        activities: ['Déménagement résidentiel', 'Déménagement de bureau', 'Service d\'emballage', 'Transport de meubles', 'Déménagement de piano', 'Solutions de stockage'],
      },
      smart_home: {
        label: 'Maison Connectée',
        description: 'Installation domotique, automatisation et configuration tech',
        eta: 'Même jour',
        activities: ['Installation thermostat connecté', 'Configuration éclairage connecté', 'Automatisation maison', 'Optimisation WiFi', 'Configuration sécurité connectée', 'Intégration assistant vocal', 'Réparation ordinateur', 'Réparation téléphone'],
      },
      sustainable: {
        label: 'Habitat Durable',
        description: 'Améliorations écologiques, isolation et efficacité énergétique',
        eta: '1-3 jours',
        activities: ['Installation panneaux solaires', 'Installation isolation', 'Audit énergétique', 'Double vitrage', 'Installation pompe à chaleur', 'Récupération eau de pluie', 'Installation toit végétalisé'],
      },
    },
  },
} as const;

export type TranslationKeys = typeof translations.en;
